import React, { useState, useEffect, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import {
  Upload, Bot, ChevronRight, FileSpreadsheet, FileText, Download,
  LogOut, Users, Key, ArrowLeft, Plus, Pencil, Trash2,
  ChevronDown, AlertCircle, CheckCircle, Loader, Activity,
  RefreshCw, ChevronUp, Clock, Calendar, Menu, X, Receipt,
} from "lucide-react";

// ── Constantes ────────────────────────────────────────────────────────────────
const _HOST        = window.location.hostname;
const _PROTO       = window.location.protocol;
const BACKEND_PORT = 3080; // porta do host mapeada para o backend no docker-compose.yml
const API_URL      = `${_PROTO}//${_HOST}:${BACKEND_PORT}`;
const POLL_MS      = 1500;

// Na VPS de produção o botão de mapeamento (homologação) do Custas CELOS é
// ocultado — só ficam "Rodar produção agora" e "Buscar comprovantes agora"
// (REACT_APP_OCULTAR_MAPEAMENTO_CELOS=true no docker-compose.yml da VPS).
const OCULTAR_MAPEAMENTO_CELOS = process.env.REACT_APP_OCULTAR_MAPEAMENTO_CELOS === "true";

// Habilita o robô "Análise de Processos" na lista do frontend. Ainda em
// homologação — trocar para true quando for liberado para uso real.
const ANALISE_PROCESSOS_HABILITADO = false;

const ROBOS_DISPONIVEIS = [
  {
    id:           "sisjurpex-custas",
    nome:         "SisJurPex",
    subtitulo:    "Pagamento de Custas",
    icon:         <Bot size={32} />,
    desc:         "Extrai o Nº do processo a partir do ID SisJur e preenche a solicitação de lançamento de custas.",
    colunas:      ["processo", "idsisjur"],
    temDestino:   false,
    temZipEproc:  true,   // exige o ZIP resultado do EPROC (resultado_final.zip)
    colCabecalho: ["Processo", "ID SisJur"],
    colKeys:      ["processo", "idsisjur"],
  },
  {
    id:              "sisjur-insercao",
    nome:            "SisJur",
    subtitulo:       "Inserção de Documentos",
    icon:            <FileText size={32} />,
    desc:            "Insere andamentos e documentos (ZIPs) em processos no SisJur PEX de forma automatizada.",
    colunas: [
      { key: "PROCESSO",                  aliases: ["PROCESSO"] },
      { key: "ACAO",                       aliases: ["ACAO", "AÇÃO"] },
      { key: "DATA",                       aliases: ["DATA"] },
      { key: "EVENTO",                     aliases: ["EVENTO"] },
      { key: "ARQUIVO",                    aliases: ["ARQUIVO"] },
      { key: "NUMERO",                     aliases: ["NUMERO", "NÚMERO"],                              opcional: true },
      { key: "COMENTARIO",                 aliases: ["COMENTARIO", "COMENTÁRIO"],                      opcional: true },
      { key: "CRIACAO",                    aliases: ["CRIACAO", "CRIAÇÃO"],                            opcional: true },
      { key: "JUIZ",                       aliases: ["JUIZ"],                                          opcional: true },
      { key: "DESEMBARGADOR_VOGAL",        aliases: ["DESEMBARGADOR_VOGAL"],                           opcional: true },
      { key: "DESEMBARGADOR_RELATOR",      aliases: ["DESEMBARGADOR_RELATOR"],                         opcional: true },
      { key: "MINISTRO",                   aliases: ["MINISTRO"],                                      opcional: true },
      { key: "DESEMBARGADOR_REVISOR",      aliases: ["DESEMBARGADOR_REVISOR"],                         opcional: true },
      { key: "DESEMBARGADOR_PRESIDENTE",   aliases: ["DESEMBARGADOR_PRESIDENTE"],                      opcional: true },
      { key: "PRAZO_DATA",                 aliases: ["PRAZO_DATA"],                                    opcional: true },
      { key: "PRAZO_HORA",                 aliases: ["PRAZO_HORA"],                                    opcional: true },
      { key: "PRAZO_TIPO",                 aliases: ["PRAZO_TIPO"],                                    opcional: true },
      { key: "PRAZO_COMENTARIO",           aliases: ["PRAZO_COMENTARIO", "PRAZO_COMENTÁRIO"],          opcional: true },
      { key: "DATA_DOCUMENTO",             aliases: ["DATA_DOCUMENTO"],                                opcional: true },
      { key: "TIPO_DOCUMENTO",             aliases: ["TIPO_DOCUMENTO"],                                opcional: true },
      { key: "COMENTARIO_DOCUMENTO",       aliases: ["COMENTARIO_DOCUMENTO", "COMENTÁRIO_DOCUMENTO"], opcional: true },
      // Preserva o UUID da tarefa Autojur (planilha da Busca Automática) — sem
      // essa coluna, o worker não sabe quais tarefas marcar como FINALIZADA
      // no Autojur depois de inserir no SisJur (ver marcar_tarefas_finalizadas
      // em sisjur_rpa.py). Não aparece pro usuário (opcional, sem alias
      // amigável), só precisa sobreviver ao parse pra ir junto no POST.
      { key: "AUTOJUR_TAREFA_UUID",        aliases: ["AUTOJUR_TAREFA_UUID"],                           opcional: true },
    ],
    temDestino:      false,
    temZipsMultiplos: true,
    temBuscaAutojurSisjur: true,  // exibe o painel "Busca Automática — Autojur → SisJur PEX"
    modeloUrl:       "/modelo-sisjur-insercao.xlsx",
    colCabecalho:    ["Processo", "Ação", "Evento", "Data", "Arquivo"],
    colKeys:         ["PROCESSO", "ACAO", "EVENTO", "DATA", "ARQUIVO"],
  },
  {
    id:              "sisjurpex-peticao",
    nome:            "Petição e Protocolo",
    subtitulo:       "Civil Consumidor e Juizado Especial",
    icon:            <Bot size={32} />,
    desc:            "Preenche andamentos e protocola petições em processos de Civil Consumidor e Juizado Especial.",
    colunas: [
      { key: "INTEGRACAO",     aliases: ["INTEGRACAO", "INTEGRAÇÃO", "INTEGRACAO"] },
      { key: "ID_TRAMITACAO",  aliases: ["ID_TRAMITACAO", "ID_TRAMITAÇÃO", "ID TRAMITACAO"] },
      { key: "PROCESSO",       aliases: ["PROCESSO"] },
      { key: "DATA",           aliases: ["DATA"] },
      { key: "DATA FIM",       aliases: ["DATA FIM", "DATA_FIM", "DATAFIM"] },
      { key: "DECISAO",        aliases: ["DECISAO", "DECISÃO"],                                 opcional: true },
      { key: "COMENTARIO",     aliases: ["COMENTARIO", "COMENTÁRIO"],                           opcional: true },
      { key: "EVENTO",         aliases: ["EVENTO"] },
      { key: "OBSERVACAO",     aliases: ["OBSERVACAO", "OBSERVAÇÃO"],                           opcional: true },
      { key: "TIPO_DOCUMENTO", aliases: ["TIPO_DOCUMENTO"] },
      { key: "ARQUIVO",        aliases: ["ARQUIVO"] },
    ],
    temDestino:    false,
    temZipPeticao: true,
    modeloUrl:     "/modelo-sisjurpex-peticao.xlsx",
    colCabecalho:  ["Processo", "ID Tramitação", "Evento", "Data", "Arquivo"],
    colKeys:       ["PROCESSO", "ID_TRAMITACAO", "EVENTO", "DATA", "ARQUIVO"],
  },
  {
    id:            "eproc-guias",
    nome:          "EPROC",
    subtitulo:     "Emissão de Guias",
    icon:          <FileSpreadsheet size={32} />,
    desc:          "Acessa o EPROC SC, localiza guias de custas processuais, gera boletos no Thema e retorna os PDFs.",
    colunas:       [
      { key: "processo", aliases: ["processo", "número cnj > pasta > pasta tarefa", "numero cnj > pasta > pasta tarefa"] },
      { key: "idsisjur", aliases: ["idsisjur", "id sisjur", "id_sisjur", "localizador > pasta > pasta tarefa"], opcional: true },
    ],
    temDestino:    false,
    singleSession: true,   // ambiente compartilhado — apenas um usuário por vez
    colCabecalho:  ["Processo", "ID SisJur"],
    colKeys:       ["processo", "idsisjur"],
  },
  {
    id:               "baixa-processos",
    nome:             "Baixa de Processos",
    subtitulo:        "Kit de Baixa via Autojur",
    icon:             <FileSpreadsheet size={32} />,
    desc:             "Parte das tarefas 'kit de baixa' abertas no Autojur: consulta a Base de Garantias só para saber se o processo ainda tem saldo (EM_ANDAMENTO) e, se não tiver, consulta o status no tribunal para decidir o destino. Execução automática configurável por calendário.",
    temDestino:       false,
    temBaixaProcessos: true,   // sem planilha/credencial — dispara pelo botão de fluxo completo (Autojur)
    singleSession:    true,    // ambiente compartilhado — apenas um usuário por vez
    modeloUrl:        "/modelo-baixa-processos.xlsx",
    colunas:          [{ key: "codigo", aliases: ["codigo", "código", "codigo da tarefa", "código da tarefa", "localizador", "idsisjur", "id sisjur"] }],
    colCabecalho:     [],
    colKeys:          [],
  },
  {
    id:               "custas-celos",
    nome:             "Custas CELOS",
    subtitulo:        "Emissão de Guias e Pagamento",
    icon:             <Receipt size={32} />,
    desc:             "Localiza as tarefas de custas/honorários da CELOS abertas no Autojur, mapeia a emissão da guia no eproc TJSC e no portal do TJPR até o botão final (bloqueado em homologação) e monta o preview do GED/comentário/finalização no Autojur e da solicitação de pagamento na CELOS. Execução automática semanal configurável (segunda-feira emite guia real, sexta-feira busca comprovante).",
    temDestino:       false,
    temCustasCelos:   true,   // sem planilha/credencial — dispara pelo botão de fluxo completo (Autojur)
    singleSession:    true,   // ambiente compartilhado — apenas um usuário por vez
    colCabecalho:     [],
    colKeys:          [],
  },
  {
    id:             "consulta-tribunais",
    nome:           "Consulta Tribunais",
    subtitulo:      "TJSC e TJPR",
    icon:           <Activity size={32} />,
    desc:           "Acessa o TJSC (Eproc 1º e 2º Grau) e TJPR (Projudi) para baixar prazos em aberto e coletar intimações. Execução automática diária configurável.",
    semPlanilha:    true,
    temTribunais:   true,
    automatico:     true,
    colCabecalho:   [],
    colKeys:        [],
  },
  {
    id:               "classificador-intimacoes",
    nome:             "Classificador",
    subtitulo:        "Intimações IA",
    icon:             <Bot size={32} />,
    desc:             "Busca intimações pendentes direto na API do Autojur e classifica automaticamente via IA (TF-IDF + DeepSeek). Execução automática diária configurável.",
    semPlanilha:      true,
    temClassificador: true,
    colCabecalho:     [],
    colKeys:          [],
  },
  ...(ANALISE_PROCESSOS_HABILITADO ? [{
    id:                  "analise-processos",
    nome:                "Análise de Processos",
    subtitulo:           "Upload GED",
    icon:                <FileText size={32} />,
    desc:                "Envia o checklist, o relatório estratégico e a ficha do processo (copias-integrais-tjsc) para a pasta correta de cada processo no GED do Autojur, casando pelo número CNJ.",
    semPlanilha:         true,
    temAnaliseProcessos: true,
    colCabecalho:        [],
    colKeys:             [],
  }] : []),
  {
    id:             "autojur-loy",
    nome:           "Autojur → Loy Legal",
    subtitulo:      "Captura de CNJ lançados",
    icon:           <Activity size={32} />,
    desc:           "Busca tarefas de VERIFICAR PROVIDÊNCIA e FAZER ANÁLISE RECURSAL criadas hoje no Autojur, pega o número CNJ do processo e envia para a Loy Legal protocolar. Execução automática 2x/dia.",
    semPlanilha:    true,
    temLoyCaptura:  true,
    colCabecalho:   [],
    colKeys:        [],
  },
  {
    id:            "pdf-to-text",
    nome:          "PDF to Text",
    subtitulo:     "Conversão e Análise de Processo",
    icon:          <FileText size={32} />,
    desc:          "Converte o PDF de um processo judicial para Markdown (com OCR de fallback nas páginas sem texto) e, opcionalmente, roda uma análise por checklist. Avisa custo estimado e pede confirmação antes de qualquer chamada de API paga.",
    semPlanilha:   true,
    temPdfToText:  true,
    colCabecalho:  [],
    colKeys:       [],
  },
];

// ── JWT decode (UTF-8 seguro) ─────────────────────────────────────────────────
function parseJwt(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const bytes  = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch { return null; }
}

// ── API helper ────────────────────────────────────────────────────────────────
async function api(endpoint, options = {}) {
  const token = localStorage.getItem("rpa_token");
  const res = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) {
    localStorage.removeItem("rpa_token");
    window.location.reload();
  }
  return res;
}

// ── parseXlsx helpers ─────────────────────────────────────────────────────────
// Converte serial Excel (ex: 46126) para "dd/mm/yyyy"
function _excelSerialParaData(serial) {
  // Epoch Excel: 30/12/1899. Corrige o bug de 1900 ser contado como bissexto.
  const d = new Date(Date.UTC(1899, 11, 30) + Math.round(serial) * 86400000);
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
}

// Retorna true se a chave de coluna indica que é uma data
function _eColunaDaData(key) {
  const k = key.toLowerCase();
  return k.includes("data") || k.includes("date") || k.includes("vencimento");
}

// Formata um valor de célula para string, tratando datas corretamente
function _formatarCelula(val, key) {
  if (val === undefined || val === null) return "";
  // XLSX com cellDates:true retorna Date objects
  if (val instanceof Date) {
    const dd = String(val.getDate()).padStart(2, "0");
    const mm = String(val.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${val.getFullYear()}`;
  }
  // Número em coluna de data → serial Excel
  if (typeof val === "number" && _eColunaDaData(key) && val > 1000) {
    return _excelSerialParaData(val);
  }
  return String(val).trim();
}

// ── parseXlsx ─────────────────────────────────────────────────────────────────
// colunas pode ser:
//   ["key1", "key2"]                                        — chave = nome da coluna
//   [{ key: "key1", aliases: ["nome1", "nome2"] }, ...]     — tenta cada alias, saída usa key
function parseXlsx(file, colunas, onSuccess, onError) {
  // Normaliza para formato { key, aliases, opcional }
  const specs = colunas.map((c) =>
    typeof c === "string"
      ? { key: c, aliases: [c], opcional: c === "idsisjur" }
      : { key: c.key, aliases: c.aliases, opcional: !!c.opcional }
  );

  const reader = new FileReader();
  reader.onload = (evt) => {
    try {
      const wb   = XLSX.read(new Uint8Array(evt.target.result), { type: "array" });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws);
      if (!json.length) { onError("Planilha vazia!"); return; }

      const obrigatorias = specs.filter(s => !s.opcional);
      const formatado = json
        .map((row) => {
          const chaves = Object.keys(row);
          const obj = {};
          for (const spec of specs) {
            const k = chaves.find((c) =>
              spec.aliases.some((alias) => c.toLowerCase().trim() === alias.toLowerCase().trim())
            );
            obj[spec.key] = k ? _formatarCelula(row[k], spec.key) : "";
          }
          return obj;
        })
        .filter((item) => obrigatorias.some((s) => item[s.key] !== ""));

      if (!formatado.length) {
        onError(`Colunas esperadas: ${obrigatorias.map(s => s.aliases[0]).join(", ")}. Verifique o cabeçalho.`);
        return;
      }
      onSuccess(formatado);
    } catch {
      onError("Erro ao ler arquivo Excel.");
    }
  };
  reader.readAsArrayBuffer(file);
}

// ── Componentes base ──────────────────────────────────────────────────────────
function Btn({ children, onClick, disabled, variant = "primary", size = "md", className = "" }) {
  const base = "inline-flex items-center gap-2 font-semibold rounded-lg transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed";
  const sizes = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2.5 text-sm", lg: "px-6 py-3 text-sm" };
  const vars = {
    primary:   "bg-[#1B2B5B] hover:bg-[#152247] text-white shadow-sm",
    danger:    "bg-red-600 hover:bg-red-700 text-white shadow-sm",
    secondary: "bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm",
    ghost:     "text-gray-500 hover:text-gray-900 hover:bg-gray-100",
  };
  return (
    <button onClick={onClick} disabled={disabled}
      className={`${base} ${sizes[size]} ${vars[variant]} ${className}`}>
      {children}
    </button>
  );
}

function Input({ label, ...props }) {
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</label>}
      <input
        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 bg-white outline-none focus:ring-2 focus:ring-[#1B2B5B]/20 focus:border-[#1B2B5B] transition-all placeholder:text-gray-400"
        {...props}
      />
    </div>
  );
}

function Select({ label, children, ...props }) {
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</label>}
      <select
        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 bg-white outline-none focus:ring-2 focus:ring-[#1B2B5B]/20 focus:border-[#1B2B5B] transition-all"
        {...props}>
        {children}
      </select>
    </div>
  );
}

function Card({ children, className = "", ...rest }) {
  return <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm ${className}`} {...rest}>{children}</div>;
}

function StatusPill({ label, active, pulsing }) {
  return (
    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold border transition-all
      ${active ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-gray-50 border-gray-200 text-gray-400"}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-gray-300"} ${pulsing ? "animate-ping" : ""}`} />
      {label}
    </div>
  );
}

function LogLine({ linha }) {
  const isErr  = /error|erro|crítico/i.test(linha);
  const isWarn = /warn|atenção|timeout/i.test(linha);
  const isSys  = linha.startsWith("[SISTEMA]");
  const color  = isErr ? "text-red-500" : isWarn ? "text-amber-600" : isSys ? "text-[#1B2B5B]" : "text-gray-700";
  return <div className={`font-mono text-[11px] leading-5 ${color} break-all`}>{linha}</div>;
}

// ── NavHeader ─────────────────────────────────────────────────────────────────
function NavHeader({ user, onLogout, onUsers, onCredentials, onExecucoes, currentScreen, onRobots }) {
  const isAdmin   = user?.role === "admin";
  const canManage = isAdmin || user?.manage_credentials;
  const [open, setOpen] = useState(false);

  const navItems = [
    { screen: "robots",      onClick: onRobots,      icon: null,                   label: "Robôs" },
    { screen: "execucoes",   onClick: onExecucoes,   icon: <Activity size={14} />, label: "Execuções" },
    ...(isAdmin   ? [{ screen: "users",       onClick: onUsers,       icon: <Users size={14} />, label: "Usuários" }]      : []),
    ...(canManage ? [{ screen: "credentials", onClick: onCredentials, icon: <Key   size={14} />, label: "Credenciais" }]   : []),
  ];

  const navBtn = ({ screen, onClick, icon, label }) => (
    <button key={screen} onClick={() => { onClick(); setOpen(false); }}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all w-full text-left
        ${currentScreen === screen ? "bg-[#EEF1F8] text-[#1B2B5B]" : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"}`}>
      {icon}{label}
    </button>
  );

  return (
    <header className="bg-white border-b border-gray-100 shadow-sm px-4 sm:px-6 flex items-center justify-between h-14 flex-shrink-0 relative z-50">
      <div className="flex items-center gap-3">
        <img src="/logo.jpg" alt="Marcondes Brincas" className="h-8 object-contain" />
      </div>

      {/* Desktop nav */}
      <nav className="hidden sm:flex items-center gap-1">
        {navItems.map(navBtn)}
        <div className="w-px h-5 bg-gray-200 mx-2" />
        <span className="text-sm text-gray-500">{user?.name}</span>
        <button onClick={onLogout}
          className="ml-2 flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 px-3 py-2 rounded-lg hover:bg-red-50 transition-all">
          <LogOut size={14} /> Sair
        </button>
      </nav>

      {/* Mobile hamburger */}
      <button onClick={() => setOpen(o => !o)}
        className="sm:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-all">
        {open ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Mobile dropdown */}
      {open && (
        <div className="sm:hidden absolute top-14 left-0 right-0 bg-white border-b border-gray-100 shadow-lg px-4 py-3 flex flex-col gap-1">
          {navItems.map(navBtn)}
          <div className="border-t border-gray-100 mt-1 pt-2 flex items-center justify-between">
            <span className="text-sm text-gray-500">{user?.name}</span>
            <button onClick={() => { onLogout(); setOpen(false); }}
              className="flex items-center gap-1.5 text-sm text-red-500 font-semibold px-3 py-2 rounded-lg hover:bg-red-50 transition-all">
              <LogOut size={14} /> Sair
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 1. TELA DE LOGIN
// ═══════════════════════════════════════════════════════════════════════════════
function LoginScreen({ onLogin }) {
  const [creds, setCreds]   = useState({ email: "", password: "" });
  const [erro, setErro]     = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setLoading(true);
    try {
      const res  = await fetch(`${API_URL}/auth/login`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email: creds.email, password: creds.password }),
      });
      const data = await res.json();
      if (!res.ok) { setErro(data.erro || "Credenciais inválidas"); return; }
      localStorage.setItem("rpa_token", data.token);
      onLogin(data.token, data.user);
    } catch {
      setErro("Erro de conexão com o servidor.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Lado esquerdo — foto */}
      <div className="hidden lg:flex lg:w-3/5 relative overflow-hidden">
        <img src="/login-bg.jpg" alt="Marcondes Brincas" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1B2B5B]/60" />
        <div className="absolute bottom-12 left-12 text-white">
          <p className="text-lg font-light opacity-80">Automação Inteligente</p>
          <p className="text-4xl font-bold mt-1">Marcondes Brincas Advogados</p>
        </div>
      </div>

      {/* Lado direito — formulário */}
      <div className="flex-1 flex flex-col items-center justify-center bg-[#F5F6FA] p-8">
        <div className="w-full max-w-sm">
          <div className="flex justify-center mb-8">
            <img src="/logo.jpg" alt="Marcondes Brincas" className="h-14 object-contain" />
          </div>
          <p className="text-center text-sm font-medium text-[#B8963E] mb-1 tracking-widest uppercase">
            Automação de Processos com IA
          </p>
          <p className="text-center text-xs text-gray-400 mb-8 font-light">
            Automatize Tarefas Repetitivas
          </p>

          <Card className="p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">Acessar</h2>
            {erro && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">
                <AlertCircle size={15} /> {erro}
              </div>
            )}
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <Input label="E-mail" type="email" placeholder="seu@email.com" required
                value={creds.email}
                onChange={(e) => setCreds({ ...creds, email: e.target.value })} />
              <Input label="Senha" type="password" placeholder="••••••••" required
                value={creds.password}
                onChange={(e) => setCreds({ ...creds, password: e.target.value })} />
              <Btn variant="primary" size="lg" disabled={loading} className="w-full justify-center mt-2">
                {loading ? <><Loader size={15} className="animate-spin" /> Entrando...</> : "Entrar"}
              </Btn>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 2. SELETOR DE ROBÔS
// ═══════════════════════════════════════════════════════════════════════════════
function RobotSelector({ user, onSelect, onLogout, onUsers, onCredentials, onExecucoes }) {
  const robosPermitidos = ROBOS_DISPONIVEIS.filter(
    r => user?.role === "admin" || user?.robots?.includes(r.id)
  );

  return (
    <div className="min-h-screen bg-[#F5F6FA] flex flex-col">
      <NavHeader user={user} onLogout={onLogout} onUsers={onUsers} onCredentials={onCredentials}
        onExecucoes={onExecucoes} currentScreen="robots" onRobots={() => {}} />

      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-12">
        <div className="mb-10">
          <p className="text-xs font-semibold text-[#B8963E] uppercase tracking-widest mb-1">Central de Automação</p>
          <h1 className="text-3xl font-bold text-gray-900">Selecione o Robô</h1>
          <p className="text-gray-500 mt-1 text-sm">Escolha a ferramenta que deseja operar.</p>
        </div>

        {robosPermitidos.length === 0 ? (
          <Card className="p-10 text-center text-gray-400">
            <p className="font-medium">Nenhum robô disponível para sua conta.</p>
            <p className="text-sm mt-1">Contate o administrador.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {robosPermitidos.map((r) => (
              <Card key={r.id}
                onClick={() => onSelect(r)}
                className="p-8 hover:border-[#1B2B5B] hover:shadow-lg cursor-pointer transition-all group hover:-translate-y-1 relative">
                {(r.automatico || r.temClassificador || r.temLoyCaptura || r.temBuscaAutojurSisjur || r.temBaixaProcessos || r.temCustasCelos) && (
                  <span className="absolute top-4 right-4 flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 uppercase tracking-wide">
                    <Clock size={9} /> Automático
                  </span>
                )}
                {r.emDesenvolvimento && (
                  <span className="absolute top-4 right-4 flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 uppercase tracking-wide">
                    <AlertCircle size={9} /> Em desenvolvimento
                  </span>
                )}
                <div className="text-[#1B2B5B] mb-4 group-hover:scale-110 transition-transform">
                  {r.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900">{r.nome}</h3>
                <p className="text-[#B8963E] text-xs font-semibold uppercase tracking-widest mt-0.5 mb-3">{r.subtitulo}</p>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">{r.desc}</p>
                <div className="flex items-center gap-1.5 text-[#1B2B5B] text-xs font-bold">
                  ABRIR <ChevronRight size={13} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

// ── Painel Prazos dos Tribunais ───────────────────────────────────────────────
function PrazosTribunaisPanel({
  credsTjsc, credsTjpr, credSelTjsc, credSelTjpr,
  onChangeTjsc, onChangeTjpr,
  status, agendado, onAgendar, onExecutar,
}) {
  const rodando    = status?.rodando;
  const arquivos   = status?.arquivos || [];
  const logs       = status?.logs || [];
  const ultimoRun  = status?.ultimo_run;
  const fmt        = (iso) => iso ? new Date(iso).toLocaleString("pt-BR") : "—";
  const statusIcon = rodando ? "🔄" : status?.status === "ok" ? "✅" : status?.status === "erro" ? "❌" : "⏸";

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity size={14} className="text-[#1B2B5B]" />
          <p className="text-[11px] font-bold text-gray-600 uppercase tracking-widest">Prazos dos Tribunais</p>
        </div>
        <span className="text-[10px] text-gray-400">última coleta: {fmt(ultimoRun)}</span>
      </div>

      <div className="p-4 flex flex-col gap-3">
        {/* Credenciais */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Credencial TJSC</p>
            <select value={credSelTjsc} onChange={e => onChangeTjsc(e.target.value)} disabled={rodando}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white outline-none focus:ring-1 focus:ring-[#1B2B5B]/20 focus:border-[#1B2B5B]">
              <option value="">— selecionar —</option>
              {credsTjsc.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Credencial TJPR</p>
            <select value={credSelTjpr} onChange={e => onChangeTjpr(e.target.value)} disabled={rodando}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white outline-none focus:ring-1 focus:ring-[#1B2B5B]/20 focus:border-[#1B2B5B]">
              <option value="">— selecionar —</option>
              {credsTjpr.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
        </div>

        {/* Ações */}
        <div className="flex items-center gap-3">
          <button
            onClick={onExecutar}
            disabled={rodando || !credSelTjsc || !credSelTjpr}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#1B2B5B] text-white hover:bg-[#1B2B5B]/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {rodando ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {rodando ? "Coletando..." : "Executar Agora"}
          </button>

          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={agendado}
              onChange={e => onAgendar(e.target.checked)}
              className="w-3 h-3 accent-[#1B2B5B]" />
            <span className="text-xs text-gray-600">Agendar 2h (Brasília)</span>
          </label>

          {status?.status && (
            <span className="text-xs text-gray-500">{statusIcon} {status.status}</span>
          )}
        </div>

        {/* Arquivos gerados */}
        {arquivos.length > 0 && (
          <div className="space-y-1">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Arquivos de Prazos</p>
            {arquivos.map(f => (
              <a key={f.nome} href={`${window.location.protocol}//${window.location.hostname}:${BACKEND_PORT}/download/${f.nome}?token=${localStorage.getItem("rpa_token")}`}
                download className="flex items-center gap-2 bg-gray-50 hover:bg-[#EEF1F8] border border-gray-100 rounded-lg p-2 transition-all">
                <Download size={11} className="text-[#1B2B5B] flex-shrink-0" />
                <span className="text-[10px] text-gray-700 truncate flex-1">{f.nome}</span>
                <span className="text-[10px] text-gray-400 flex-shrink-0">{new Date(f.data).toLocaleString("pt-BR")}</span>
              </a>
            ))}
          </div>
        )}

        {/* Log compacto */}
        {logs.length > 0 && (
          <div className="bg-gray-950/5 rounded-lg p-2 max-h-28 overflow-y-auto">
            {logs.slice(-10).map((l, i) => (
              <p key={i} className="text-[10px] font-mono text-gray-600 leading-relaxed">{l}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Resumo Baixa de Processos (le o xlsx de resultado e mostra CNJ/SISJUR/Autojur) ──
// Destino de cada processo no fluxo novo (dry_run_fluxo_completo) — mesmas
// categorias e cores do relatório de referência
// (relatorio_fluxo_completo_por_processo_novo.html).
const DESTINO_CFG = {
  EM_ANDAMENTO:        { label: "EM_ANDAMENTO",           cls: "text-emerald-700 bg-emerald-50", desc: "tem saldo na Base de Garantia" },
  AGUARDANDO:          { label: "AGUARDANDO",             cls: "text-amber-700 bg-amber-50",     desc: "ativo (ou não localizado) no tribunal" },
  FINALIZADA:          { label: "FINALIZADA",             cls: "text-blue-700 bg-blue-50",       desc: "encerramento no SISJUR" },
  AGRAVO_INSTRUMENTO:  { label: "AGRAVO DE INSTRUMENTO",  cls: "text-red-700 bg-red-50",         desc: "recurso — cancela a tarefa no Autojur" },
  FIM_DO_FLUXO:        { label: "FIM DO FLUXO",           cls: "text-gray-600 bg-gray-100",      desc: "condições não satisfeitas" },
  SEM_CNJ:             { label: "SEM CNJ",                cls: "text-red-700 bg-red-50",         desc: "não consultável" },
};
const DESTINO_ORDEM = ["EM_ANDAMENTO", "AGUARDANDO", "FINALIZADA", "AGRAVO_INSTRUMENTO", "FIM_DO_FLUXO", "SEM_CNJ"];

function BaixaProcessosResumo({ exec }) {
  const [linhas,     setLinhas]     = useState(null);
  const [erro,       setErro]       = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      const arquivos = exec.result_files || [];
      // Prioriza o arquivo do fluxo novo (dry_run_fluxo_completo_*); só cai
      // pro nome antigo (cnj_baixa_processos_*) se não houver o novo.
      const arquivo = arquivos.find(f => (f.nome || f).startsWith("dry_run_fluxo_completo_"))
                   || arquivos.find(f => (f.nome || f).startsWith("cnj_baixa_processos_"));
      if (!arquivo) { setCarregando(false); return; }
      try {
        const r = await api(`/execucoes/${exec.id}/arquivo/${encodeURIComponent(arquivo.nome || arquivo)}`);
        if (!r.ok) throw new Error("falha ao baixar");
        const buf = await r.arrayBuffer();
        const wb  = XLSX.read(buf, { type: "array" });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const linhasLidas = XLSX.utils.sheet_to_json(ws, { defval: null });
        if (!cancelado) setLinhas(linhasLidas);
      } catch {
        if (!cancelado) setErro("Não foi possível carregar o resumo dos processos.");
      } finally {
        if (!cancelado) setCarregando(false);
      }
    };
    carregar();
    return () => { cancelado = true; };
  }, [exec.id, exec.result_files]);

  if (carregando) {
    return (
      <div className="px-6 py-4 border-b border-gray-100 text-xs text-gray-400 flex items-center gap-2">
        <Loader size={12} className="animate-spin" /> Carregando resumo dos processos...
      </div>
    );
  }
  if (erro || !linhas) return null;

  const fluxoNovo = linhas.some(l => "destino" in l);
  const sisjurCriado      = linhas.filter(l => String(l.servico_pex_status || "").startsWith("OK"));
  const autojurAtualizado = linhas.filter(l => !!l.autojur_nova_situacao);

  const exportarXlsx = () => {
    const linhasExport = linhas.map(l => ({
      "CNJ":              l.cnj || "Não localizado",
      "Localizador":       l.localizador || l.numero_processo_curto || "",
      "Status Tribunal":  l.status_tribunal || "",
      "Destino":          l.destino || "",
      "SISJUR":           String(l.servico_pex_status || "").startsWith("OK") ? "Criado" : "—",
      "Autojur":          l.autojur_nova_situacao || "—",
    }));
    const ws = XLSX.utils.json_to_sheet(linhasExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Resumo");
    XLSX.writeFile(wb, `resumo_baixa_processos_execucao_${exec.id}.xlsx`);
  };

  // ── Fluxo novo (dry_run_fluxo_completo): agrupa por destino, igual ao
  // relatorio_fluxo_completo_por_processo_novo.html ────────────────────────
  if (fluxoNovo) {
    const porDestino = {};
    for (const l of linhas) porDestino[l.destino || "?"] = (porDestino[l.destino || "?"] || 0) + 1;
    const total = linhas.length;
    const pct = (n) => total > 0 ? ((n / total) * 100).toFixed(1) : "0.0";

    return (
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
            Resumo dos {total} processos com tarefa "kit de baixa" aberta no Autojur
          </p>
          <button onClick={exportarXlsx}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#1B2B5B] hover:underline">
            <Download size={12} /> Exportar XLSX
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-3">
          {DESTINO_ORDEM.filter(d => porDestino[d] > 0).map(d => {
            const n   = porDestino[d];
            const cfg = DESTINO_CFG[d];
            return (
              <div key={d} className={`rounded-lg p-3 text-center ${cfg.cls}`}>
                <p className="text-lg font-bold">{n}</p>
                <p className="text-[10px] uppercase tracking-wide font-semibold">{cfg.label}</p>
                <p className="text-[10px] opacity-70">{pct(n)}% · {cfg.desc}</p>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-emerald-50 rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-emerald-700">{sisjurCriado.length}</p>
            <p className="text-[10px] text-emerald-500 uppercase tracking-wide">Serviço SISJUR criado</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-blue-700">{autojurAtualizado.length}</p>
            <p className="text-[10px] text-blue-500 uppercase tracking-wide">Autojur atualizado</p>
          </div>
        </div>

        <div className="border border-gray-100 rounded-xl overflow-auto max-h-72">
          <table className="w-full text-left text-[11px]">
            <thead className="bg-[#1B2B5B] text-white sticky top-0">
              <tr>
                <th className="p-2">CNJ</th>
                <th className="p-2">Cliente</th>
                <th className="p-2">Localizador</th>
                <th className="p-2">Base Garantia</th>
                <th className="p-2">Status Tribunal</th>
                <th className="p-2">Apensos</th>
                <th className="p-2">Destino</th>
                <th className="p-2">SISJUR</th>
                <th className="p-2">Autojur</th>
                <th className="p-2">Motivo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {linhas.map((l, i) => {
                const cfg = DESTINO_CFG[l.destino] || { label: l.destino || "—", cls: "text-gray-600 bg-gray-100" };
                return (
                  <tr key={i} className="hover:bg-[#EEF1F8]">
                    <td className="p-2 font-mono">
                      {l.cnj || <span className="text-amber-600 font-semibold">Não localizado</span>}
                    </td>
                    <td className="p-2">{l.cliente_principal || "—"}</td>
                    <td className="p-2 font-mono">{l.localizador || "—"}</td>
                    <td className="p-2">{l.na_base_garantia ? "sim" : "não"}</td>
                    <td className="p-2">{l.status_tribunal || "—"}</td>
                    <td className="p-2">{l.apensos_ok === true ? "ok" : l.apensos_ok === false ? "não ok" : "—"}</td>
                    <td className="p-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${cfg.cls}`}>{cfg.label}</span>
                    </td>
                    <td className="p-2">
                      {String(l.servico_pex_status || "").startsWith("OK")
                        ? <span className="text-emerald-600 font-semibold">Criado</span>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="p-2">
                      {l.autojur_nova_situacao
                        ? <span className="text-blue-600 font-semibold">{l.autojur_nova_situacao}</span>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="p-2 text-gray-500">{l.motivo || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── Fluxo antigo (main()/cnj_baixa_processos_*) — mantém o resumo anterior ──
  const semCnj            = linhas.filter(l => !l.cnj);
  const naoLocalizadoTrib = linhas.filter(l => l.status_tribunal === "Não localizado");
  const processosBaixados = linhas.filter(l => l.autojur_nova_situacao === "FINALIZADA");

  return (
    <div className="px-6 py-4 border-b border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
          Resumo dos {linhas.length} Processos (Base de Garantias)
        </p>
        <button onClick={exportarXlsx}
          className="flex items-center gap-1.5 text-xs font-semibold text-[#1B2B5B] hover:underline">
          <Download size={12} /> Exportar XLSX
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
        <div className="bg-gray-100 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-gray-900">{linhas.length}</p>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Total</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-gray-900">{linhas.length - semCnj.length}</p>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">CNJ localizado</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-amber-700">{semCnj.length}</p>
          <p className="text-[10px] text-amber-500 uppercase tracking-wide">Sem CNJ</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-amber-700">{naoLocalizadoTrib.length}</p>
          <p className="text-[10px] text-amber-500 uppercase tracking-wide">Não localizado no tribunal</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-emerald-700">{sisjurCriado.length}</p>
          <p className="text-[10px] text-emerald-500 uppercase tracking-wide">Serviço SISJUR criado</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-blue-700">{autojurAtualizado.length}</p>
          <p className="text-[10px] text-blue-500 uppercase tracking-wide">Autojur atualizado</p>
        </div>
        <div className="bg-indigo-50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-indigo-700">{processosBaixados.length}</p>
          <p className="text-[10px] text-indigo-500 uppercase tracking-wide">Processos baixados</p>
        </div>
      </div>
      <div className="border border-gray-100 rounded-xl overflow-auto max-h-72">
        <table className="w-full text-left text-[11px]">
          <thead className="bg-[#1B2B5B] text-white sticky top-0">
            <tr>
              <th className="p-2">Nº Processo</th>
              <th className="p-2">CNJ</th>
              <th className="p-2">Status Tribunal</th>
              <th className="p-2">SISJUR</th>
              <th className="p-2">Autojur</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {linhas.map((l, i) => (
              <tr key={i} className="hover:bg-[#EEF1F8]">
                <td className="p-2 font-mono">{l.numero_processo_curto || "—"}</td>
                <td className="p-2 font-mono">
                  {l.cnj || <span className="text-amber-600 font-semibold">Não localizado</span>}
                </td>
                <td className="p-2">
                  {l.status_tribunal === "Não localizado"
                    ? <span className="text-amber-600 font-semibold">Não localizado</span>
                    : (l.status_tribunal || "—")}
                </td>
                <td className="p-2">
                  {String(l.servico_pex_status || "").startsWith("OK")
                    ? <span className="text-emerald-600 font-semibold">Criado</span>
                    : <span className="text-gray-400">—</span>}
                </td>
                <td className="p-2">
                  {l.autojur_nova_situacao
                    ? <span className="text-blue-600 font-semibold">{l.autojur_nova_situacao}</span>
                    : <span className="text-gray-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Resumo SisJur — Inserção de Documentos (criado no SisJur / finalizado no
// Autojur, por tarefa) — lê o resultados_sisjur_*.xlsx que o worker já grava
// (ver sisjur_rpa.py:salvar_resultados / AUTOJUR_STATUS), mesmo padrão do
// BaixaProcessosResumo acima. ──────────────────────────────────────────────
function SisjurInsercaoResumo({ exec }) {
  const [linhas,     setLinhas]     = useState(null);
  const [erro,       setErro]       = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      const arquivo = (exec.result_files || []).find(f => (f.nome || f).startsWith("resultados_sisjur_"));
      if (!arquivo) { setCarregando(false); return; }
      try {
        const r = await api(`/execucoes/${exec.id}/arquivo/${encodeURIComponent(arquivo.nome || arquivo)}`);
        if (!r.ok) throw new Error("falha ao baixar");
        const buf = await r.arrayBuffer();
        const wb  = XLSX.read(buf, { type: "array" });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const linhasLidas = XLSX.utils.sheet_to_json(ws, { defval: null });
        if (!cancelado) setLinhas(linhasLidas);
      } catch {
        if (!cancelado) setErro("Não foi possível carregar o resumo das tarefas.");
      } finally {
        if (!cancelado) setCarregando(false);
      }
    };
    carregar();
    return () => { cancelado = true; };
  }, [exec.id, exec.result_files]);

  if (carregando) {
    return (
      <div className="px-6 py-4 border-b border-gray-100 text-xs text-gray-400 flex items-center gap-2">
        <Loader size={12} className="animate-spin" /> Carregando resumo das tarefas...
      </div>
    );
  }
  if (erro || !linhas || linhas.length === 0) return null;

  const criadas     = linhas.filter(l => l._status === "OK");
  const comErro     = linhas.filter(l => l._status === "ERRO");
  const finalizadas = linhas.filter(l => l.AUTOJUR_STATUS === "FINALIZADA");

  const exportarXlsx = () => {
    const linhasExport = linhas.map(l => ({
      "Processo":  l.PROCESSO || "",
      "Evento":    l.EVENTO || "",
      "Arquivo":   l.ARQUIVO || "",
      "SisJur":    l._status === "OK" ? "Criado" : l._status === "ERRO" ? "Erro" : (l._status || "—"),
      "Autojur":   l.AUTOJUR_STATUS || "—",
      "Detalhe":   l._erro || "",
    }));
    const ws = XLSX.utils.json_to_sheet(linhasExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Resumo");
    XLSX.writeFile(wb, `resumo_sisjur_insercao_execucao_${exec.id}.xlsx`);
  };

  return (
    <div className="px-6 py-4 border-b border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
          Resumo das {linhas.length} tarefas — SisJur PEX / Autojur
        </p>
        <button onClick={exportarXlsx}
          className="flex items-center gap-1.5 text-xs font-semibold text-[#1B2B5B] hover:underline">
          <Download size={12} /> Exportar XLSX
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-emerald-50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-emerald-700">{criadas.length}</p>
          <p className="text-[10px] text-emerald-500 uppercase tracking-wide">Criadas no SisJur</p>
        </div>
        <div className="bg-red-50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-red-700">{comErro.length}</p>
          <p className="text-[10px] text-red-500 uppercase tracking-wide">Erro no SisJur</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-blue-700">{finalizadas.length}</p>
          <p className="text-[10px] text-blue-500 uppercase tracking-wide">Finalizadas no Autojur</p>
        </div>
      </div>

      <div className="border border-gray-100 rounded-xl overflow-auto max-h-72">
        <table className="w-full text-left text-[11px]">
          <thead className="bg-[#1B2B5B] text-white sticky top-0">
            <tr>
              <th className="p-2">Processo</th>
              <th className="p-2">Evento</th>
              <th className="p-2">Arquivo</th>
              <th className="p-2">SisJur</th>
              <th className="p-2">Autojur</th>
              <th className="p-2">Detalhe</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {linhas.map((l, i) => (
              <tr key={i} className="hover:bg-[#EEF1F8]">
                <td className="p-2 font-mono">{l.PROCESSO || "—"}</td>
                <td className="p-2">{l.EVENTO || "—"}</td>
                <td className="p-2 font-mono">{l.ARQUIVO || "—"}</td>
                <td className="p-2">
                  {l._status === "OK"
                    ? <span className="text-emerald-600 font-semibold">Criado</span>
                    : l._status === "ERRO"
                      ? <span className="text-red-600 font-semibold">Erro</span>
                      : <span className="text-gray-400">{l._status || "—"}</span>}
                </td>
                <td className="p-2">
                  {l.AUTOJUR_STATUS === "FINALIZADA"
                    ? <span className="text-blue-600 font-semibold">Finalizada</span>
                    : l.AUTOJUR_STATUS
                      ? <span className="text-amber-600 font-semibold">{l.AUTOJUR_STATUS}</span>
                      : <span className="text-gray-400">—</span>}
                </td>
                <td className="p-2 text-gray-500">{l._erro || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Resumo Autojur -> Loy Legal (quais CNJ foram enviados nesta execução) ────
const LOY_STATUS_CFG = {
  enviado:            { label: "Enviado",            cls: "text-emerald-700 bg-emerald-50" },
  falha:              { label: "Falha",               cls: "text-red-700 bg-red-50" },
  "já_enviado_antes": { label: "Já enviado antes",    cls: "text-gray-500 bg-gray-100" },
};

function LoyResumo({ exec }) {
  const [linhas,     setLinhas]     = useState(null);
  const [erro,       setErro]       = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      const arquivos = exec.result_files || [];
      const arquivo = arquivos.find(f => (f.nome || f).startsWith("loy_envio_"));
      if (!arquivo) { setCarregando(false); return; }
      try {
        const r = await api(`/execucoes/${exec.id}/arquivo/${encodeURIComponent(arquivo.nome || arquivo)}`);
        if (!r.ok) throw new Error("falha ao baixar");
        const buf = await r.arrayBuffer();
        const wb  = XLSX.read(buf, { type: "array" });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const linhasLidas = XLSX.utils.sheet_to_json(ws, { defval: null });
        if (!cancelado) setLinhas(linhasLidas);
      } catch {
        if (!cancelado) setErro("Não foi possível carregar a lista de processos enviados.");
      } finally {
        if (!cancelado) setCarregando(false);
      }
    };
    carregar();
    return () => { cancelado = true; };
  }, [exec.id, exec.result_files]);

  if (carregando) {
    return (
      <div className="px-6 py-4 border-b border-gray-100 text-xs text-gray-400 flex items-center gap-2">
        <Loader size={12} className="animate-spin" /> Carregando lista de processos enviados...
      </div>
    );
  }
  if (erro || !linhas) return null;

  const enviados   = linhas.filter(l => l.status === "enviado");
  const falharam   = linhas.filter(l => l.status === "falha");
  const jaEnviados = linhas.filter(l => l.status === "já_enviado_antes");

  const linhasExport = () => linhas.map(l => ({
    "CNJ":     l.cnj || "",
    "Evento":  l.evento_nome || "",
    "Status":  LOY_STATUS_CFG[l.status]?.label || l.status || "",
    "Detalhe": l.detalhe || "",
  }));

  const exportarXlsx = () => {
    const ws = XLSX.utils.json_to_sheet(linhasExport());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Envio Loy Legal");
    XLSX.writeFile(wb, `loy_envio_execucao_${exec.id}.xlsx`);
  };

  const exportarCsv = () => {
    const ws  = XLSX.utils.json_to_sheet(linhasExport());
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `loy_envio_execucao_${exec.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="px-6 py-4 border-b border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
          Processos enviados pra Loy Legal ({linhas.length})
        </p>
        <div className="flex items-center gap-3">
          <button onClick={exportarXlsx}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#1B2B5B] hover:underline">
            <Download size={12} /> Exportar XLSX
          </button>
          <button onClick={exportarCsv}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#1B2B5B] hover:underline">
            <Download size={12} /> Exportar CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-emerald-50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-emerald-700">{enviados.length}</p>
          <p className="text-[10px] text-emerald-500 uppercase tracking-wide">Enviados</p>
        </div>
        <div className="bg-red-50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-red-700">{falharam.length}</p>
          <p className="text-[10px] text-red-500 uppercase tracking-wide">Falha</p>
        </div>
        <div className="bg-gray-100 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-gray-600">{jaEnviados.length}</p>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Já enviados antes</p>
        </div>
      </div>

      {linhas.length === 0 ? (
        <p className="text-xs text-gray-400">Nenhum processo candidato nesta execução.</p>
      ) : (
        <div className="border border-gray-100 rounded-xl overflow-auto max-h-72">
          <table className="w-full text-left text-[11px]">
            <thead className="bg-[#1B2B5B] text-white sticky top-0">
              <tr>
                <th className="p-2">CNJ</th>
                <th className="p-2">Evento</th>
                <th className="p-2">Status</th>
                <th className="p-2">Detalhe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {linhas.map((l, i) => {
                const cfg = LOY_STATUS_CFG[l.status] || { label: l.status || "—", cls: "text-gray-500 bg-gray-100" };
                return (
                  <tr key={i} className="hover:bg-[#EEF1F8]">
                    <td className="p-2 font-mono">{l.cnj || "—"}</td>
                    <td className="p-2">{l.evento_nome || "—"}</td>
                    <td className="p-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${cfg.cls}`}>{cfg.label}</span>
                    </td>
                    <td className="p-2 text-gray-500">{l.detalhe || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Dashboard Classificador ───────────────────────────────────────────────────
function ClassificadorDashboard({ data, envioResumo, rodando }) {
  if (!data) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        <Loader size={18} className="animate-spin mr-2" /> Carregando métricas...
      </div>
    );
  }

  const pct    = (n, d) => (d > 0 ? `${((n / d) * 100).toFixed(1)}%` : "—");
  const pctNum = (n, d) => (d > 0 ? ((n / d) * 100).toFixed(1) : "0.0");

  const {
    total, por_classe = {}, revisao_humana, local_gate, gemini,
    erros, ultima_atualizacao, confianca_media, detalhes = [],
  } = data;

  // Ordena por confiança crescente (piores primeiro) para destacar incertezas
  const detalhesSorted = [...detalhes].sort((a, b) => (a.confianca ?? 1) - (b.confianca ?? 1));

  const autoClassificadas = total - revisao_humana;
  const taxaAcerto        = pct(autoClassificadas, total);
  const confMedia         = confianca_media != null
    ? `${(confianca_media * 100).toFixed(1)}%`
    : "—";

  const classesSorted = Object.entries(por_classe)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  const ts = ultima_atualizacao ? new Date(ultima_atualizacao).toLocaleString("pt-BR") : "—";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-gray-700">Dashboard Operacional</h2>
        <span className="text-[10px] text-gray-400">última classificação: {ts}</span>
      </div>

      {/* KPIs — linha 1 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total classificadas",  value: total.toLocaleString("pt-BR"), color: "text-[#1B2B5B]" },
          { label: "Acurácia",              value: taxaAcerto,                    color: "text-emerald-600",
            sub: `${autoClassificadas} sem revisão` },
          { label: "Confiança média",      value: confMedia,                     color: "text-[#1B2B5B]" },
          { label: "Erros técnicos",       value: erros.toLocaleString("pt-BR"), color: erros > 0 ? "text-red-600" : "text-gray-400" },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <p className={`text-xl font-bold ${color}`}>{value}</p>
            <p className="text-[10px] text-gray-400 mt-0.5 uppercase tracking-wide">{label}</p>
            {sub && <p className="text-[10px] text-gray-300 mt-0.5">{sub}</p>}
          </div>
        ))}
      </div>

      {/* Roteamento (local + IA = 100%) e Revisão Humana — lado a lado */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

        {/* Roteamento — origem da classificação (local + IA somam 100%) */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
            Roteamento <span className="text-gray-300 font-normal normal-case">(origem)</span>
          </p>
          <div className="flex gap-5 mb-3">
            <div>
              <span className="text-base font-bold text-emerald-600">{local_gate}</span>
              <span className="text-xs text-gray-500 ml-1">local</span>
              <p className="text-[10px] text-gray-400">{pct(local_gate, total)}</p>
            </div>
            <div>
              <span className="text-base font-bold text-[#1B2B5B]">{gemini}</span>
              <span className="text-xs text-gray-500 ml-1">IA</span>
              <p className="text-[10px] text-gray-400">{pct(gemini, total)}</p>
            </div>
          </div>
          {total > 0 && (
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden flex">
              <div className="bg-emerald-400 h-full transition-all"
                style={{ width: `${pctNum(local_gate, total)}%` }} />
              <div className="bg-[#1B2B5B] h-full transition-all"
                style={{ width: `${pctNum(gemini, total)}%` }} />
            </div>
          )}
          <p className="text-[10px] text-gray-300 mt-2">local + IA = 100% do total</p>
        </div>

        {/* Revisão Humana — dimensão independente */}
        <div className={`rounded-xl border p-4 shadow-sm ${
          revisao_humana > 0
            ? "bg-amber-50 border-amber-200"
            : "bg-white border-gray-100"
        }`}>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
            Revisão Humana <span className="text-gray-300 font-normal normal-case">(validação)</span>
          </p>
          <div className="flex items-end gap-2 mb-2">
            <span className={`text-2xl font-bold ${revisao_humana > 0 ? "text-amber-600" : "text-gray-400"}`}>
              {revisao_humana}
            </span>
            <span className="text-xs text-gray-500 mb-1">
              de {total} ({pct(revisao_humana, total)})
            </span>
          </div>
          {total > 0 && (
            <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
              <div className={`h-full transition-all ${revisao_humana > 0 ? "bg-amber-400" : "bg-gray-300"}`}
                style={{ width: `${pctNum(revisao_humana, total)}%` }} />
            </div>
          )}
          <p className="text-[10px] text-gray-400 mt-2">precisam de validação manual</p>
        </div>
      </div>

      {/* Distribuição de classes */}
      {classesSorted.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Distribuição de Classes</p>
          <div className="space-y-2">
            {classesSorted.map(([classe, count]) => (
              <div key={classe} className="flex items-center gap-3">
                <span className="text-[11px] text-gray-600 w-44 truncate flex-shrink-0">{classe}</span>
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#1B2B5B]/60 rounded-full"
                    style={{ width: `${pctNum(count, total)}%` }} />
                </div>
                <span className="text-[11px] text-gray-500 w-8 text-right flex-shrink-0">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detalhes por processo */}
      {detalhesSorted.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              Acurácia por Processo
            </p>
            <span className="text-[10px] text-gray-400">{detalhesSorted.length} registros</span>
          </div>
          <div className="overflow-x-auto max-h-72 overflow-y-auto">
            <table className="w-full text-xs min-w-[500px]">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="border-b border-gray-100">
                  <th className="text-left px-3 py-2 font-semibold text-gray-500 w-20">Código</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-500">Classe</th>
                  <th className="text-center px-3 py-2 font-semibold text-gray-500 w-24">Acurácia</th>
                  <th className="text-center px-3 py-2 font-semibold text-gray-500 w-16">Origem</th>
                  <th className="text-center px-3 py-2 font-semibold text-gray-500 w-20">Revisão</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {detalhesSorted.map((d, i) => {
                  const pctConf = d.confianca != null ? (d.confianca * 100) : null;
                  const confColor = pctConf == null ? "text-gray-300"
                    : pctConf >= 85 ? "text-emerald-600"
                    : pctConf >= 60 ? "text-amber-500"
                    : "text-red-500";
                  return (
                    <tr key={i} className={`hover:bg-gray-50 transition-colors ${d.erro ? "bg-red-50" : ""}`}>
                      <td className="px-3 py-2 font-mono text-gray-600 whitespace-nowrap">
                        {d.codigo || "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-700 max-w-[180px]">
                        <span className="truncate block" title={d.classe}>{d.classe || "—"}</span>
                        {d.preview && (
                          <span className="text-[10px] text-gray-400 truncate block" title={d.preview}>
                            {d.preview}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {pctConf != null ? (
                          <div className="flex flex-col items-center gap-0.5">
                            <span className={`font-bold ${confColor}`}>
                              {pctConf.toFixed(1)}%
                            </span>
                            <div className="w-14 h-1 bg-gray-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${
                                pctConf >= 85 ? "bg-emerald-400" : pctConf >= 60 ? "bg-amber-400" : "bg-red-400"
                              }`} style={{ width: `${pctConf}%` }} />
                            </div>
                          </div>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                          d.origem === "gemini"
                            ? "bg-[#1B2B5B]/10 text-[#1B2B5B]"
                            : "bg-emerald-50 text-emerald-700"
                        }`}>
                          {d.origem === "gemini" ? "IA" : "Local"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {d.erro
                          ? <span className="text-red-500 font-bold text-[10px]">Erro</span>
                          : d.revisao
                          ? <span className="text-amber-500 font-semibold text-[10px]">⚠ Revisar</span>
                          : <span className="text-emerald-600 text-[10px]">✓ Auto</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Resumo do envio ao Autojur */}
      {envioResumo && !envioResumo.desabilitado && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              Envio Autojur
            </p>
            <div className="flex items-center gap-3 text-[10px]">
              <span className={`font-semibold ${envioResumo.autojur_erro > 0 ? "text-red-600" : "text-emerald-600"}`}>
                {envioResumo.autojur_ok}/{envioResumo.total} enviados
              </span>
              <span className="text-gray-300">|</span>
              <span className="text-[#1B2B5B] font-semibold">
                GED: {envioResumo.ged_pdf_ok} PDFs · {envioResumo.ged_print_ok} prints
              </span>
              {(envioResumo.ged_pdf_skip > 0 || envioResumo.ged_print_skip > 0) && (
                <span className="text-gray-400">{envioResumo.ged_pdf_skip} sem PDF · {envioResumo.ged_print_skip} sem print</span>
              )}
              {(envioResumo.ged_pdf_erro > 0 || envioResumo.ged_print_erro > 0) && (
                <span className="text-red-500">{(envioResumo.ged_pdf_erro || 0) + (envioResumo.ged_print_erro || 0)} erro GED</span>
              )}
            </div>
          </div>

          {/* KPIs de envio */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-0 divide-x divide-gray-100 border-b border-gray-100">
            {[
              { label: "Total",           value: envioResumo.total,          color: "text-gray-700" },
              { label: "Autojur OK",      value: envioResumo.put_ok,         color: "text-emerald-600" },
              { label: "Autojur Erro",    value: envioResumo.put_erro,       color: envioResumo.put_erro > 0 ? "text-red-600" : "text-gray-400" },
              { label: "PDF Enviado",     value: envioResumo.ged_pdf_ok,     color: "text-[#1B2B5B]" },
              { label: "Print Enviado",   value: envioResumo.ged_print_ok,   color: "text-[#1B2B5B]" },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex flex-col items-center py-3 px-2">
                <span className={`text-lg font-bold ${color}`}>{value}</span>
                <span className="text-[9px] text-gray-400 uppercase tracking-wide mt-0.5">{label}</span>
              </div>
            ))}
          </div>

          {/* Tabela por processo */}
          <div className="overflow-x-auto max-h-80 overflow-y-auto">
            <table className="w-full text-xs min-w-[620px]">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="border-b border-gray-100">
                  <th className="text-left px-3 py-2 font-semibold text-gray-500 w-24">Código</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-500 w-40">CNJ</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-500">Classe</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-500 w-28">UUID Autojur</th>
                  <th className="text-center px-3 py-2 font-semibold text-gray-500 w-20">Autojur</th>
                  <th className="text-center px-3 py-2 font-semibold text-gray-500 w-20">GED</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-500">PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(envioResumo.processos || []).map((p, i) => (
                  <tr key={i} className={`hover:bg-gray-50 transition-colors ${!p.autojur_ok ? "bg-red-50" : ""}`}>
                    <td className="px-3 py-2 font-mono text-gray-600 whitespace-nowrap">{p.codigo || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-gray-500 whitespace-nowrap">{p.cnj || "—"}</td>
                    <td className="px-3 py-2 text-gray-700 max-w-[200px]">
                      <span className="truncate block" title={p.classe}>{p.classe || <span className="text-gray-300 italic">sem classe</span>}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px] text-gray-400">
                      {p.uuid ? (
                        <span title={p.uuid}>{p.uuid.slice(0, 8)}…</span>
                      ) : <span className="text-red-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {p.autojur_ok
                        ? <span className="text-emerald-600 font-bold">✓</span>
                        : <span className="text-red-500 font-bold">✗</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {p.ged_ok === true  ? <span className="text-emerald-600 font-bold">✓</span>
                      : p.ged_ok === false ? <span className="text-red-500 font-bold">✗</span>
                      : <span className="text-gray-300 text-[10px]">—</span>}
                    </td>
                    <td className="px-3 py-2 text-[10px] text-gray-400 max-w-[160px]">
                      <span className="truncate block" title={p.ged_arquivo}>{p.ged_arquivo || "—"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {total === 0 && !envioResumo && (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center shadow-sm">
          {rodando ? (
            <>
              <Loader size={18} className="animate-spin text-[#1B2B5B] mx-auto mb-2" />
              <p className="text-gray-500 text-sm font-medium">Buscando processos no eproc...</p>
              <p className="text-gray-300 text-xs mt-1">Isso pode levar alguns minutos.</p>
            </>
          ) : (
            <>
              <p className="text-gray-400 text-sm">Nenhuma classificação registrada ainda.</p>
              <p className="text-gray-300 text-xs mt-1">Faça upload de um CSV do Autojur e execute o classificador.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Contagem "kit de baixa" no Autojur x Base de Garantias — novo fluxo do
// worker Baixa de Processos. A busca parte do Autojur (evento "kit de
// baixa"); a Base de Garantias entra só depois, como consulta, para saber
// se o processo ainda tem saldo (fica AGUARDANDO/EM_ANDAMENTO) ou não
// (segue para o tribunal e, se baixado/arquivado, para o SISJUR).
function KitDeBaixaAutojurPainel({ data, atualizando, onAtualizar }) {
  if (!data) {
    return (
      <section className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
        <div className="flex items-center justify-center text-gray-400 text-xs py-4">
          <Loader size={16} className="animate-spin mr-2" /> Consultando Autojur...
        </div>
      </section>
    );
  }

  if (data.erro) {
    return (
      <section className="bg-red-50 border border-red-200 rounded-xl p-4">
        <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest mb-1">Kit de Baixa — Autojur</p>
        <p className="text-xs text-red-700">{data.erro}</p>
        <button onClick={onAtualizar} disabled={atualizando}
          className="mt-2 text-[10px] font-semibold text-red-600 hover:underline disabled:opacity-50">
          Tentar de novo
        </button>
      </section>
    );
  }

  const {
    total_abertas = 0, na_base_garantia = 0, fora_base_garantia = 0,
    fora_base_eproc_tjsc = 0, fora_base_projudi_tjpr = 0, fora_base_desconhecido = 0,
    fora_base_sem_cnj = 0, ultima_atualizacao,
  } = data;

  const pctNum = (n, d) => (d > 0 ? ((n / d) * 100).toFixed(0) : "0");
  const ts = ultima_atualizacao
    ? new Date(ultima_atualizacao).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <section className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Kit de Baixa — Autojur</p>
        <button onClick={onAtualizar} disabled={atualizando}
          title="Consultar o Autojur agora (ignora o cache de 5 min)"
          className="text-gray-300 hover:text-[#1B2B5B] disabled:opacity-40">
          <RefreshCw size={12} className={atualizando ? "animate-spin" : ""} />
        </button>
      </div>

      <div>
        <p className="text-2xl font-bold text-[#1B2B5B]">{total_abertas.toLocaleString("pt-BR")}</p>
        <p className="text-[10px] text-gray-400">tarefas em aberto (Aguardando/Em Andamento/Nova)</p>
      </div>

      <div className="h-2 rounded-full bg-gray-100 overflow-hidden flex">
        <div className="bg-emerald-400 h-full" style={{ width: `${pctNum(na_base_garantia, total_abertas)}%` }} />
        <div className="bg-amber-400 h-full" style={{ width: `${pctNum(fora_base_garantia, total_abertas)}%` }} />
      </div>

      <div className="flex justify-between text-xs">
        <div>
          <span className="font-bold text-emerald-600">{na_base_garantia}</span>
          <span className="text-gray-400 ml-1">na Base de Garantia</span>
          <p className="text-[10px] text-gray-300">tem saldo → vira EM_ANDAMENTO</p>
        </div>
        <div className="text-right">
          <span className="font-bold text-amber-600">{fora_base_garantia}</span>
          <span className="text-gray-400 ml-1">sem saldo</span>
          <p className="text-[10px] text-gray-300">seguem para o tribunal</p>
        </div>
      </div>

      <div className="border-t border-gray-100 pt-2.5 space-y-1">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Sem saldo — destino no tribunal</p>
        <div className="flex justify-between text-[11px]">
          <span className="text-gray-500">eproc (TJSC)</span>
          <span className="font-semibold text-gray-700">{fora_base_eproc_tjsc}</span>
        </div>
        <div className="flex justify-between text-[11px]">
          <span className="text-gray-500">projudi (TJPR)</span>
          <span className="font-semibold text-gray-700">{fora_base_projudi_tjpr}</span>
        </div>
        {fora_base_desconhecido > 0 && (
          <div className="flex justify-between text-[11px]">
            <span className="text-gray-400">CNJ não reconhecido</span>
            <span className="font-semibold text-gray-500">{fora_base_desconhecido}</span>
          </div>
        )}
        {fora_base_sem_cnj > 0 && (
          <div className="flex justify-between text-[11px]">
            <span className="text-gray-400">sem CNJ (travados)</span>
            <span className="font-semibold text-gray-500">{fora_base_sem_cnj}</span>
          </div>
        )}
      </div>

      <p className="text-[10px] text-gray-300 text-right">atualizado {ts}</p>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 3. PAINEL DO ROBÔ
// ═══════════════════════════════════════════════════════════════════════════════
function RobotPanel({ robo, onVoltar, user, onLogout, onUsers, onCredentials, onExecucoes }) {
  const [registros,    setRegistros]    = useState([]);
  const [logs,         setLogs]         = useState([]);
  const [status,       setStatus]       = useState({ chrome: false, rodando: false });
  const [resultados,   setResultados]   = useState([]);
  const [alerta,       setAlerta]       = useState(null);
  const [novncPort,    setNovncPort]    = useState(null);
  const [credentials,  setCredentials]  = useState([]);
  const [credSel,      setCredSel]      = useState("");
  const [credSelTjpr,  setCredSelTjpr]  = useState("");
  const [eprocZipPath, setEprocZipPath] = useState(null);
  const [alertaZip,    setAlertaZip]    = useState(null);
  const [uploadingZip, setUploadingZip] = useState(false);
  const [zipsDir,      setZipsDir]      = useState(null);
  const [zipsFiles,    setZipsFiles]    = useState([]);   // [{ nome, ok }]
  const [uploadingZips,setUploadingZips]= useState(false);
  const [alertaZips,   setAlertaZips]   = useState(null);
  const [runningExec,         setRunningExec]        = useState(null); // execução em andamento (do banco)
  const [peticaoZipPath,      setPeticaoZipPath]     = useState(null);
  const [alertaZipPeticao,    setAlertaZipPeticao]   = useState(null);
  const [uploadingZipPeticao, setUploadingZipPeticao]= useState(false);
  const [enriquecendoIA,      setEnriquecendoIA]     = useState(false);
  const [resultadoIA,         setResultadoIA]        = useState(null); // { output_path, stats }
  const [envioResumo,         setEnvioResumo]        = useState(null); // resumo do POST Autojur
  const [agendamento,         setAgendamento]         = useState(null);
  const [showModalAgendar,    setShowModalAgendar]    = useState(false);
  const [horaAgendada,        setHoraAgendada]        = useState("20:00");
  const [agendandoLoading,    setAgendandoLoading]    = useState(false);
  const [alertaAgendar,       setAlertaAgendar]       = useState(null);
  // ── Consulta Tribunais ─────────────────────────────────────────────────────
  const [tribunalSel,      setTribunalSel]      = useState("");
  const [csvPath,          setCsvPath]          = useState(null);
  const [uploadingCsv,     setUploadingCsv]     = useState(false);
  const [alertaCsv,        setAlertaCsv]        = useState(null);
  // ── Análise de Processos — upload de planilha + storage.zip ────────────────
  const [analisePlanilhaPath,      setAnalisePlanilhaPath]      = useState(null);
  const [uploadingAnalisePlanilha, setUploadingAnalisePlanilha] = useState(false);
  const [alertaAnalisePlanilha,    setAlertaAnalisePlanilha]    = useState(null);
  const [analiseStoragePath,       setAnaliseStoragePath]       = useState(null);
  const [uploadingAnaliseStorage,  setUploadingAnaliseStorage]  = useState(false);
  const [alertaAnaliseStorage,     setAlertaAnaliseStorage]     = useState(null);
  const [analiseAmbiente,          setAnaliseAmbiente]          = useState("homologacao");
  // ── PDF to Text — upload de um ou mais PDFs (partes do mesmo processo) ──────
  const [pdfArquivos,        setPdfArquivos]        = useState([]);   // [{ nome, path, tamanho }]
  const [uploadingPdf,       setUploadingPdf]       = useState(false);
  const [alertaPdf,          setAlertaPdf]          = useState(null);
  const [pdfModo,            setPdfModo]            = useState("completo"); // completo | dividido
  const [pdfAnalisar,        setPdfAnalisar]        = useState(false);
  const [pdfPlano,           setPdfPlano]           = useState(null);  // plano devolvido pelo /run quando precisa confirmação
  const [pdfAguardaConfirmacao, setPdfAguardaConfirmacao] = useState(false);
  const [showModalChecklist, setShowModalChecklist] = useState(false);
  const [showModalProducaoConfirm, setShowModalProducaoConfirm] = useState(false);
  const [checklistTexto,     setChecklistTexto]     = useState("");
  const [checklistLoading,   setChecklistLoading]   = useState(false);
  const [checklistSaving,    setChecklistSaving]    = useState(false);
  const [checklistAlerta,    setChecklistAlerta]    = useState(null);
  // ── Execução automática (consulta-tribunais e classificador) ───────────────
  const [autoAgendado,     setAutoAgendado]     = useState(false);
  const [autoHora,         setAutoHora]         = useState("02:00");
  const [autoNotifUsers,   setAutoNotifUsers]   = useState([]);
  const [allUsers,         setAllUsers]         = useState([]);
  const [autoStatus,       setAutoStatus]       = useState(null);
  const [limpandoTjpr,     setLimpandoTjpr]     = useState(false);
  const [limpandoTjsc,     setLimpandoTjsc]     = useState(false);
  const [toasts,           setToasts]           = useState([]); // [{ id, tipo, msg }]
  // ── Classificador de Intimações ────────────────────────────────────────────
  const [dashboardData,      setDashboardData]      = useState(null);
  // ── Baixa de Processos — contagem "kit de baixa" no Autojur ────────────────
  const [kitBaixaContagem,   setKitBaixaContagem]   = useState(null);
  const [kitBaixaAtualizando, setKitBaixaAtualizando] = useState(false);
  const [uploadingBaseGarantias, setUploadingBaseGarantias] = useState(false);
  const [alertaBaseGarantias,    setAlertaBaseGarantias]    = useState(null);
  // ── Classificador — busca automática via API AutoJur ────────────────────────
  const [autojurAgendado,    setAutojurAgendado]    = useState(false);
  const [autojurHora,        setAutojurHora]        = useState("09:00");
  const [autojurHora2,       setAutojurHora2]       = useState("");
  const [autojurEmailsTexto, setAutojurEmailsTexto] = useState(""); // e-mails separados por vírgula
  const [autojurStatus,      setAutojurStatus]      = useState(null);
  const [autojurLimiteTeste, setAutojurLimiteTeste] = useState(""); // limite manual só para teste (vazio = sem limite)
  // ── SisJur-insercao — busca automática de tarefas PEX via API Autojur ───────
  const [autojurSisjurAgendado,    setAutojurSisjurAgendado]    = useState(false);
  const [autojurSisjurHora,        setAutojurSisjurHora]        = useState("20:00");
  const [autojurSisjurEmailsTexto, setAutojurSisjurEmailsTexto] = useState("");
  const [autojurSisjurLimite,      setAutojurSisjurLimite]      = useState(""); // quantas tarefas buscar (vazio = todas as prontas)
  const [autojurSisjurRodandoDireto, setAutojurSisjurRodandoDireto] = useState(false);
  const [autojurSisjurStatus,      setAutojurSisjurStatus]      = useState(null);
  const [autojurSisjurRodando,     setAutojurSisjurRodando]     = useState(false);
  const [autojurSisjurAlerta,      setAutojurSisjurAlerta]      = useState(null);
  const [autojurSisjurResultados,  setAutojurSisjurResultados]  = useState([]);
  const [autojurSisjurExecId,      setAutojurSisjurExecId]      = useState(null);
  // ── Autojur → Loy Legal — captura diária de tarefas-gatilho (CNJ) ───────────
  const [loyAgendado,        setLoyAgendado]        = useState(false);
  const [loyHora,            setLoyHora]            = useState("15:00");
  const [loyHora2,           setLoyHora2]           = useState("23:00");
  const [loyNotifUsers,      setLoyNotifUsers]      = useState([]);
  const [loyStatus,          setLoyStatus]          = useState(null);
  // ── Baixa de Processos — agendamento do fluxo completo ──────────────────────
  // Calendário de dias do mês recorrentes (ex.: todo dia 5 e dia 20), cada
  // um com seu horário — substitui o antigo horário único diário.
  const [baixaAgendaAtiva,   setBaixaAgendaAtiva]   = useState(false);
  const [baixaAgendaSlots,   setBaixaAgendaSlots]   = useState([]); // [{dia_mes, hora}]
  const [baixaEmailsTexto,   setBaixaEmailsTexto]   = useState("");
  const [baixaAgendaStatus,  setBaixaAgendaStatus]  = useState(null);
  // Duas perguntas Sim/Não — valem pra execução manual E pra agendada.
  const [consultarTribunal,      setConsultarTribunal]      = useState(true);
  const [fazerLancamentoSisjur,  setFazerLancamentoSisjur]  = useState(true);
  // Planilha opcional com códigos de tarefa (localizador/IDSISJUR) — filtra
  // o fluxo completo pra rodar só nesse subconjunto; vazio = todas as
  // tarefas "kit de baixa" abertas (comportamento de sempre).
  const [codigosTarefaArquivo,  setCodigosTarefaArquivo]  = useState(null);
  const [codigosTarefaLista,    setCodigosTarefaLista]    = useState([]);
  const [alertaCodigosTarefa,   setAlertaCodigosTarefa]   = useState(null);
  const [baixandoBaseGarantias, setBaixandoBaseGarantias] = useState(false);
  // E-mails de notificação só desta execução manual (opcional) — substitui,
  // sem alterar, a config padrão usada pela execução agendada.
  const [notifEmailsExecucao,   setNotifEmailsExecucao]   = useState("");
  // ── Custas CELOS — notificação por e-mail + status da última execução ───────
  const [custasCelosEmailsTexto, setCustasCelosEmailsTexto] = useState("");
  const [custasCelosStatus,      setCustasCelosStatus]      = useState(null);
  // Resumo por categoria (busca ao vivo no Autojur — quantas tarefas tem
  // aberta em cada uma das 8 categorias de custas CELOS, por tribunal, e se
  // já roda automático ou ainda precisa de validação manual).
  const [celosResumoCategorias, setCelosResumoCategorias] = useState(null);
  const [celosResumoCarregando, setCelosResumoCarregando] = useState(false);
  const [celosResumoErro,       setCelosResumoErro]       = useState(null);
  const [celosResumoGeradoEm,   setCelosResumoGeradoEm]   = useState(null);
  // Agendamento automático (decisão do usuário, 2026-09-28): segunda-feira
  // emite guia real ponta a ponta, sexta-feira busca comprovante — dia da
  // semana é fixo (não configurável), só horário e liga/desliga.
  const [custasCelosAtivoProducao,     setCustasCelosAtivoProducao]     = useState(false);
  const [custasCelosHoraProducao,      setCustasCelosHoraProducao]      = useState("12:00");
  const [custasCelosAtivoComprovantes, setCustasCelosAtivoComprovantes] = useState(false);
  const [custasCelosHoraComprovantes,  setCustasCelosHoraComprovantes]  = useState("16:00");
  const zipsGroupRef = useRef(`grp_${Date.now()}`);
  const pdfGroupRef  = useRef(`grp_${Date.now()}`);

  const terminalRef    = useRef(null);
  const logsOffsetRef  = useRef(0);
  const pollingRef     = useRef(null);
  const executionIdRef = useRef(null);

  // Carrega credenciais do robô
  useEffect(() => {
    api(`/credentials/${robo.id}`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setCredentials(data); })
      .catch(() => {});
  }, [robo.id]);

  // Reconecta ao abrir: restaura executionId e novncPort de execução em andamento
  useEffect(() => {
    api(`/execucoes/running/${robo.id}`)
      .then(r => r.json())
      .then(exec => {
        if (!exec) return;
        setRunningExec(exec);
        if (exec.novnc_port)    setNovncPort(exec.novnc_port);
        if (exec.execution_id)  executionIdRef.current = exec.execution_id;
      })
      .catch(() => {});
  }, [robo.id]);

  // Carrega config/status de execução automática (consulta-tribunais)
  useEffect(() => {
    if (!robo.automatico) return;
    const loadConfig = () => {
      api("/api/prazos/config").then(r => r.json()).then(d => {
        if (d.config) {
          setAutoAgendado(!!d.config.ativo);
          if (d.config.hora) setAutoHora(d.config.hora);
          try {
            const ids = JSON.parse(d.config.notification_user_ids || "[]");
            setAutoNotifUsers(ids.map(String));
          } catch {}
        }
        if (Array.isArray(d.users)) setAllUsers(d.users);
      }).catch(() => {});
    };
    const loadStatus = () => {
      api("/api/prazos/status").then(r => r.json()).then(setAutoStatus).catch(() => {});
    };
    loadConfig(); loadStatus();
    const iv = setInterval(loadStatus, 8000);
    return () => clearInterval(iv);
  }, [robo.id, robo.automatico]);

  // Carrega agendamento pendente (somente sisjur-insercao)
  useEffect(() => {
    if (!robo.temZipsMultiplos) return;
    api(`/api/agendamentos/${robo.id}`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data) && data.length > 0) setAgendamento(data[0]); })
      .catch(() => {});
  }, [robo.id, robo.temZipsMultiplos]);

  // Carrega métricas do dashboard do classificador (poll a cada 30s)
  useEffect(() => {
    if (!robo.temClassificador) return;
    const fetch_dash = () => {
      api(`/dashboard/${robo.id}`)
        .then(r => r.json())
        .then(data => setDashboardData(data))
        .catch(() => {});
    };
    fetch_dash();
    const iv = setInterval(fetch_dash, 30000);
    return () => clearInterval(iv);
  }, [robo.id, robo.temClassificador]);

  // Contagem "kit de baixa" no Autojur x Base de Garantias (poll a cada 30s
  // — o worker mesmo cacheia por 5min, então isso não martela a API real)
  const fetchKitBaixaContagem = useCallback((forcar) => {
    if (forcar) setKitBaixaAtualizando(true);
    return api(`/dashboard/baixa-processos${forcar ? "?forcar=1" : ""}`)
      .then(r => r.json())
      .then(data => setKitBaixaContagem(data))
      .catch(() => {})
      .finally(() => setKitBaixaAtualizando(false));
  }, []);

  useEffect(() => {
    if (robo.id !== "baixa-processos") return;
    fetchKitBaixaContagem(false);
    const iv = setInterval(() => fetchKitBaixaContagem(false), 30000);
    return () => clearInterval(iv);
  }, [robo.id, fetchKitBaixaContagem]);

  const handleBaseGarantiasUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    setUploadingBaseGarantias(true);
    setAlertaBaseGarantias(null);
    try {
      const resp = await fetch(`${API_URL}/upload/base-garantias`, {
        method: "POST",
        headers: {
          "Content-Type":  "application/octet-stream",
          "Authorization": `Bearer ${localStorage.getItem("rpa_token")}`,
        },
        body: file,
      });
      const data = await resp.json();
      if (resp.ok && data.ok) {
        setAlertaBaseGarantias({ tipo: "ok", msg: `Base de Garantias atualizada: ${file.name}` });
        fetchKitBaixaContagem(true);
      } else {
        setAlertaBaseGarantias({ tipo: "erro", msg: data.erro || "Erro ao enviar a planilha." });
      }
    } catch {
      setAlertaBaseGarantias({ tipo: "erro", msg: "Falha ao enviar a planilha." });
    } finally {
      setUploadingBaseGarantias(false);
    }
  };

  // Baixa a Base de Garantias em uso agora pelo fluxo completo — a que o
  // usuário importou ou, se ninguém importou ainda, a "de fábrica" que já
  // vem com a imagem do worker (hoje invisível, só citada como texto).
  const handleDownloadBaseGarantias = async () => {
    setBaixandoBaseGarantias(true);
    try {
      const r = await api("/api/baixa-processos/base-garantias");
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setAlertaBaseGarantias({ tipo: "erro", msg: data.erro || "Erro ao baixar a Base de Garantias." });
        return;
      }
      const blob = await r.blob();
      const nome = kitBaixaContagem?.base_garantias_arquivo || "base-garantias.xlsx";
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = nome; a.click();
      URL.revokeObjectURL(url);
    } catch {
      setAlertaBaseGarantias({ tipo: "erro", msg: "Falha ao baixar a Base de Garantias." });
    } finally {
      setBaixandoBaseGarantias(false);
    }
  };

  // Planilha de códigos de tarefa (localizador/IDSISJUR) — filtra o fluxo
  // completo pra rodar só nesse subconjunto. Fica só em memória no front
  // (mesmo padrão dos outros robôs com colKeys) — nada é enviado ao
  // backend até o clique em "Rodar fluxo completo".
  const handleCodigosTarefaUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    parseXlsx(file, robo.colunas,
      (dados) => {
        const codigos = dados.map(d => String(d.codigo).trim()).filter(Boolean);
        setCodigosTarefaLista(codigos);
        setCodigosTarefaArquivo(file.name);
        setAlertaCodigosTarefa({ tipo: "ok", msg: `${codigos.length} código(s) carregado(s) de ${file.name}.` });
      },
      (err) => setAlertaCodigosTarefa({ tipo: "erro", msg: err }),
    );
  };

  const limparCodigosTarefa = () => {
    setCodigosTarefaLista([]);
    setCodigosTarefaArquivo(null);
    setAlertaCodigosTarefa(null);
  };

  // Carrega config + status da busca automática de intimações via API AutoJur
  useEffect(() => {
    if (!robo.temClassificador) return;
    const loadConfig = () => {
      api("/api/autojur/config").then(r => r.json()).then(d => {
        if (d.config) {
          setAutojurAgendado(!!d.config.ativo);
          if (d.config.hora) setAutojurHora(d.config.hora);
          setAutojurHora2(d.config.hora2 || "");
          try {
            const emails = JSON.parse(d.config.notification_emails || "[]");
            setAutojurEmailsTexto(emails.join(", "));
          } catch {}
        }
      }).catch(() => {});
    };
    const loadStatus = () => {
      api("/api/autojur/status").then(r => r.json()).then(setAutojurStatus).catch(() => {});
    };
    loadConfig();
    loadStatus();
    const iv = setInterval(loadStatus, 8000);
    return () => clearInterval(iv);
  }, [robo.id, robo.temClassificador]);

  // Quando polling detecta que ficou rodando mas runningExec está vazio, busca do banco
  useEffect(() => {
    if (!status.rodando || runningExec) return;
    api(`/execucoes/running/${robo.id}`)
      .then(r => r.json())
      .then(exec => {
        if (!exec) return;
        setRunningExec(exec);
        if (exec.novnc_port && !novncPort)          setNovncPort(exec.novnc_port);
        if (exec.execution_id && !executionIdRef.current) executionIdRef.current = exec.execution_id;
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.rodando, robo.id]);

  const salvarAutojurConfig = async (campo) => {
    const emailsTexto = campo.emailsTexto !== undefined ? campo.emailsTexto : autojurEmailsTexto;
    const emails = emailsTexto.split(",").map(e => e.trim()).filter(e => e.includes("@"));
    try {
      await api("/api/autojur/config", {
        method: "PUT",
        body: JSON.stringify({
          ativo:                campo.ativo !== undefined ? campo.ativo : autojurAgendado,
          hora:                 campo.hora  !== undefined ? campo.hora  : autojurHora,
          hora2:                campo.hora2 !== undefined ? campo.hora2 : autojurHora2,
          notification_emails:  emails,
        }),
      });
    } catch {}
  };

  // Carrega config + status da busca automática de tarefas SisJur PEX via API Autojur
  useEffect(() => {
    if (!robo.temBuscaAutojurSisjur) return;
    const loadConfig = () => {
      api("/api/autojur-sisjur/config").then(r => r.json()).then(d => {
        if (d.config) {
          setAutojurSisjurAgendado(!!d.config.ativo);
          if (d.config.hora) setAutojurSisjurHora(d.config.hora);
          try {
            const emails = JSON.parse(d.config.notification_emails || "[]");
            setAutojurSisjurEmailsTexto(emails.join(", "));
          } catch {}
        }
      }).catch(() => {});
    };
    const loadStatus = () => {
      api("/api/autojur-sisjur/status").then(r => r.json()).then(setAutojurSisjurStatus).catch(() => {});
    };
    loadConfig();
    loadStatus();
    const iv = setInterval(loadStatus, 8000);
    return () => clearInterval(iv);
  }, [robo.id, robo.temBuscaAutojurSisjur]);

  // Enquanto a busca (deste botão) estiver rodando, consulta os resultados do
  // worker pelo mesmo executionId — reaproveita os proxies genéricos /status
  // e /resultados já usados pelo fluxo manual (o worker guarda a sessão da
  // busca no mesmo dicionário de sessões usado pelo /run).
  useEffect(() => {
    if (!autojurSisjurRodando || !autojurSisjurExecId) return;
    const { workerExecutionId } = autojurSisjurExecId;
    const poll = async () => {
      try {
        const st = await (await api(`/status/sisjur-insercao?executionId=${workerExecutionId}`)).json();
        if (!st.rodando) {
          const res = await (await api(`/resultados/sisjur-insercao?executionId=${workerExecutionId}`)).json();
          setAutojurSisjurResultados(Array.isArray(res) ? res : []);
          setAutojurSisjurRodando(false);
        }
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => clearInterval(iv);
  }, [autojurSisjurRodando, autojurSisjurExecId]);

  const salvarAutojurSisjurConfig = async (campo) => {
    const emailsTexto = campo.emailsTexto !== undefined ? campo.emailsTexto : autojurSisjurEmailsTexto;
    const emails = emailsTexto.split(",").map(e => e.trim()).filter(e => e.includes("@"));
    try {
      await api("/api/autojur-sisjur/config", {
        method: "PUT",
        body: JSON.stringify({
          ativo:               campo.ativo !== undefined ? campo.ativo : autojurSisjurAgendado,
          hora:                campo.hora  !== undefined ? campo.hora  : autojurSisjurHora,
          notification_emails: emails,
        }),
      });
    } catch {}
  };

  const buscarAutojurSisjurAgora = async () => {
    setAutojurSisjurAlerta(null); setAutojurSisjurResultados([]);
    const limite = parseInt(autojurSisjurLimite, 10);
    try {
      const resp = await api("/api/autojur-sisjur/executar", {
        method: "POST",
        body: JSON.stringify(limite > 0 ? { limite } : {}),
      });
      const data = await resp.json();
      if (resp.ok && data.ok) {
        setAutojurSisjurExecId({ dbId: data.execId, workerExecutionId: data.executionId });
        setAutojurSisjurRodando(true);
      } else {
        setAutojurSisjurAlerta({ tipo: "erro", msg: data.erro || "Erro ao iniciar busca." });
      }
    } catch {
      setAutojurSisjurAlerta({ tipo: "erro", msg: "Falha ao conectar API." });
    }
  };

  // "Rodar com estes resultados" — usa a planilha + zips individuais que a
  // busca automática já gerou no worker, direto, sem o usuário precisar
  // baixar e reenviar nada.
  const rodarComResultadosBusca = async () => {
    if (!credSel || !autojurSisjurExecId?.workerExecutionId) return;
    setAutojurSisjurRodandoDireto(true);
    setAutojurSisjurAlerta(null);
    // Mesmo padrão de executarRPA — sem isso, o painel principal (noVNC/log)
    // continua mostrando "Aguardando execução" mesmo com o worker já rodando.
    setLogs([]); setNovncPort(null);
    executionIdRef.current = null; logsOffsetRef.current = 0;
    setResultados([]); setAlerta(null);
    try {
      const resp = await api("/api/autojur-sisjur/executar-direto", {
        method: "POST",
        body: JSON.stringify({
          workerExecutionId: autojurSisjurExecId.workerExecutionId,
          credential_id:     credSel,
        }),
      });
      const data = await resp.json();
      if (resp.ok && data.ok) {
        if (data.novncPort)   setNovncPort(data.novncPort);
        if (data.executionId) executionIdRef.current = data.executionId;
        setRunningExec({ user_id: user?.id, user_name: user?.name, novnc_port: data.novncPort, execution_id: data.executionId });
        setAutojurSisjurAlerta({ tipo: "ok", msg: "Execução real disparada — acompanhe aqui embaixo ou no Monitoramento de Execuções." });
      } else {
        setAutojurSisjurAlerta({ tipo: "erro", msg: data.erro || "Erro ao disparar a execução." });
      }
    } catch {
      setAutojurSisjurAlerta({ tipo: "erro", msg: "Falha ao conectar API." });
    } finally {
      setAutojurSisjurRodandoDireto(false);
    }
  };

  // Carrega config + status da captura Autojur -> Loy Legal
  useEffect(() => {
    if (!robo.temLoyCaptura) return;
    const loadConfig = () => {
      api("/api/loy/config").then(r => r.json()).then(d => {
        if (d.config) {
          setLoyAgendado(!!d.config.ativo);
          if (d.config.hora) setLoyHora(d.config.hora);
          if (d.config.hora2) setLoyHora2(d.config.hora2);
          try {
            const ids = JSON.parse(d.config.notification_user_ids || "[]");
            setLoyNotifUsers(ids.map(String));
          } catch {}
        }
        if (Array.isArray(d.users)) setAllUsers(d.users);
      }).catch(() => {});
    };
    const loadStatus = () => {
      api("/api/loy/status").then(r => r.json()).then(setLoyStatus).catch(() => {});
    };
    loadConfig();
    loadStatus();
    const iv = setInterval(loadStatus, 8000);
    return () => clearInterval(iv);
  }, [robo.id, robo.temLoyCaptura]);

  const salvarLoyConfig = async (campo) => {
    try {
      await api("/api/loy/config", {
        method: "PUT",
        body: JSON.stringify({
          ativo:                  campo.ativo !== undefined ? campo.ativo : loyAgendado,
          hora:                   campo.hora  !== undefined ? campo.hora  : loyHora,
          hora2:                  campo.hora2 !== undefined ? campo.hora2 : loyHora2,
          notification_user_ids:  campo.notif !== undefined ? campo.notif : loyNotifUsers.map(Number),
        }),
      });
    } catch {}
  };

  const executarLoyAgora = async () => {
    try {
      await api("/api/loy/executar", { method: "POST" });
      setLoyStatus(s => ({ ...s, rodando: true }));
    } catch {}
  };

  // Carrega config (calendário + toggles Sim/Não + e-mails) + status do
  // fluxo completo de Baixa de Processos
  useEffect(() => {
    if (robo.id !== "baixa-processos") return;
    const loadConfig = () => {
      api("/api/baixa-processos/config").then(r => r.json()).then(d => {
        if (d.config) {
          setBaixaAgendaAtiva(!!d.config.agenda_ativa);
          setConsultarTribunal(d.config.consultar_tribunal !== false);
          setFazerLancamentoSisjur(d.config.fazer_lancamento_sisjur !== false);
          try {
            const emails = JSON.parse(d.config.notification_emails || "[]");
            setBaixaEmailsTexto(emails.join(", "));
          } catch {}
        }
        if (Array.isArray(d.agenda)) {
          setBaixaAgendaSlots(d.agenda.map(s => ({ dia_mes: s.dia_mes, hora: s.hora })));
        }
      }).catch(() => {});
    };
    const loadStatus = () => {
      api("/api/baixa-processos/status").then(r => r.json()).then(setBaixaAgendaStatus).catch(() => {});
    };
    loadConfig();
    loadStatus();
    const iv = setInterval(loadStatus, 8000);
    return () => clearInterval(iv);
  }, [robo.id]);

  const salvarBaixaConfig = async (campo) => {
    const emailsTexto = campo.emailsTexto !== undefined ? campo.emailsTexto : baixaEmailsTexto;
    const emails = emailsTexto.split(",").map(e => e.trim()).filter(e => e.includes("@"));
    try {
      await api("/api/baixa-processos/config", {
        method: "PUT",
        body: JSON.stringify({
          agenda_ativa:            campo.agenda_ativa            !== undefined ? campo.agenda_ativa            : baixaAgendaAtiva,
          consultar_tribunal:      campo.consultar_tribunal       !== undefined ? campo.consultar_tribunal      : consultarTribunal,
          fazer_lancamento_sisjur: campo.fazer_lancamento_sisjur  !== undefined ? campo.fazer_lancamento_sisjur : fazerLancamentoSisjur,
          notification_emails:     emails,
          slots:                   campo.slots !== undefined ? campo.slots : baixaAgendaSlots,
        }),
      });
    } catch {}
  };

  // Carrega config (e-mails de notificação) + status da última execução do
  // fluxo completo de Custas CELOS.
  useEffect(() => {
    if (robo.id !== "custas-celos") return;
    const loadConfig = () => {
      api("/api/custas-celos/config").then(r => r.json()).then(d => {
        if (d.config) {
          try {
            const emails = JSON.parse(d.config.notification_emails || "[]");
            setCustasCelosEmailsTexto(emails.join(", "));
          } catch {}
          setCustasCelosAtivoProducao(!!d.config.ativo_producao);
          if (d.config.hora_producao) setCustasCelosHoraProducao(d.config.hora_producao);
          setCustasCelosAtivoComprovantes(!!d.config.ativo_comprovantes);
          if (d.config.hora_comprovantes) setCustasCelosHoraComprovantes(d.config.hora_comprovantes);
        }
      }).catch(() => {});
    };
    const loadStatus = () => {
      api("/api/custas-celos/status").then(r => r.json()).then(setCustasCelosStatus).catch(() => {});
    };
    loadConfig();
    loadStatus();
    buscarResumoCategoriasCelos();
    const iv = setInterval(loadStatus, 8000);
    return () => clearInterval(iv);
  }, [robo.id]);

  const buscarResumoCategoriasCelos = async () => {
    setCelosResumoCarregando(true);
    setCelosResumoErro(null);
    try {
      const resp = await api("/api/custas-celos/resumo-categorias");
      const data = await resp.json();
      if (resp.ok && data.ok !== false) {
        setCelosResumoCategorias(data.linhas || []);
        setCelosResumoGeradoEm(data.gerado_em || null);
      } else {
        setCelosResumoErro(data.erro || "Falha ao buscar resumo de categorias.");
      }
    } catch {
      setCelosResumoErro("Falha ao conectar à API.");
    } finally {
      setCelosResumoCarregando(false);
    }
  };

  const salvarCustasCelosConfig = async (campo) => {
    const emailsTexto = campo?.emailsTexto !== undefined ? campo.emailsTexto : custasCelosEmailsTexto;
    const emails = emailsTexto.split(",").map(e => e.trim()).filter(e => e.includes("@"));
    try {
      await api("/api/custas-celos/config", {
        method: "PUT",
        body: JSON.stringify({
          notification_emails: emails,
          ativo_producao:      campo?.ativo_producao      !== undefined ? campo.ativo_producao      : custasCelosAtivoProducao,
          hora_producao:       campo?.hora_producao       !== undefined ? campo.hora_producao       : custasCelosHoraProducao,
          ativo_comprovantes:  campo?.ativo_comprovantes  !== undefined ? campo.ativo_comprovantes  : custasCelosAtivoComprovantes,
          hora_comprovantes:   campo?.hora_comprovantes   !== undefined ? campo.hora_comprovantes   : custasCelosHoraComprovantes,
        }),
      });
    } catch {}
  };

  const executarCustasCelosProducao = async () => {
    setLogs([]); setNovncPort(null);
    executionIdRef.current = null; logsOffsetRef.current = 0;
    setResultados([]); setAlerta(null);
    try {
      const resp = await api(`/api/custas-celos/executar-producao`, { method: "POST" });
      const data = await resp.json();
      if (data.novncPort)   setNovncPort(data.novncPort);
      if (data.executionId) executionIdRef.current = data.executionId;
      if (resp.ok && data.ok) {
        setRunningExec({ user_id: user?.id, user_name: user?.name, novnc_port: data.novncPort, execution_id: data.executionId });
      } else {
        setAlerta({ tipo: "erro", msg: data.erro || "Erro ao iniciar a produção." });
      }
    } catch {
      setLogs(["[ERRO REDE] Falha ao conectar API."]);
    }
  };

  const executarCustasCelosComprovantes = async () => {
    setLogs([]); setNovncPort(null);
    executionIdRef.current = null; logsOffsetRef.current = 0;
    setResultados([]); setAlerta(null);
    try {
      const resp = await api(`/api/custas-celos/buscar-comprovantes`, { method: "POST" });
      const data = await resp.json();
      if (data.novncPort)   setNovncPort(data.novncPort);
      if (data.executionId) executionIdRef.current = data.executionId;
      if (resp.ok && data.ok) {
        setRunningExec({ user_id: user?.id, user_name: user?.name, novnc_port: data.novncPort, execution_id: data.executionId });
      } else {
        setAlerta({ tipo: "erro", msg: data.erro || "Erro ao buscar comprovantes." });
      }
    } catch {
      setLogs(["[ERRO REDE] Falha ao conectar API."]);
    }
  };

  const buscarAutojurAgora = async () => {
    setLogs([]); setNovncPort(null);
    executionIdRef.current = null; logsOffsetRef.current = 0;
    setResultados([]); setAlerta(null); setEnvioResumo(null);
    const limite = parseInt(autojurLimiteTeste, 10);
    try {
      const resp = await api("/api/autojur/executar", {
        method: "POST",
        body: JSON.stringify(limite > 0 ? { limite } : {}),
      });
      const data = await resp.json();
      if (data.executionId) executionIdRef.current = data.executionId;
      if (resp.ok && data.ok) {
        setRunningExec({ user_id: user?.id, user_name: user?.name, execution_id: data.executionId });
      } else {
        setAlerta({ tipo: "erro", msg: data.erro || "Erro ao iniciar busca." });
      }
    } catch {
      setAlerta({ tipo: "erro", msg: "Falha ao conectar API." });
    }
  };

  const executarAutoTodos = async () => {
    try {
      await api("/api/prazos/executar", {
        method: "POST",
        body: JSON.stringify({ tribunal: tribunalSel || "" }),
      });
    } catch {}
  };

  const pushToast = (tipo, msg) => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, tipo, msg }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  };

  const handleLimparTjpr = async () => {
    if (!window.confirm("Apagar todos os PDFs, TXTs e o CSV do TJPR? Esta ação não pode ser desfeita.")) return;
    setLimpandoTjpr(true);
    try {
      const r = await api(`/limpar-tjpr/${robo.id}`, { method: "POST" });
      const d = await r.json();
      if (d?.ok) {
        pushToast("ok", `TJPR: ${d.deletados} arquivo(s) removido(s)`);
        api("/api/prazos/status").then(r2 => r2.json()).then(setAutoStatus).catch(() => {});
      } else {
        pushToast("erro", d?.erro || "Falha ao limpar TJPR");
      }
    } catch {
      pushToast("erro", "Erro de comunicação ao limpar TJPR");
    } finally {
      setLimpandoTjpr(false);
    }
  };

  const handleLimparTjsc = async () => {
    if (!window.confirm("Apagar todos os PDFs, TXTs e o CSV do TJSC? Esta ação não pode ser desfeita.")) return;
    setLimpandoTjsc(true);
    try {
      const r = await api(`/limpar-tjsc/${robo.id}`, { method: "POST" });
      const d = await r.json();
      if (d?.ok) {
        pushToast("ok", `TJSC: ${d.deletados} arquivo(s) removido(s)`);
        api("/api/prazos/status").then(r2 => r2.json()).then(setAutoStatus).catch(() => {});
      } else {
        pushToast("erro", d?.erro || "Falha ao limpar TJSC");
      }
    } catch {
      pushToast("erro", "Erro de comunicação ao limpar TJSC");
    } finally {
      setLimpandoTjsc(false);
    }
  };

  const salvarAutoConfig = async (campo) => {
    try {
      await api("/api/prazos/config", {
        method: "PUT",
        body: JSON.stringify({
          ativo:                campo.ativo  !== undefined ? campo.ativo  : autoAgendado,
          hora:                 campo.hora   !== undefined ? campo.hora   : autoHora,
          notification_user_ids: campo.notif !== undefined ? campo.notif  : autoNotifUsers.map(Number),
        }),
      });
    } catch {}
  };

  const buscarResultados = useCallback(async () => {
    const eid = executionIdRef.current;
    if (!eid) return;
    try {
      const res = await api(`/resultados/${robo.id}?executionId=${eid}`);
      if (res.ok) setResultados(await res.json());
    } catch {}
  }, [robo.id]);

  useEffect(() => {
    logsOffsetRef.current = 0;
    setLogs([]);
    setResultados([]);
    setAlerta(null);

    pollingRef.current = setInterval(async () => {
      try {
        const eid = executionIdRef.current || "";
        const [sRes, lRes] = await Promise.all([
          api(`/status/${robo.id}?executionId=${eid}`),
          api(`/log/${robo.id}?offset=${logsOffsetRef.current}&executionId=${eid}`),
        ]);
        const sd = await sRes.json();
        const ld = await lRes.json();

        const eraRodando = status.rodando;
        setStatus({ chrome: sd.chrome, rodando: sd.rodando });
        if (ld.linhas?.length > 0) {
          logsOffsetRef.current = ld.offset;
          setLogs(prev => [...prev, ...ld.linhas]);
        }
        if (executionIdRef.current) buscarResultados();
        if (eraRodando && !sd.rodando) {
          const finishedExecId = executionIdRef.current;
          setNovncPort(null);
          setRunningExec(null);
          executionIdRef.current = null;
          if (robo.temClassificador && finishedExecId) {
            api(`/envio-resumo/${robo.id}?execution_id=${finishedExecId}`)
              .then(r => r.json())
              .then(d => { if (d && !d.desabilitado) setEnvioResumo(d); })
              .catch(() => {});
          }
        }
      } catch {}
    }, POLL_MS);

    return () => clearInterval(pollingRef.current);
  }, [robo.id, buscarResultados, status.rodando]);

  useEffect(() => {
    if (terminalRef.current)
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
  }, [logs]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    parseXlsx(file, robo.colunas,
      (dados) => { setRegistros(dados); setAlerta({ tipo: "ok", msg: `${dados.length} registros carregados.` }); },
      (err)   => setAlerta({ tipo: "erro", msg: err }),
    );
    e.target.value = "";
  };

  const handleZipsUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";
    setUploadingZips(true);
    setAlertaZips(null);
    const groupId = zipsGroupRef.current;
    const results = [];
    for (const file of files) {
      try {
        const resp = await fetch(`${API_URL}/upload/zips?group=${groupId}`, {
          method: "POST",
          headers: {
            "Content-Type":  "application/octet-stream",
            "Authorization": `Bearer ${localStorage.getItem("rpa_token")}`,
            "X-Filename":    encodeURIComponent(file.name),
          },
          body: file,
        });
        const data = await resp.json();
        results.push({ nome: file.name, ok: resp.ok });
        if (resp.ok && data.group_dir) setZipsDir(data.group_dir);
      } catch {
        results.push({ nome: file.name, ok: false });
      }
    }
    setZipsFiles(prev => {
      const existentes = prev.map(f => f.nome);
      const novos = results.filter(r => !existentes.includes(r.nome));
      return [...prev, ...novos];
    });
    const erros = results.filter(r => !r.ok).length;
    setAlertaZips(erros === 0
      ? { tipo: "ok",  msg: `${results.length} arquivo(s) enviado(s).` }
      : { tipo: "erro", msg: `${erros} arquivo(s) falharam no envio.` }
    );
    setUploadingZips(false);
  };

  const handleZipUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    setUploadingZip(true);
    setAlertaZip(null);
    try {
      const resp = await fetch(`${API_URL}/upload/zip`, {
        method: "POST",
        headers: {
          "Content-Type": "application/octet-stream",
          "Authorization": `Bearer ${localStorage.getItem("rpa_token")}`,
        },
        body: file,
      });
      const data = await resp.json();
      if (resp.ok && data.path) {
        setEprocZipPath(data.path);
        setAlertaZip({ tipo: "ok", msg: `ZIP carregado: ${file.name}` });
      } else {
        setAlertaZip({ tipo: "erro", msg: data.erro || "Erro ao enviar ZIP." });
      }
    } catch {
      setAlertaZip({ tipo: "erro", msg: "Falha ao enviar ZIP." });
    } finally {
      setUploadingZip(false);
    }
  };

  const handleZipPeticaoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    setUploadingZipPeticao(true);
    setAlertaZipPeticao(null);
    try {
      const resp = await fetch(`${API_URL}/upload/zip-peticao`, {
        method: "POST",
        headers: {
          "Content-Type":  "application/octet-stream",
          "Authorization": `Bearer ${localStorage.getItem("rpa_token")}`,
        },
        body: file,
      });
      const data = await resp.json();
      if (resp.ok && data.path) {
        setPeticaoZipPath(data.path);
        setAlertaZipPeticao({ tipo: "ok", msg: `ZIP carregado: ${file.name}` });
      } else {
        setAlertaZipPeticao({ tipo: "erro", msg: data.erro || "Erro ao enviar ZIP." });
      }
    } catch {
      setAlertaZipPeticao({ tipo: "erro", msg: "Falha ao enviar ZIP." });
    } finally {
      setUploadingZipPeticao(false);
    }
  };

  const cancelarAgendamento = async () => {
    if (!agendamento) return;
    try {
      await api(`/api/agendamentos/${agendamento.id}`, { method: "DELETE" });
      setAgendamento(null);
    } catch {}
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    setUploadingCsv(true);
    setAlertaCsv(null);
    try {
      const resp = await fetch(`${API_URL}/upload/csv`, {
        method: "POST",
        headers: {
          "Content-Type":  "text/csv",
          "Authorization": `Bearer ${localStorage.getItem("rpa_token")}`,
        },
        body: file,
      });
      const data = await resp.json();
      if (resp.ok && data.path) {
        setCsvPath(data.path);
        setAlertaCsv({ tipo: "ok", msg: `CSV carregado: ${file.name}` });
      } else {
        setAlertaCsv({ tipo: "erro", msg: data.erro || "Erro ao enviar CSV." });
      }
    } catch {
      setAlertaCsv({ tipo: "erro", msg: "Falha ao enviar CSV." });
    } finally {
      setUploadingCsv(false);
    }
  };

  const handleAnalisePlanilhaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    setUploadingAnalisePlanilha(true);
    setAlertaAnalisePlanilha(null);
    try {
      const resp = await fetch(`${API_URL}/upload/analise-planilha`, {
        method: "POST",
        headers: {
          "Content-Type":  "application/octet-stream",
          "Authorization": `Bearer ${localStorage.getItem("rpa_token")}`,
        },
        body: file,
      });
      const data = await resp.json();
      if (resp.ok && data.path) {
        setAnalisePlanilhaPath(data.path);
        setAlertaAnalisePlanilha({ tipo: "ok", msg: `Planilha carregada: ${file.name}` });
      } else {
        setAlertaAnalisePlanilha({ tipo: "erro", msg: data.erro || "Erro ao enviar a planilha." });
      }
    } catch {
      setAlertaAnalisePlanilha({ tipo: "erro", msg: "Falha ao enviar a planilha." });
    } finally {
      setUploadingAnalisePlanilha(false);
    }
  };

  const handleAnaliseStorageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    setUploadingAnaliseStorage(true);
    setAlertaAnaliseStorage(null);
    try {
      const resp = await fetch(`${API_URL}/upload/analise-storage`, {
        method: "POST",
        headers: {
          "Content-Type":  "application/octet-stream",
          "Authorization": `Bearer ${localStorage.getItem("rpa_token")}`,
        },
        body: file,
      });
      const data = await resp.json();
      if (resp.ok && data.path) {
        setAnaliseStoragePath(data.path);
        setAlertaAnaliseStorage({ tipo: "ok", msg: `storage.zip carregado: ${file.name}` });
      } else {
        setAlertaAnaliseStorage({ tipo: "erro", msg: data.erro || "Erro ao enviar o storage.zip." });
      }
    } catch {
      setAlertaAnaliseStorage({ tipo: "erro", msg: "Falha ao enviar o storage.zip." });
    } finally {
      setUploadingAnaliseStorage(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";
    setUploadingPdf(true);
    setAlertaPdf(null);
    setPdfPlano(null);
    setPdfAguardaConfirmacao(false);
    const groupId = pdfGroupRef.current;
    const novos = [];
    for (const file of files) {
      try {
        const resp = await fetch(`${API_URL}/upload/pdfs?group=${groupId}`, {
          method: "POST",
          headers: {
            "Content-Type":  "application/octet-stream",
            "Authorization": `Bearer ${localStorage.getItem("rpa_token")}`,
            "X-Filename":    encodeURIComponent(file.name),
          },
          body: file,
        });
        const data = await resp.json();
        if (resp.ok && data.path) {
          novos.push({ nome: file.name, path: data.path, tamanho: file.size });
        }
      } catch {
        // arquivo individual falhou — segue com os demais
      }
    }
    setPdfArquivos(prev => {
      const existentes = prev.map(f => f.nome);
      const inclusos = novos.filter(n => !existentes.includes(n.nome));
      return [...prev, ...inclusos];
    });
    const erros = files.length - novos.length;
    setAlertaPdf(erros === 0
      ? { tipo: "ok", msg: `${novos.length} PDF(s) carregado(s).` }
      : { tipo: "erro", msg: `${novos.length} carregado(s), ${erros} com falha.` });
    setUploadingPdf(false);
  };

  const removerPdfArquivo = (nome) => {
    setPdfArquivos(prev => prev.filter(f => f.nome !== nome));
  };

  const abrirChecklist = async () => {
    setShowModalChecklist(true);
    setChecklistLoading(true);
    setChecklistAlerta(null);
    try {
      const resp = await api("/pdf-to-text/checklist");
      const data = await resp.json();
      if (resp.ok) setChecklistTexto(data.conteudo || "");
      else setChecklistAlerta({ tipo: "erro", msg: data.erro || "Erro ao carregar o checklist." });
    } catch {
      setChecklistAlerta({ tipo: "erro", msg: "Falha ao conectar API." });
    } finally {
      setChecklistLoading(false);
    }
  };

  const salvarChecklist = async () => {
    setChecklistSaving(true);
    setChecklistAlerta(null);
    try {
      const resp = await api("/pdf-to-text/checklist", {
        method: "POST",
        body:   JSON.stringify({ conteudo: checklistTexto }),
      });
      const data = await resp.json();
      if (resp.ok && data.ok) {
        setShowModalChecklist(false);
      } else {
        setChecklistAlerta({ tipo: "erro", msg: data.erro || "Erro ao salvar o checklist." });
      }
    } catch {
      setChecklistAlerta({ tipo: "erro", msg: "Falha ao conectar API." });
    } finally {
      setChecklistSaving(false);
    }
  };

  const criarAgendamento = async () => {
    if (!registros.length) { setAlertaAgendar({ tipo: "erro", msg: "Importe a planilha antes de agendar." }); return; }
    if (!credSel) { setAlertaAgendar({ tipo: "erro", msg: "Selecione uma credencial." }); return; }
    if (!horaAgendada) { setAlertaAgendar({ tipo: "erro", msg: "Selecione um horário." }); return; }
    setAgendandoLoading(true);
    setAlertaAgendar(null);
    try {
      const resp = await api("/api/agendamentos", {
        method: "POST",
        body: JSON.stringify({
          registros,
          credential_id: credSel,
          zips_dir: zipsDir || null,
          hora: horaAgendada,
          robot_id: robo.id,
        }),
      });
      const data = await resp.json();
      if (resp.ok && data.ok) {
        setAgendamento(data.agendamento);
        setShowModalAgendar(false);
        setAlertaAgendar(null);
      } else {
        setAlertaAgendar({ tipo: "erro", msg: data.erro || "Erro ao agendar." });
      }
    } catch {
      setAlertaAgendar({ tipo: "erro", msg: "Falha ao conectar API." });
    } finally {
      setAgendandoLoading(false);
    }
  };

  const executarRPA = async (useIA = true, pdfConfirmado = false) => {
    if (!robo.temZipEproc && !robo.temTribunais && !robo.temAnaliseProcessos && !robo.temPdfToText && !registros.length) return;
    if (!robo.temAnaliseProcessos && !robo.temPdfToText && !credSel) { setAlerta({ tipo: "erro", msg: "Selecione uma credencial antes de executar." }); return; }
    if (robo.temAnaliseProcessos && (!analisePlanilhaPath || !analiseStoragePath)) {
      setAlerta({ tipo: "erro", msg: "Envie a planilha e o storage.zip antes de executar." });
      return;
    }
    if (robo.temPdfToText && !pdfArquivos.length) {
      setAlertaPdf({ tipo: "erro", msg: "Envie ao menos um PDF antes de executar." });
      return;
    }
    if (robo.temZipEproc && !eprocZipPath) {
      setAlertaZip({ tipo: "erro", msg: "Selecione o ZIP resultado do EPROC antes de executar." });
      return;
    }
    if (robo.temZipsMultiplos && !zipsDir) {
      setAlertaZips({ tipo: "erro", msg: "Envie os arquivos ZIP antes de executar." });
      return;
    }
    if (robo.temZipPeticao && !peticaoZipPath) {
      setAlertaZipPeticao({ tipo: "erro", msg: "Envie o arquivo ZIP de petições antes de executar." });
      return;
    }
    const tjprOnly   = robo.temTribunais && tribunalSel.toLowerCase().startsWith("tjpr");
    const tjscOnly   = robo.temTribunais && tribunalSel.toLowerCase().startsWith("tjsc");
    const credTjscId = tjprOnly ? "" : credSel;
    const credTjprId = tjscOnly ? "" : credSelTjpr;
    const cred       = credentials.find(c => String(c.id) === String(credTjscId));
    const credTjpr   = credentials.find(c => String(c.id) === String(credTjprId));
    setLogs([]); setNovncPort(null);
    executionIdRef.current = null; logsOffsetRef.current = 0;
    setResultados([]); setAlerta(null);
    try {
      const resp = await api(`/executar/${robo.id}`, {
        method: "POST",
        body: JSON.stringify({
          registros,
          login: { usuario: cred?.login || "", senha: cred?.senha || "" },
          ...(robo.temZipEproc      && eprocZipPath  ? { eproc_zip_path: eprocZipPath }  : {}),
          ...(robo.temZipsMultiplos && zipsDir        ? { zips_dir: zipsDir }              : {}),
          ...(robo.temZipPeticao    && peticaoZipPath ? { zip_path: peticaoZipPath }       : {}),
          ...(robo.temZipPeticao    && useIA && resultadoIA?.output_path ? { xlsx_ia_path: resultadoIA.output_path } : {}),
          ...(robo.temTribunais ? {
            login_tjpr:         { usuario: credTjpr?.login || "", senha: credTjpr?.senha || "" },
            tribunal:           tribunalSel || "",
            csv_path:           csvPath || null,
            collect_prazos:     true,
            collect_intimacoes: true,
          } : {}),
          ...(robo.temAnaliseProcessos ? {
            planilha_path:    analisePlanilhaPath,
            storage_zip_path: analiseStoragePath,
            ambiente:         analiseAmbiente,
          } : {}),
          ...(robo.temPdfToText ? {
            pdf_paths:     pdfArquivos.map(f => f.path),
            pdf_modo:      pdfModo,
            pdf_analisar:  pdfAnalisar,
            pdf_confirmado: pdfConfirmado,
          } : {}),
        }),
      });
      const data = await resp.json();
      if (robo.temPdfToText && data.ok && data.precisaConfirmacao) {
        setPdfPlano(data.plano || null);
        setPdfAguardaConfirmacao(true);
        return;
      }
      setPdfAguardaConfirmacao(false);
      if (data.novncPort)   setNovncPort(data.novncPort);
      if (data.executionId) executionIdRef.current = data.executionId;
      if (resp.ok && data.ok) {
        setRunningExec({ user_id: user?.id, user_name: user?.name, novnc_port: data.novncPort, execution_id: data.executionId });
      }
      if (!resp.ok) setAlerta({ tipo: "erro", msg: data.erro || "Erro ao iniciar." });
    } catch {
      setLogs(["[ERRO REDE] Falha ao conectar API."]);
    }
  };

  // Fluxo novo — não precisa de planilha, consulta o tribunal de verdade.
  // Em homologação só classifica (não cria nada no SISJUR nem atualiza o
  // Autojur). Em produção (SISJUR_PEX_AMBIENTE, ver kitBaixaContagem) abre o
  // serviço de encerramento no SISJUR PEX e atualiza o Autojur de verdade —
  // por isso pede confirmação explícita (modal, não window.confirm) antes de
  // disparar nesse ambiente. Passa pela mesma esteira de /executar (aparece
  // em Monitoramento > Execuções).
  const _dispararDryRunFluxoCompleto = async () => {
    setLogs([]); setNovncPort(null);
    executionIdRef.current = null; logsOffsetRef.current = 0;
    setResultados([]); setAlerta(null);
    try {
      const resp = await api(`/executar/${robo.id}`, {
        method: "POST",
        body: JSON.stringify({
          dry_run_fluxo_completo:  true,
          codigos_tarefa:          codigosTarefaLista,
          consultar_tribunal:      consultarTribunal,
          fazer_lancamento_sisjur: fazerLancamentoSisjur,
          notification_emails:     notifEmailsExecucao.split(",").map(e => e.trim()).filter(e => e.includes("@")),
        }),
      });
      const data = await resp.json();
      if (data.novncPort)   setNovncPort(data.novncPort);
      if (data.executionId) executionIdRef.current = data.executionId;
      if (resp.ok && data.ok) {
        setRunningExec({ user_id: user?.id, user_name: user?.name, novnc_port: data.novncPort, execution_id: data.executionId });
      } else {
        setAlerta({ tipo: "erro", msg: data.erro || "Erro ao iniciar o dry-run." });
      }
    } catch {
      setLogs(["[ERRO REDE] Falha ao conectar API."]);
    }
  };

  const executarDryRunFluxoCompleto = () => {
    if (kitBaixaContagem?.sisjur_pex_ambiente === "producao") {
      setShowModalProducaoConfirm(true);
      return;
    }
    _dispararDryRunFluxoCompleto();
  };

  // Custas CELOS — fluxo completo (Autojur -> eproc TJSC/portal TJPR ->
  // GED/comentário/finalização -> preview de pagamento CELOS). Sempre
  // bloqueado em homologação (nenhuma guia real é criada) — não precisa de
  // confirmação de produção como o kit de baixa.
  const executarFluxoCompletoCelos = async () => {
    setLogs([]); setNovncPort(null);
    executionIdRef.current = null; logsOffsetRef.current = 0;
    setResultados([]); setAlerta(null);
    try {
      const resp = await api(`/api/custas-celos/executar`, { method: "POST" });
      const data = await resp.json();
      if (data.novncPort)   setNovncPort(data.novncPort);
      if (data.executionId) executionIdRef.current = data.executionId;
      if (resp.ok && data.ok) {
        setRunningExec({ user_id: user?.id, user_name: user?.name, novnc_port: data.novncPort, execution_id: data.executionId });
      } else {
        setAlerta({ tipo: "erro", msg: data.erro || "Erro ao iniciar o fluxo completo." });
      }
    } catch {
      setLogs(["[ERRO REDE] Falha ao conectar API."]);
    }
  };

  const cred_sel_obj = credentials.find(c => String(c.id) === String(credSel));

  // Robô de sessão única rodando por OUTRO usuário → UI de bloqueio
  const isOtherUserRunning = robo.singleSession && status.rodando
    && runningExec && runningExec.user_id !== user?.id;

  const handleAdminPararExecucao = async () => {
    if (!runningExec?.id) return;
    try {
      await api(`/execucoes/${runningExec.id}/parar`, { method: "POST" });
    } catch {}
    setRunningExec(null); setNovncPort(null); executionIdRef.current = null;
  };

  const fmtDuracao = (ini) => {
    if (!ini) return "—";
    const s = Math.round((Date.now() - new Date(ini).getTime()) / 1000);
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    return s < 60 ? `${sec}s` : `${m}m ${sec}s`;
  };

  return (
    <div className="h-screen bg-[#F5F6FA] flex flex-col overflow-hidden">
      {/* Toasts (canto inferior direito, auto-dismiss) */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 w-72">
        {toasts.map(t => (
          <div key={t.id} className={`px-3 py-2 rounded-lg shadow-lg flex items-center gap-1.5 text-xs font-medium
            ${t.tipo === "ok" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-red-50 text-red-600 border border-red-100"}`}>
            {t.tipo === "ok"
              ? <CheckCircle size={13} className="flex-shrink-0" />
              : <AlertCircle size={13} className="flex-shrink-0" />}
            {t.msg}
          </div>
        ))}
      </div>
      <NavHeader user={user} onLogout={onLogout} onUsers={onUsers} onCredentials={onCredentials}
        onExecucoes={onExecucoes} currentScreen="robots" onRobots={onVoltar} />
      {/* Sub-header do robô */}
      <div className="bg-white border-b border-gray-100 px-6 h-10 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onVoltar} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 transition-colors">
            <ArrowLeft size={13} /> Voltar
          </button>
          <div className="w-px h-4 bg-gray-200" />
          <span className="font-bold text-xs text-[#1B2B5B]">{robo.nome}</span>
          <span className="text-gray-400 text-xs">— {robo.subtitulo}</span>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill label="Chrome" active={status.chrome} />
          <StatusPill label="Worker" active={status.rodando} pulsing={status.rodando} />
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <aside className="w-72 bg-white border-r border-gray-100 flex flex-col gap-5 p-5 overflow-y-auto flex-shrink-0 shadow-sm">

          {/* Bloqueio: sessão única em uso por outro usuário */}
          {isOtherUserRunning && (
            <div className="flex flex-col gap-4 flex-1">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                <Loader size={18} className="animate-spin text-amber-500 mx-auto mb-2" />
                <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-1">Robô em uso</p>
                <p className="text-sm text-amber-800">
                  Execução iniciada por<br />
                  <span className="font-semibold">{runningExec.user_name}</span>
                </p>
                <p className="text-xs text-amber-600 mt-1.5">há {fmtDuracao(runningExec.iniciado_em)}</p>
              </div>
              {user?.role === "admin"
                ? <Btn variant="danger" size="lg" className="w-full justify-center" onClick={handleAdminPararExecucao}>
                    Parar Execução (Admin)
                  </Btn>
                : <p className="text-xs text-center text-gray-400 italic">Aguardando disponibilidade...</p>
              }
            </div>
          )}

          {/* Baixa de Processos — contagem "kit de baixa" no Autojur (novo fluxo) */}
          {!isOtherUserRunning && robo.id === "baixa-processos" && (
            <>
              <KitDeBaixaAutojurPainel
                data={kitBaixaContagem}
                atualizando={kitBaixaAtualizando}
                onAtualizar={() => fetchKitBaixaContagem(true)}
              />
              <section>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Base de Garantias</p>
                  <button type="button" onClick={handleDownloadBaseGarantias} disabled={baixandoBaseGarantias}
                    className="flex items-center gap-1 text-[10px] text-[#1B2B5B] hover:underline font-semibold disabled:opacity-50 disabled:no-underline">
                    <Download size={11} /> {baixandoBaseGarantias ? "Baixando..." : "Baixar base atual"}
                  </button>
                </div>
                <label className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-xl p-3 cursor-pointer transition-all ${
                  uploadingBaseGarantias ? "border-gray-200 bg-gray-50" : "border-gray-200 hover:border-[#1B2B5B] hover:bg-[#EEF1F8]"
                }`}>
                  <input type="file" className="hidden" accept=".xlsx,.xls"
                    onChange={handleBaseGarantiasUpload} disabled={uploadingBaseGarantias} />
                  {uploadingBaseGarantias
                    ? <Loader size={16} className="animate-spin text-gray-400" />
                    : <Upload size={16} className="text-gray-400" />}
                  <span className="text-[10px] text-gray-500 text-center">
                    {uploadingBaseGarantias ? "Enviando..." : <>Importar/atualizar <span className="text-[#1B2B5B] font-semibold">.xlsx</span></>}
                  </span>
                </label>
                {alertaBaseGarantias && (
                  <p className={`text-[10px] mt-1.5 ${alertaBaseGarantias.tipo === "ok" ? "text-emerald-600" : "text-red-500"}`}>
                    {alertaBaseGarantias.msg}
                  </p>
                )}
                {kitBaixaContagem?.base_garantias_arquivo && (
                  <p className="text-[10px] text-gray-300 mt-1">
                    usando: {kitBaixaContagem.base_garantias_arquivo}
                    {!kitBaixaContagem.base_garantias_importada && " (padrão de fábrica — importe a sua)"}
                  </p>
                )}
              </section>

              <section>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Códigos de Tarefa (opcional)</p>
                  {robo.modeloUrl && (
                    <a href={robo.modeloUrl} download
                      className="flex items-center gap-1 text-[10px] text-[#1B2B5B] hover:underline font-semibold">
                      <Download size={11} /> Baixar modelo
                    </a>
                  )}
                </div>
                <label className="flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-xl p-3 cursor-pointer transition-all border-gray-200 hover:border-[#1B2B5B] hover:bg-[#EEF1F8]">
                  <input type="file" className="hidden" accept=".xlsx,.xls" onChange={handleCodigosTarefaUpload} />
                  <Upload size={16} className="text-gray-400" />
                  <span className="text-[10px] text-gray-500 text-center">
                    Importar <span className="text-[#1B2B5B] font-semibold">.xlsx</span> com os códigos a processar
                  </span>
                </label>
                {alertaCodigosTarefa && (
                  <p className={`text-[10px] mt-1.5 ${alertaCodigosTarefa.tipo === "ok" ? "text-emerald-600" : "text-red-500"}`}>
                    {alertaCodigosTarefa.msg}
                  </p>
                )}
                <p className="text-[10px] text-gray-300 mt-1">
                  {codigosTarefaLista.length > 0
                    ? <>usando {codigosTarefaLista.length} código(s) de {codigosTarefaArquivo} — <button onClick={limparCodigosTarefa} className="underline hover:text-gray-500">limpar (voltar a processar todas)</button></>
                    : "nenhuma planilha importada — vai processar todas as tarefas 'kit de baixa' abertas"}
                </p>
              </section>

              <section>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Consultar no tribunal?</p>
                <div className="grid grid-cols-2 gap-2">
                  <label className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-semibold cursor-pointer transition-all
                    ${consultarTribunal ? "border-[#1B2B5B] bg-[#1B2B5B]/5 text-[#1B2B5B]" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                    <input type="radio" name="consultarTribunal" className="hidden" checked={consultarTribunal}
                      onChange={() => { setConsultarTribunal(true); salvarBaixaConfig({ consultar_tribunal: true }); }} disabled={status.rodando} />
                    Sim
                  </label>
                  <label className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-semibold cursor-pointer transition-all
                    ${!consultarTribunal ? "border-amber-500 bg-amber-50 text-amber-700" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                    <input type="radio" name="consultarTribunal" className="hidden" checked={!consultarTribunal}
                      onChange={() => { setConsultarTribunal(false); salvarBaixaConfig({ consultar_tribunal: false }); }} disabled={status.rodando} />
                    Não
                  </label>
                </div>
              </section>

              <section>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Fazer lançamento no SisJur?</p>
                <div className="grid grid-cols-2 gap-2">
                  <label className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-semibold cursor-pointer transition-all
                    ${fazerLancamentoSisjur ? "border-[#1B2B5B] bg-[#1B2B5B]/5 text-[#1B2B5B]" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                    <input type="radio" name="fazerLancamentoSisjur" className="hidden" checked={fazerLancamentoSisjur}
                      onChange={() => { setFazerLancamentoSisjur(true); salvarBaixaConfig({ fazer_lancamento_sisjur: true }); }} disabled={status.rodando} />
                    Sim
                  </label>
                  <label className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-semibold cursor-pointer transition-all
                    ${!fazerLancamentoSisjur ? "border-amber-500 bg-amber-50 text-amber-700" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                    <input type="radio" name="fazerLancamentoSisjur" className="hidden" checked={!fazerLancamentoSisjur}
                      onChange={() => { setFazerLancamentoSisjur(false); salvarBaixaConfig({ fazer_lancamento_sisjur: false }); }} disabled={status.rodando} />
                    Não
                  </label>
                </div>
                <p className="text-[10px] text-gray-400 mt-1.5">
                  "Não" nunca liga escrita real — só pode desligar, mesmo que o ambiente esteja em produção.
                </p>
              </section>

              <section>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
                  Notificar por e-mail só nesta execução (opcional, separe vários por vírgula)
                </p>
                <input
                  type="text"
                  value={notifEmailsExecucao}
                  onChange={e => setNotifEmailsExecucao(e.target.value)}
                  placeholder="fulano@escritorio.com.br, ciclano@escritorio.com.br"
                  className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-indigo-500/30 focus:border-indigo-500"
                />
                <p className="text-[10px] text-gray-300 mt-1">
                  Deixe em branco para usar a configuração padrão (seção de agendamento, mais abaixo).
                </p>
              </section>
            </>
          )}

          {/* Upload planilha — oculto quando o robô usa o ZIP do EPROC, Tribunais, Classificador, Análise de Processos ou Baixa de Processos (que não usa mais planilha como parâmetro) */}
          {!isOtherUserRunning && !robo.temZipEproc && !robo.temTribunais && !robo.temClassificador && !robo.temAnaliseProcessos && !robo.temPdfToText && !robo.temBaixaProcessos && !robo.temLoyCaptura && !robo.temCustasCelos && (
            <section>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Importar Planilha</p>
                {robo.modeloUrl && (
                  <a href={robo.modeloUrl} download
                    className="flex items-center gap-1 text-[10px] font-semibold text-[#1B2B5B] hover:underline">
                    <Download size={11} /> Baixar modelo
                  </a>
                )}
              </div>
              <div className="relative border-2 border-dashed border-gray-200 rounded-xl p-5 text-center hover:border-[#1B2B5B] transition-all bg-gray-50 group cursor-pointer">
                <input type="file" className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  accept=".xlsx,.xls" onChange={handleFileUpload} />
                <Upload className="mx-auto mb-1.5 text-gray-300 group-hover:text-[#1B2B5B]" size={18} />
                <p className="text-xs text-gray-400">Clique para importar <span className="text-[#1B2B5B] font-semibold">.xlsx</span></p>
              </div>
              {alerta && (
                <div className={`mt-2 p-2.5 rounded-lg flex items-start gap-1.5 text-xs
                  ${alerta.tipo === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                  {alerta.tipo === "ok" ? <CheckCircle size={13} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />}
                  {alerta.msg}
                </div>
              )}
            </section>
          )}

          {/* Análise de Processos — planilha (Cód./Pasta/Número CNJ) + storage.zip */}
          {!isOtherUserRunning && robo.temAnaliseProcessos && (
            <section className="flex flex-col gap-3">
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                  Planilha (Cód. / Pasta / Número CNJ)
                </p>
                <div className="relative border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-[#1B2B5B] transition-all bg-gray-50 group cursor-pointer">
                  <input type="file" className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    accept=".xlsx,.xls" onChange={handleAnalisePlanilhaUpload} disabled={uploadingAnalisePlanilha} />
                  {uploadingAnalisePlanilha
                    ? <Loader className="mx-auto mb-1.5 text-[#1B2B5B] animate-spin" size={18} />
                    : <Upload className="mx-auto mb-1.5 text-gray-300 group-hover:text-[#1B2B5B]" size={18} />}
                  <p className="text-xs text-gray-400">
                    {uploadingAnalisePlanilha ? "Enviando..." : <>Clique para importar <span className="text-[#1B2B5B] font-semibold">.xlsx</span></>}
                  </p>
                </div>
                {alertaAnalisePlanilha && (
                  <div className={`mt-2 p-2.5 rounded-lg flex items-start gap-1.5 text-xs
                    ${alertaAnalisePlanilha.tipo === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                    {alertaAnalisePlanilha.tipo === "ok" ? <CheckCircle size={13} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />}
                    {alertaAnalisePlanilha.msg}
                  </div>
                )}
              </div>

              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                  storage.zip (checklist + relatório por processo)
                </p>
                <div className="relative border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-[#1B2B5B] transition-all bg-gray-50 group cursor-pointer">
                  <input type="file" className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    accept=".zip" onChange={handleAnaliseStorageUpload} disabled={uploadingAnaliseStorage} />
                  {uploadingAnaliseStorage
                    ? <Loader className="mx-auto mb-1.5 text-[#1B2B5B] animate-spin" size={18} />
                    : <Upload className="mx-auto mb-1.5 text-gray-300 group-hover:text-[#1B2B5B]" size={18} />}
                  <p className="text-xs text-gray-400">
                    {uploadingAnaliseStorage ? "Enviando..." : <>Clique para importar <span className="text-[#1B2B5B] font-semibold">storage.zip</span></>}
                  </p>
                </div>
                {alertaAnaliseStorage && (
                  <div className={`mt-2 p-2.5 rounded-lg flex items-start gap-1.5 text-xs
                    ${alertaAnaliseStorage.tipo === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                    {alertaAnaliseStorage.tipo === "ok" ? <CheckCircle size={13} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />}
                    {alertaAnaliseStorage.msg}
                  </div>
                )}
              </div>

              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Ambiente</p>
                <div className="grid grid-cols-2 gap-2">
                  <label className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-semibold cursor-pointer transition-all
                    ${analiseAmbiente === "homologacao" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                    <input type="radio" name="analiseAmbiente" className="hidden" checked={analiseAmbiente === "homologacao"}
                      onChange={() => setAnaliseAmbiente("homologacao")} disabled={status.rodando} />
                    Homologação
                  </label>
                  <label className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-semibold cursor-pointer transition-all
                    ${analiseAmbiente === "producao" ? "border-red-500 bg-red-50 text-red-700" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                    <input type="radio" name="analiseAmbiente" className="hidden" checked={analiseAmbiente === "producao"}
                      onChange={() => setAnaliseAmbiente("producao")} disabled={status.rodando} />
                    Produção
                  </label>
                </div>
                <p className="text-[10px] text-gray-400 mt-1.5">
                  {analiseAmbiente === "producao"
                    ? "⚠️ Vai enviar os arquivos de verdade para o GED do Autojur."
                    : "Só loga o que seria enviado (arquivo, pasta, CNJ) — nada é enviado. Confira o log antes de trocar para Produção."}
                </p>
              </div>
            </section>
          )}

          {/* PDF to Text — upload de um ou mais PDFs (partes do mesmo processo) */}
          {!isOtherUserRunning && robo.temPdfToText && (
            <section className="flex flex-col gap-3">
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                  PDF do processo (uma ou mais partes)
                </p>
                <div className="relative border-2 border-dashed border-gray-200 rounded-xl p-5 text-center hover:border-[#1B2B5B] transition-all bg-gray-50 group cursor-pointer">
                  <input type="file" multiple className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    accept=".pdf" onChange={handlePdfUpload} disabled={uploadingPdf || status.rodando} />
                  {uploadingPdf
                    ? <Loader className="mx-auto mb-1.5 text-[#1B2B5B] animate-spin" size={18} />
                    : <Upload className="mx-auto mb-1.5 text-gray-300 group-hover:text-[#1B2B5B]" size={18} />}
                  <p className="text-xs text-gray-400">
                    {uploadingPdf ? "Enviando..." : <>Clique para enviar <span className="text-[#1B2B5B] font-semibold">PDF(s)</span></>}
                  </p>
                </div>
                {pdfArquivos.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-28 overflow-y-auto">
                    {pdfArquivos.map((f, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700">
                        <CheckCircle size={11} className="flex-shrink-0" />
                        <span className="truncate flex-1">{f.nome}</span>
                        {!status.rodando && (
                          <button onClick={() => removerPdfArquivo(f.nome)} className="text-emerald-700 hover:text-red-600 flex-shrink-0">
                            <X size={11} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {alertaPdf && (
                  <div className={`mt-2 p-2.5 rounded-lg flex items-start gap-1.5 text-xs
                    ${alertaPdf.tipo === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                    {alertaPdf.tipo === "ok" ? <CheckCircle size={13} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />}
                    {alertaPdf.msg}
                  </div>
                )}
              </div>

              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Formato de saída</p>
                <div className="grid grid-cols-2 gap-2">
                  <label className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-semibold cursor-pointer transition-all
                    ${pdfModo === "completo" ? "border-[#1B2B5B] bg-[#1B2B5B]/5 text-[#1B2B5B]" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                    <input type="radio" name="pdfModo" className="hidden" checked={pdfModo === "completo"}
                      onChange={() => setPdfModo("completo")} disabled={status.rodando} />
                    Único (completo)
                  </label>
                  <label className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-semibold cursor-pointer transition-all
                    ${pdfModo === "dividido" ? "border-[#1B2B5B] bg-[#1B2B5B]/5 text-[#1B2B5B]" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                    <input type="radio" name="pdfModo" className="hidden" checked={pdfModo === "dividido"}
                      onChange={() => setPdfModo("dividido")} disabled={status.rodando} />
                    Por evento (.zip)
                  </label>
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                <input type="checkbox" checked={pdfAnalisar} onChange={(e) => setPdfAnalisar(e.target.checked)} disabled={status.rodando} />
                Rodar análise por checklist depois de converter
              </label>
              <button onClick={abrirChecklist}
                className="text-xs text-[#1B2B5B] font-semibold hover:underline self-start flex items-center gap-1">
                <Pencil size={11} /> Editar checklist da análise
              </button>

              {pdfAguardaConfirmacao && pdfPlano && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 space-y-1">
                  <p className="font-bold flex items-center gap-1.5"><AlertCircle size={13} /> Confirmação necessária antes de converter</p>
                  <p>Processo: {pdfPlano.numeroProcesso} — {pdfPlano.totalPaginas} página(s), {pdfPlano.paginasOcr} precisam de OCR ({pdfPlano.provider}).</p>
                  <p>Custo estimado: US$ {Number(pdfPlano.custoEstimadoUsd || 0).toFixed(4)}</p>
                  {pdfPlano.sobSigilo && <p className="font-bold">⚠️ Processo sob SEGREDO DE JUSTIÇA — o conteúdo das páginas de OCR será enviado a uma API externa.</p>}
                  <Btn size="sm" onClick={() => executarRPA(true, true)}>Confirmar e converter</Btn>
                </div>
              )}
            </section>
          )}

          {/* ZIPs múltiplos — SisJur Inserção */}
          {!isOtherUserRunning && robo.temZipsMultiplos && (
            <section>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Arquivos ZIP</p>
              <div className="relative border-2 border-dashed border-gray-200 rounded-xl p-5 text-center hover:border-[#1B2B5B] transition-all bg-gray-50 group cursor-pointer">
                <input type="file" multiple className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  accept=".zip" onChange={handleZipsUpload} disabled={uploadingZips} />
                {uploadingZips
                  ? <Loader className="mx-auto mb-1.5 text-[#1B2B5B] animate-spin" size={18} />
                  : <Upload className="mx-auto mb-1.5 text-gray-300 group-hover:text-[#1B2B5B]" size={18} />}
                <p className="text-xs text-gray-400">
                  {uploadingZips ? "Enviando..." : <>Clique para enviar <span className="text-[#1B2B5B] font-semibold">ZIPs</span> (múltiplos)</>}
                </p>
              </div>
              {zipsFiles.length > 0 && (
                <div className="mt-2 space-y-1 max-h-28 overflow-y-auto">
                  {zipsFiles.map((f, i) => (
                    <div key={i} className={`flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-lg
                      ${f.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                      {f.ok ? <CheckCircle size={11} className="flex-shrink-0" /> : <AlertCircle size={11} className="flex-shrink-0" />}
                      <span className="truncate">{f.nome}</span>
                    </div>
                  ))}
                </div>
              )}
              {alertaZips && (
                <div className={`mt-2 p-2.5 rounded-lg flex items-start gap-1.5 text-xs
                  ${alertaZips.tipo === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                  {alertaZips.tipo === "ok" ? <CheckCircle size={13} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />}
                  {alertaZips.msg}
                </div>
              )}
              {registros.length > 0 && zipsFiles.filter(f => f.ok).length > 0 &&
                registros.length !== zipsFiles.filter(f => f.ok).length && (
                <div className="mt-2 p-2.5 rounded-lg flex items-start gap-1.5 text-xs bg-amber-50 text-amber-700">
                  <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
                  O número de ZIPs enviados ({zipsFiles.filter(f => f.ok).length}) é diferente do número de
                  linhas da planilha ({registros.length}) — o número de ZIPs precisa ser igual ao de linhas
                  carregadas na planilha, senão a execução não vai encontrar o arquivo certo pra cada tarefa.
                </div>
              )}
            </section>
          )}

          {/* ZIP do EPROC */}
          {!isOtherUserRunning && robo.temZipEproc && (
            <section>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">ZIP Resultado EPROC</p>
              <div className="relative border-2 border-dashed border-gray-200 rounded-xl p-5 text-center hover:border-[#1B2B5B] transition-all bg-gray-50 group cursor-pointer">
                <input type="file" className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  accept=".zip" onChange={handleZipUpload} disabled={uploadingZip} />
                {uploadingZip
                  ? <Loader className="mx-auto mb-1.5 text-[#1B2B5B] animate-spin" size={18} />
                  : <Upload className="mx-auto mb-1.5 text-gray-300 group-hover:text-[#1B2B5B]" size={18} />}
                <p className="text-xs text-gray-400">
                  {uploadingZip ? "Enviando..." : <>Clique para importar <span className="text-[#1B2B5B] font-semibold">resultado_final.zip</span></>}
                </p>
              </div>
              {alertaZip && (
                <div className={`mt-2 p-2.5 rounded-lg flex items-start gap-1.5 text-xs
                  ${alertaZip.tipo === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                  {alertaZip.tipo === "ok" ? <CheckCircle size={13} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />}
                  {alertaZip.msg}
                </div>
              )}
            </section>
          )}

          {/* ZIP único de petições — SisJurPex Petição */}
          {!isOtherUserRunning && robo.temZipPeticao && (
            <section className="flex flex-col gap-2">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Arquivo ZIP de Petições</p>
              <div className="relative border-2 border-dashed border-gray-200 rounded-xl p-5 text-center hover:border-[#1B2B5B] transition-all bg-gray-50 group cursor-pointer">
                <input type="file" className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  accept=".zip" onChange={handleZipPeticaoUpload} disabled={uploadingZipPeticao} />
                {uploadingZipPeticao
                  ? <Loader className="mx-auto mb-1.5 text-[#1B2B5B] animate-spin" size={18} />
                  : <Upload className="mx-auto mb-1.5 text-gray-300 group-hover:text-[#1B2B5B]" size={18} />}
                <p className="text-xs text-gray-400">
                  {uploadingZipPeticao ? "Enviando..." : <>Clique para importar <span className="text-[#1B2B5B] font-semibold">petições.zip</span></>}
                </p>
              </div>
              {alertaZipPeticao && (
                <div className={`p-2.5 rounded-lg flex items-start gap-1.5 text-xs
                  ${alertaZipPeticao.tipo === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                  {alertaZipPeticao.tipo === "ok"
                    ? <CheckCircle size={13} className="mt-0.5 flex-shrink-0" />
                    : <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />}
                  {alertaZipPeticao.msg}
                </div>
              )}

              {/* Botão de enriquecimento IA */}
              {peticaoZipPath && registros.length > 0 && (
                <div className="border border-amber-200 bg-amber-50 rounded-xl p-3 flex flex-col gap-2">
                  <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wide">
                    🤖 Preencher DECISÃO com IA
                  </p>
                  <p className="text-[10px] text-amber-600">
                    Lê os PDFs do ZIP e detecta automaticamente a DECISÃO para linhas com campo vazio.
                    Gera nova planilha com colunas <strong>DECISAO_IA</strong> e <strong>REFERENCIA_IA</strong>.
                  </p>
                  <button
                    onClick={async () => {
                      setEnriquecendoIA(true);
                      setResultadoIA(null);
                      const payload = {
                        registros,
                        zip_path: peticaoZipPath,
                        tipo:     "civil_consumidor",
                      };
                      console.log("[IA] Iniciando enriquecimento:", payload);
                      try {
                        const r = await api("/api/peticao/enriquecer", {
                          method: "POST",
                          body: JSON.stringify(payload),
                        });
                        console.log("[IA] HTTP status:", r.status);
                        const d = await r.json();
                        console.log("[IA] Resposta:", d);
                        if (r.ok && d.ok) {
                          setResultadoIA(d);
                        } else {
                          setResultadoIA({ erro: d.erro || "Erro desconhecido" });
                        }
                      } catch (e) {
                        console.error("[IA] Erro fetch:", e);
                        setResultadoIA({ erro: e.message });
                      } finally {
                        setEnriquecendoIA(false);
                      }
                    }}
                    disabled={enriquecendoIA}
                    className="flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    {enriquecendoIA ? <Loader size={12} className="animate-spin" /> : null}
                    {enriquecendoIA ? "Analisando PDFs com IA..." : "Detectar DECISÃO via IA"}
                  </button>

                  {/* Resultado do enriquecimento */}
                  {resultadoIA && !resultadoIA.erro && (
                    <div className="bg-white border border-emerald-200 rounded-lg p-2.5 text-[10px] text-gray-600 flex flex-col gap-1.5">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-emerald-700">✅ Execução usará planilha com DECISÃO IA</p>
                        <button
                          onClick={() => setResultadoIA(null)}
                          title="Descartar — executar sem IA"
                          className="text-gray-400 hover:text-red-500 transition-colors ml-2 flex-shrink-0">
                          ✕
                        </button>
                      </div>
                      <p>
                        Total: {resultadoIA.stats?.total} |{" "}
                        IA detectou: <strong className="text-emerald-600">{resultadoIA.stats?.enriquecidos}</strong> |{" "}
                        Já preenchido: {resultadoIA.stats?.ja_preenchido} |{" "}
                        Sem PDF: {resultadoIA.stats?.sem_pdf} |{" "}
                        Erros: {resultadoIA.stats?.erros}
                      </p>
                      <a
                        href={`${window.location.protocol}//${window.location.hostname}:${BACKEND_PORT}/download/${encodeURIComponent(resultadoIA.output_path?.split("/").pop() || "")}?token=${localStorage.getItem("rpa_token")}`}
                        download
                        className="flex items-center gap-1 text-[#1B2B5B] font-semibold hover:underline">
                        <Download size={10} /> Baixar planilha com DECISAO_IA
                      </a>
                      <p className="text-gray-400 italic">Clique no ✕ para executar sem IA.</p>
                    </div>
                  )}
                  {resultadoIA?.erro && (
                    <div className="flex items-start gap-1.5">
                      <p className="text-[10px] text-red-600 flex-1">❌ {resultadoIA.erro}</p>
                      <button onClick={() => setResultadoIA(null)} className="text-gray-400 hover:text-red-500 text-[10px]">✕</button>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}


          {/* Consulta Tribunais — execução automática de todos os 4 */}
          {!isOtherUserRunning && robo.automatico && (
            <section className="flex flex-col gap-3 border-t border-gray-100 pt-4">
              <div className="flex items-center gap-2">
                <Clock size={12} className="text-emerald-600" />
                <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest">Execução Automática</p>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={autoAgendado}
                    onChange={e => { setAutoAgendado(e.target.checked); salvarAutoConfig({ ativo: e.target.checked }); }}
                    disabled={autoStatus?.rodando}
                    className="w-3.5 h-3.5 accent-emerald-600" />
                  <span className="text-xs text-gray-700">Agendar diariamente às</span>
                </label>
                <input
                  type="time"
                  value={autoHora}
                  onChange={e => { setAutoHora(e.target.value); salvarAutoConfig({ hora: e.target.value }); }}
                  disabled={autoStatus?.rodando}
                  className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500 disabled:opacity-50"
                />
                <span className="text-xs text-gray-400">(Brasília)</span>
              </div>

              {/* Notificações por e-mail */}
              {allUsers.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">
                    Notificar por e-mail ao concluir
                  </p>
                  <div className="flex flex-col gap-1">
                    {allUsers.map(u => {
                      const checked = autoNotifUsers.includes(String(u.id));
                      return (
                        <label key={u.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const next = checked
                                ? autoNotifUsers.filter(id => id !== String(u.id))
                                : [...autoNotifUsers, String(u.id)];
                              setAutoNotifUsers(next);
                              salvarAutoConfig({ notif: next.map(Number) });
                            }}
                            className="w-3 h-3 accent-[#1B2B5B]"
                          />
                          <span className="text-xs text-gray-700">{u.name}</span>
                          <span className="text-[10px] text-gray-400">{u.email}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Executar</p>
                <select value={tribunalSel} onChange={e => setTribunalSel(e.target.value)}
                  disabled={autoStatus?.rodando}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500 disabled:opacity-50">
                  <option value="">Todos (TJSC e TJPR)</option>
                  <option value="TJSC - Eproc 1">TJSC — Eproc 1º Grau</option>
                  <option value="TJSC - Eproc 2">TJSC — Eproc 2º Grau</option>
                  <option value="TJPR - 1o Grau">TJPR — Projudi</option>
                </select>
              </div>

              <button onClick={executarAutoTodos} disabled={autoStatus?.rodando}
                className="flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {autoStatus?.rodando ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                {autoStatus?.rodando
                  ? "Coletando..."
                  : tribunalSel
                    ? `Executar ${{
                        "TJSC - Eproc 1": "TJSC Eproc 1º Grau",
                        "TJSC - Eproc 2": "TJSC Eproc 2º Grau",
                        "TJPR - 1o Grau": "TJPR Projudi",
                      }[tribunalSel] || tribunalSel} Agora`
                    : "Executar Todos Agora"}
              </button>

              {autoStatus?.ultimo_run && (
                <p className="text-[10px] text-gray-400 text-center">
                  Última coleta: {new Date(autoStatus.ultimo_run).toLocaleString("pt-BR")}
                  {" — "}{autoStatus.ultimo_status === "ok" ? "✅ OK" : autoStatus.ultimo_status === "erro" ? "❌ Erro" : ""}
                </p>
              )}
            </section>
          )}

          {/* Consulta Tribunais — download dos resultados */}
          {robo.temTribunais && (autoStatus?.arquivos?.length > 0 || autoStatus?.pdfs_tjpr > 0 || autoStatus?.pdfs_tjsc > 0) && (
            <section className="flex flex-col gap-2 border-t border-gray-100 pt-4">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Resultados disponíveis</p>

              {/* PDFs prontos para o GED */}
              {(autoStatus?.pdfs_tjpr > 0 || autoStatus?.pdfs_tjsc > 0) && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
                  <span className="text-xs text-blue-700 font-medium">PDFs para GED:</span>
                  {autoStatus?.pdfs_tjpr > 0 && (
                    <span className="text-[10px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
                      TJPR: {autoStatus.pdfs_tjpr}
                    </span>
                  )}
                  {autoStatus?.pdfs_tjsc > 0 && (
                    <span className="text-[10px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
                      TJSC: {autoStatus.pdfs_tjsc}
                    </span>
                  )}
                  <div className="ml-auto flex items-center gap-1">
                    {autoStatus?.pdfs_tjpr > 0 && (
                      <button
                        onClick={handleLimparTjpr}
                        disabled={limpandoTjpr}
                        className="flex items-center gap-1 text-[10px] text-red-500 hover:text-red-700 hover:bg-red-50 rounded px-2 py-0.5 transition-colors disabled:opacity-40"
                        title="Apagar PDFs, TXTs e CSV do TJPR">
                        {limpandoTjpr ? <Loader size={10} className="animate-spin" /> : <Trash2 size={10} />}
                        Limpar TJPR
                      </button>
                    )}
                    {autoStatus?.pdfs_tjsc > 0 && (
                      <button
                        onClick={handleLimparTjsc}
                        disabled={limpandoTjsc}
                        className="flex items-center gap-1 text-[10px] text-red-500 hover:text-red-700 hover:bg-red-50 rounded px-2 py-0.5 transition-colors disabled:opacity-40"
                        title="Apagar PDFs, TXTs e CSV do TJSC">
                        {limpandoTjsc ? <Loader size={10} className="animate-spin" /> : <Trash2 size={10} />}
                        Limpar TJSC
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* CSVs de prazos/intimações */}
              {autoStatus.arquivos?.map(f => (
                <a
                  key={f.nome}
                  href={`${API_URL}/download/${f.nome}?token=${localStorage.getItem("rpa_token")}`}
                  download={f.nome}
                  className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gray-50 hover:bg-[#1B2B5B]/5 border border-gray-200 transition-colors group">
                  <div className="flex items-center gap-2 min-w-0">
                    <Download size={12} className="text-[#1B2B5B] flex-shrink-0" />
                    <span className="text-xs text-gray-700 truncate">{f.nome}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {f.tamanho != null && (
                      <span className="text-[10px] text-gray-400">{(f.tamanho / 1024).toFixed(1)} KB</span>
                    )}
                    {f.data && (
                      <span className="text-[10px] text-gray-400">{new Date(f.data).toLocaleDateString("pt-BR")}</span>
                    )}
                  </div>
                </a>
              ))}
            </section>
          )}

          {/* Classificador — busca automática de intimações via API AutoJur */}
          {!isOtherUserRunning && robo.temClassificador && (
            <section className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Clock size={12} className="text-emerald-600" />
                <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest">Busca Automática — API AutoJur</p>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={autojurAgendado}
                    onChange={e => { setAutojurAgendado(e.target.checked); salvarAutojurConfig({ ativo: e.target.checked }); }}
                    disabled={autojurStatus?.rodando}
                    className="w-3.5 h-3.5 accent-emerald-600" />
                  <span className="text-xs text-gray-700">Agendar diariamente às</span>
                </label>
                <input
                  type="time"
                  value={autojurHora}
                  onChange={e => { setAutojurHora(e.target.value); salvarAutojurConfig({ hora: e.target.value }); }}
                  disabled={autojurStatus?.rodando}
                  className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500 disabled:opacity-50"
                />
                <span className="text-xs text-gray-400">e</span>
                <input
                  type="time"
                  value={autojurHora2}
                  onChange={e => { setAutojurHora2(e.target.value); salvarAutojurConfig({ hora2: e.target.value }); }}
                  disabled={autojurStatus?.rodando}
                  className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500 disabled:opacity-50"
                />
                <span className="text-xs text-gray-400">(Brasília)</span>
              </div>

              {/* Notificações por e-mail (livre, não precisa ser usuário cadastrado) */}
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">
                  Notificar por e-mail ao concluir (separe vários por vírgula)
                </p>
                <input
                  type="text"
                  value={autojurEmailsTexto}
                  onChange={e => setAutojurEmailsTexto(e.target.value)}
                  onBlur={e => salvarAutojurConfig({ emailsTexto: e.target.value })}
                  placeholder="fulano@escritorio.com.br, ciclano@escritorio.com.br"
                  className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  value={autojurLimiteTeste}
                  onChange={e => setAutojurLimiteTeste(e.target.value)}
                  disabled={autojurStatus?.rodando || status.rodando}
                  placeholder="sem limite"
                  title="Limite de intimações a processar (só para teste — deixe vazio pra buscar todas)"
                  className="w-24 border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500 disabled:opacity-50"
                />
                <button onClick={buscarAutojurAgora} disabled={autojurStatus?.rodando || status.rodando}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  {(autojurStatus?.rodando || status.rodando) ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  {(autojurStatus?.rodando || status.rodando) ? "Buscando..." : "Buscar intimações agora"}
                </button>
              </div>
              <p className="text-[10px] text-gray-400 -mt-1.5">
                Limite acima é só para teste — as intimações fora do limite continuam pendentes e voltam na próxima busca.
              </p>

              {autojurStatus?.ultimo_run && (
                <p className="text-[10px] text-gray-400 text-center">
                  Última busca: {new Date(autojurStatus.ultimo_run).toLocaleString("pt-BR")}
                  {" — "}{autojurStatus.ultimo_status === "ok" ? "✅ OK" : autojurStatus.ultimo_status === "erro" ? "❌ Erro" : ""}
                  {autojurStatus.ultimo_novas != null && ` · ${autojurStatus.ultimo_novas} nova(s)`}
                  {autojurStatus.ultimo_existentes != null && ` · ${autojurStatus.ultimo_existentes} já existente(s)`}
                </p>
              )}

              {alerta && (
                <div className={`p-2.5 rounded-lg flex items-start gap-1.5 text-xs
                  ${alerta.tipo === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                  {alerta.tipo === "ok"
                    ? <CheckCircle size={13} className="mt-0.5 flex-shrink-0" />
                    : <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />}
                  {alerta.msg}
                </div>
              )}
            </section>
          )}

          {/* SisJur-insercao — busca automática de tarefas PEX via API Autojur.
              Só busca + monta planilha/ZIPs/relatório HTML — a inserção real no
              SisJur continua exigindo o fluxo manual de upload logo abaixo. */}
          {!isOtherUserRunning && robo.temBuscaAutojurSisjur && (
            <section className="flex flex-col gap-3 pb-4 mb-1 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Clock size={12} className="text-indigo-600" />
                <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-widest">Busca Automática — Autojur → SisJur PEX</p>
              </div>
              <p className="text-[10px] text-gray-400 -mt-1.5">
                Busca tarefas PEX (Especulador/Societário, Juízo-UF ≠ RJ, lote vazio, com anexo) direto no Autojur e
                monta a planilha + ZIPs + relatório HTML de validação abaixo. Não insere nada no SisJur nem altera o
                Autojur sozinha — revise o relatório e rode pelo fluxo manual de upload se aprovar.
              </p>

              <div className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={autojurSisjurAgendado}
                    onChange={e => { setAutojurSisjurAgendado(e.target.checked); salvarAutojurSisjurConfig({ ativo: e.target.checked }); }}
                    disabled={autojurSisjurStatus?.rodando}
                    className="w-3.5 h-3.5 accent-indigo-600" />
                  <span className="text-xs text-gray-700">Agendar diariamente às</span>
                </label>
                <input
                  type="time"
                  value={autojurSisjurHora}
                  onChange={e => { setAutojurSisjurHora(e.target.value); salvarAutojurSisjurConfig({ hora: e.target.value }); }}
                  disabled={autojurSisjurStatus?.rodando}
                  className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-indigo-500/30 focus:border-indigo-500 disabled:opacity-50"
                />
                <span className="text-xs text-gray-400">(Brasília)</span>
              </div>

              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">
                  Notificar por e-mail ao concluir (separe vários por vírgula)
                </p>
                <input
                  type="text"
                  value={autojurSisjurEmailsTexto}
                  onChange={e => setAutojurSisjurEmailsTexto(e.target.value)}
                  onBlur={e => salvarAutojurSisjurConfig({ emailsTexto: e.target.value })}
                  placeholder="fulano@escritorio.com.br, ciclano@escritorio.com.br"
                  className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-indigo-500/30 focus:border-indigo-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  value={autojurSisjurLimite}
                  onChange={e => setAutojurSisjurLimite(e.target.value)}
                  disabled={autojurSisjurStatus?.rodando || autojurSisjurRodando}
                  placeholder="todas"
                  title="Quantas tarefas prontas buscar (deixe em branco para trazer todas as que estiverem prontas)"
                  className="w-20 border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-indigo-500/30 focus:border-indigo-500 disabled:opacity-50"
                />
                <button onClick={buscarAutojurSisjurAgora} disabled={autojurSisjurStatus?.rodando || autojurSisjurRodando}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  {(autojurSisjurStatus?.rodando || autojurSisjurRodando) ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  {(autojurSisjurStatus?.rodando || autojurSisjurRodando) ? "Buscando..." : "Buscar tarefas agora"}
                </button>
              </div>
              <p className="text-[10px] text-gray-400 -mt-1">
                Quantas tarefas prontas trazer — deixe em branco para buscar todas.
              </p>

              {autojurSisjurStatus?.ultimo_run && (
                <p className="text-[10px] text-gray-400 text-center">
                  Última busca: {new Date(autojurSisjurStatus.ultimo_run).toLocaleString("pt-BR")}
                  {" — "}{autojurSisjurStatus.ultimo_status === "ok" ? "✅ OK" : autojurSisjurStatus.ultimo_status === "com_erros" ? "⚠️ Com erros" : ""}
                  {autojurSisjurStatus.ultimo_total_tarefas != null && ` · ${autojurSisjurStatus.ultimo_total_tarefas} pronta(s) na última execução`}
                </p>
              )}

              {autojurSisjurAlerta && (
                <div className={`p-2.5 rounded-lg flex items-start gap-1.5 text-xs
                  ${autojurSisjurAlerta.tipo === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                  {autojurSisjurAlerta.tipo === "ok"
                    ? <CheckCircle size={13} className="mt-0.5 flex-shrink-0" />
                    : <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />}
                  {autojurSisjurAlerta.msg}
                </div>
              )}

              {autojurSisjurResultados.length > 0 && (
                <div className="flex flex-col gap-2">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Resultado desta busca</p>
                  <div className="flex flex-col gap-1.5">
                    {autojurSisjurResultados.map((f, i) => {
                      const nome = f.nome || f;
                      const handleDownload = async () => {
                        try {
                          const r = await api(`/execucoes/${autojurSisjurExecId.dbId}/arquivo/${encodeURIComponent(nome)}`);
                          if (!r.ok) return;
                          const blob = await r.blob();
                          const url  = URL.createObjectURL(blob);
                          const a    = document.createElement("a");
                          a.href = url; a.download = nome; a.click();
                          URL.revokeObjectURL(url);
                        } catch {}
                      };
                      return (
                        <button key={i} onClick={handleDownload}
                          className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 hover:underline text-left">
                          <Download size={14} /> {nome}
                        </button>
                      );
                    })}
                  </div>
                  <button onClick={rodarComResultadosBusca}
                    disabled={!credSel || autojurSisjurRodandoDireto}
                    title={!credSel ? "Selecione uma credencial mais abaixo antes" : ""}
                    className="flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    {autojurSisjurRodandoDireto ? <Loader size={12} className="animate-spin" /> : <Bot size={12} />}
                    {autojurSisjurRodandoDireto ? "Disparando..." : "Rodar com estes resultados"}
                  </button>
                  {!credSel && (
                    <p className="text-[10px] text-amber-600">
                      Selecione uma credencial na seção "Credencial" mais abaixo antes de rodar.
                    </p>
                  )}
                </div>
              )}
            </section>
          )}

          {!isOtherUserRunning && robo.temLoyCaptura && (
            <section className="flex flex-col gap-3 pb-4 mb-1 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Clock size={12} className="text-indigo-600" />
                <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-widest">Execução Automática — Autojur → Loy Legal</p>
              </div>
              <p className="text-[10px] text-gray-400 -mt-1.5">
                Busca tarefas novas de VERIFICAR PROVIDÊNCIA e FAZER ANÁLISE RECURSAL criadas hoje no Autojur,
                pega o CNJ do processo e envia pra Loy Legal protocolar. Nunca reenvia um CNJ já confirmado antes.
              </p>

              <div className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={loyAgendado}
                    onChange={e => { setLoyAgendado(e.target.checked); salvarLoyConfig({ ativo: e.target.checked }); }}
                    disabled={loyStatus?.rodando}
                    className="w-3.5 h-3.5 accent-indigo-600" />
                  <span className="text-xs text-gray-700">Agendar diariamente às</span>
                </label>
                <input
                  type="time"
                  value={loyHora}
                  onChange={e => { setLoyHora(e.target.value); salvarLoyConfig({ hora: e.target.value }); }}
                  disabled={loyStatus?.rodando}
                  className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-indigo-500/30 focus:border-indigo-500 disabled:opacity-50"
                />
                <span className="text-xs text-gray-400">e às</span>
                <input
                  type="time"
                  value={loyHora2}
                  onChange={e => { setLoyHora2(e.target.value); salvarLoyConfig({ hora2: e.target.value }); }}
                  disabled={loyStatus?.rodando}
                  className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-indigo-500/30 focus:border-indigo-500 disabled:opacity-50"
                />
                <span className="text-xs text-gray-400">(Brasília)</span>
              </div>

              {allUsers.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">
                    Notificar por e-mail ao concluir
                  </p>
                  <div className="flex flex-col gap-1">
                    {allUsers.map(u => {
                      const checked = loyNotifUsers.includes(String(u.id));
                      return (
                        <label key={u.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const next = checked
                                ? loyNotifUsers.filter(id => id !== String(u.id))
                                : [...loyNotifUsers, String(u.id)];
                              setLoyNotifUsers(next);
                              salvarLoyConfig({ notif: next.map(Number) });
                            }}
                            className="w-3 h-3 accent-indigo-600"
                          />
                          <span className="text-xs text-gray-700">{u.name}</span>
                          <span className="text-[10px] text-gray-400">{u.email}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <button onClick={executarLoyAgora} disabled={loyStatus?.rodando}
                className="flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {loyStatus?.rodando ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                {loyStatus?.rodando ? "Capturando..." : "Buscar e enviar agora"}
              </button>

              {loyStatus?.ultimo_run && (
                <p className="text-[10px] text-gray-400 text-center">
                  Última execução: {new Date(loyStatus.ultimo_run).toLocaleString("pt-BR")}
                  {" — "}{loyStatus.ultimo_status === "ok" ? "✅ OK" : loyStatus.ultimo_status === "com_erros" ? "⚠️ Com erros" : ""}
                  {loyStatus.ultimo_novos_cnj != null && ` · ${loyStatus.ultimo_novos_cnj} CNJ novo(s)`}
                </p>
              )}
            </section>
          )}

          {/* Agendamento automático — Baixa de Processos (fluxo completo) */}
          {!isOtherUserRunning && robo.id === "baixa-processos" && (
            <section className="flex flex-col gap-3 pb-4 mb-1 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Clock size={12} className="text-indigo-600" />
                <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-widest">Execução Automática — Fluxo Completo</p>
              </div>
              <p className="text-[10px] text-gray-400 -mt-1.5">
                Consulta o Autojur ("kit de baixa"), o tribunal (eProc/Projudi) e classifica cada processo — em
                homologação só simula; em produção abre o serviço no SISJUR e atualiza o Autojur de verdade.
                A execução agendada sempre roda sobre todas as tarefas abertas (sem filtro de códigos), respeitando
                os toggles Sim/Não configurados acima.
              </p>

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={baixaAgendaAtiva}
                  onChange={e => { setBaixaAgendaAtiva(e.target.checked); salvarBaixaConfig({ agenda_ativa: e.target.checked }); }}
                  disabled={status.rodando}
                  className="w-3.5 h-3.5 accent-indigo-600" />
                <span className="text-xs text-gray-700">Ativar agendamento automático</span>
              </label>

              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">
                  Dias do mês (clique para marcar/desmarcar — repete todo mês, horário de Brasília)
                </p>
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(dia => {
                    const slot = baixaAgendaSlots.find(s => s.dia_mes === dia);
                    return (
                      <button key={dia} type="button" disabled={status.rodando}
                        onClick={() => {
                          const jaMarcado = !!slot;
                          const novos = jaMarcado
                            ? baixaAgendaSlots.filter(s => s.dia_mes !== dia)
                            : [...baixaAgendaSlots, { dia_mes: dia, hora: "08:00" }].sort((a, b) => a.dia_mes - b.dia_mes);
                          setBaixaAgendaSlots(novos);
                          salvarBaixaConfig({ slots: novos });
                        }}
                        className={`rounded-md py-1.5 text-[11px] font-semibold transition-all disabled:opacity-50
                          ${slot ? "bg-indigo-600 text-white" : "bg-gray-50 text-gray-400 hover:bg-gray-100"}`}>
                        {dia}
                      </button>
                    );
                  })}
                </div>
              </div>

              {baixaAgendaSlots.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Horário de cada dia marcado</p>
                  {baixaAgendaSlots.map(slot => (
                    <div key={slot.dia_mes} className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 w-16">Dia {slot.dia_mes}</span>
                      <input
                        type="time"
                        value={slot.hora}
                        disabled={status.rodando}
                        onChange={e => {
                          const novos = baixaAgendaSlots.map(s => s.dia_mes === slot.dia_mes ? { ...s, hora: e.target.value } : s);
                          setBaixaAgendaSlots(novos);
                        }}
                        onBlur={e => salvarBaixaConfig({ slots: baixaAgendaSlots.map(s => s.dia_mes === slot.dia_mes ? { ...s, hora: e.target.value } : s) })}
                        className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-indigo-500/30 focus:border-indigo-500 disabled:opacity-50"
                      />
                      <button type="button" disabled={status.rodando}
                        onClick={() => {
                          const novos = baixaAgendaSlots.filter(s => s.dia_mes !== slot.dia_mes);
                          setBaixaAgendaSlots(novos);
                          salvarBaixaConfig({ slots: novos });
                        }}
                        className="text-[10px] text-red-500 hover:underline disabled:opacity-50">
                        remover
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">
                  Notificar por e-mail ao concluir (padrão — separe vários por vírgula)
                </p>
                <input
                  type="text"
                  value={baixaEmailsTexto}
                  onChange={e => setBaixaEmailsTexto(e.target.value)}
                  onBlur={e => salvarBaixaConfig({ emailsTexto: e.target.value })}
                  placeholder="fulano@escritorio.com.br, ciclano@escritorio.com.br"
                  className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-indigo-500/30 focus:border-indigo-500"
                />
                <p className="text-[10px] text-gray-300 mt-1">
                  Usado pela execução agendada e por execuções manuais que não preencherem um e-mail específico (acima).
                </p>
              </div>

              <p className="text-[10px] text-gray-400 -mt-1">
                O botão de execução manual fica mais abaixo, na área principal — o mesmo botão que roda a
                execução agendada.
              </p>

              {baixaAgendaStatus?.ultimo_run && (
                <p className="text-[10px] text-gray-400 text-center">
                  Última execução: {new Date(baixaAgendaStatus.ultimo_run).toLocaleString("pt-BR")}
                  {" — "}{baixaAgendaStatus.ultimo_status === "ok" ? "✅ OK" : baixaAgendaStatus.ultimo_status === "com_erros" ? "⚠️ Com erros" : ""}
                  {baixaAgendaStatus.ultimo_finalizada != null && ` · ${baixaAgendaStatus.ultimo_finalizada} elegível(is) pro SISJUR`}
                  {baixaAgendaStatus.ultimo_sisjur_erro > 0 && ` · ⚠️ ${baixaAgendaStatus.ultimo_sisjur_erro} erro(s) no SISJUR`}
                  {baixaAgendaStatus.ultimo_autojur_falha > 0 && ` · ⚠️ ${baixaAgendaStatus.ultimo_autojur_falha} falha(s) no Autojur`}
                </p>
              )}
            </section>
          )}

          {/* Notificação por e-mail — Custas CELOS (fluxo completo) */}
          {!isOtherUserRunning && robo.id === "custas-celos" && (
            <section className="flex flex-col gap-3 pb-4 mb-1 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Receipt size={12} className="text-indigo-600" />
                <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-widest">Fluxo Completo — Custas CELOS</p>
              </div>
              <p className="text-[10px] text-gray-400 -mt-1.5">
                Localiza as tarefas de custas/honorários da CELOS no Autojur, mapeia a emissão da guia no eproc
                TJSC e no portal do TJPR até o botão final (sempre bloqueado em homologação — nenhuma guia real
                é criada) e monta o preview do GED/comentário/finalização e da solicitação de pagamento na CELOS.
              </p>

              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5">
                  Notificar por e-mail ao concluir (separe vários por vírgula) — vale pros 3 fluxos (mapeamento,
                  produção e comprovantes)
                </p>
                <input
                  type="text"
                  value={custasCelosEmailsTexto}
                  onChange={e => setCustasCelosEmailsTexto(e.target.value)}
                  onBlur={e => salvarCustasCelosConfig({ emailsTexto: e.target.value })}
                  placeholder="fulano@escritorio.com.br, ciclano@escritorio.com.br"
                  className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-indigo-500/30 focus:border-indigo-500"
                />
              </div>

              <p className="text-[10px] text-gray-400 -mt-1">
                O botão de execução do mapeamento (homologação) fica mais abaixo, na área principal.
              </p>

              {custasCelosStatus?.ultimo_run && (
                <p className="text-[10px] text-gray-400 text-center">
                  Última execução (mapeamento): {new Date(custasCelosStatus.ultimo_run).toLocaleString("pt-BR")}
                  {" — "}{custasCelosStatus.ultimo_status === "ok" ? "✅ OK" : custasCelosStatus.ultimo_status}
                  {custasCelosStatus.ultimo_total != null && ` · ${custasCelosStatus.ultimo_chegaram_fim ?? 0}/${custasCelosStatus.ultimo_total} guia(s) chegariam até o fim`}
                </p>
              )}
            </section>
          )}

          {/* Resumo por categoria — busca ao vivo no Autojur: quantas tarefas tem
              em cada uma das 8 categorias de custas CELOS, por tribunal, e se já
              roda automático ou ainda precisa de validação manual (com o motivo) */}
          {!isOtherUserRunning && robo.id === "custas-celos" && (
            <section className="flex flex-col gap-2 pb-4 mb-1 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Receipt size={12} className="text-indigo-600" />
                  <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-widest">Categorias no Autojur</p>
                </div>
                <button onClick={buscarResumoCategoriasCelos} disabled={celosResumoCarregando}
                  className="text-[10px] text-indigo-600 hover:text-indigo-800 flex items-center gap-1 disabled:opacity-40 transition-colors">
                  <RefreshCw size={11} className={celosResumoCarregando ? "animate-spin" : ""} />
                  {celosResumoCarregando ? "Buscando..." : "Atualizar"}
                </button>
              </div>

              {celosResumoErro && (
                <p className="text-[10px] text-red-600">{celosResumoErro}</p>
              )}

              {celosResumoCategorias && (
                <>
                  <div className="border border-gray-100 rounded-xl overflow-hidden">
                    {celosResumoCategorias.map(l => (
                      <div key={l.categoria} className="p-2 border-b border-gray-100 last:border-b-0 bg-white">
                        <p className="text-[11px] font-semibold text-gray-700 mb-1">{l.categoria}</p>

                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[10px] text-gray-500">
                            TJSC — {l.qtd_tjsc} aberta(s)
                            {l.automatico_tjsc && ` · ${l.qtd_tjsc_elegivel_agora} c/ prazo vencido/hoje`}
                          </p>
                          <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full whitespace-nowrap
                            ${l.automatico_tjsc ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                            {l.automatico_tjsc ? "Automático" : "Validar"}
                          </span>
                        </div>
                        {l.motivo_tjsc && (
                          <p className="text-[9px] text-amber-600">⚠ {l.motivo_tjsc}</p>
                        )}

                        <div className="flex items-center justify-between gap-2 mt-1">
                          <p className="text-[10px] text-gray-500">TJPR — {l.qtd_tjpr} aberta(s)</p>
                          <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full whitespace-nowrap bg-amber-50 text-amber-700">
                            Validar
                          </span>
                        </div>
                        <p className="text-[9px] text-amber-600">⚠ {l.motivo_tjpr}</p>

                        {l.qtd_outros > 0 && (
                          <p className="text-[9px] text-gray-400 mt-0.5">Outro tribunal: {l.qtd_outros}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  {celosResumoGeradoEm && (
                    <p className="text-[9px] text-gray-300 text-right">
                      Atualizado em {new Date(celosResumoGeradoEm).toLocaleString("pt-BR")}
                    </p>
                  )}
                </>
              )}
            </section>
          )}

          {/* Agendamento automático — Custas CELOS (produção segunda-feira + comprovantes sexta-feira) */}
          {!isOtherUserRunning && robo.id === "custas-celos" && (
            <section className="flex flex-col gap-4 pb-4 mb-1 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Clock size={12} className="text-indigo-600" />
                <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-widest">Execução Automática Semanal</p>
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={custasCelosAtivoProducao}
                      onChange={e => { setCustasCelosAtivoProducao(e.target.checked); salvarCustasCelosConfig({ ativo_producao: e.target.checked }); }}
                      disabled={status.rodando}
                      className="w-3.5 h-3.5 accent-indigo-600" />
                    <span className="text-xs text-gray-700">Toda segunda-feira às</span>
                  </label>
                  <input
                    type="time"
                    value={custasCelosHoraProducao}
                    onChange={e => { setCustasCelosHoraProducao(e.target.value); salvarCustasCelosConfig({ hora_producao: e.target.value }); }}
                    disabled={status.rodando}
                    className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-indigo-500/30 focus:border-indigo-500 disabled:opacity-50"
                  />
                  <span className="text-xs text-gray-400">(Brasília), emite guia real ponta a ponta</span>
                </div>
                <p className="text-[10px] text-red-600 -mt-0.5">
                  ⚠ Sem pausa humana — emite a guia, finaliza a tarefa e envia o pedido de pagamento à CELOS sozinho
                  (só Apelação/Agravo/Recurso Especial, TJSC).
                </p>
                {custasCelosStatus?.ultimo_run_producao && (
                  <p className="text-[10px] text-gray-400">
                    Última: {new Date(custasCelosStatus.ultimo_run_producao).toLocaleString("pt-BR")}
                    {" — "}{custasCelosStatus.ultimo_status_producao === "ok" ? "✅ OK" : custasCelosStatus.ultimo_status_producao}
                    {custasCelosStatus.ultimo_producao_concluidas != null &&
                      ` · ${custasCelosStatus.ultimo_producao_concluidas}/${custasCelosStatus.ultimo_producao_elegiveis ?? 0} concluída(s)`}
                    {custasCelosStatus.ultimo_producao_pendentes_manual > 0 &&
                      ` · ${custasCelosStatus.ultimo_producao_pendentes_manual} pendente(s) manual`}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={custasCelosAtivoComprovantes}
                      onChange={e => { setCustasCelosAtivoComprovantes(e.target.checked); salvarCustasCelosConfig({ ativo_comprovantes: e.target.checked }); }}
                      disabled={status.rodando}
                      className="w-3.5 h-3.5 accent-indigo-600" />
                    <span className="text-xs text-gray-700">Toda sexta-feira às</span>
                  </label>
                  <input
                    type="time"
                    value={custasCelosHoraComprovantes}
                    onChange={e => { setCustasCelosHoraComprovantes(e.target.value); salvarCustasCelosConfig({ hora_comprovantes: e.target.value }); }}
                    disabled={status.rodando}
                    className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-900 bg-white outline-none focus:ring-1 focus:ring-indigo-500/30 focus:border-indigo-500 disabled:opacity-50"
                  />
                  <span className="text-xs text-gray-400">(Brasília), busca e anexa comprovante de pagamento</span>
                </div>
                <p className="text-[10px] text-amber-600 -mt-0.5">
                  ⚠ A leitura do comprovante na tela Acompanhamento da CELOS ainda não foi validada ao vivo — por
                  enquanto essa execução loga "não implementado" pra cada pendente, sem travar nada.
                </p>
                {custasCelosStatus?.ultimo_run_comprovantes && (
                  <p className="text-[10px] text-gray-400">
                    Última: {new Date(custasCelosStatus.ultimo_run_comprovantes).toLocaleString("pt-BR")}
                    {" — "}{custasCelosStatus.ultimo_status_comprovantes === "ok" ? "✅ OK" : custasCelosStatus.ultimo_status_comprovantes}
                    {custasCelosStatus.ultimo_comprovantes_resolvidos != null &&
                      ` · ${custasCelosStatus.ultimo_comprovantes_resolvidos} resolvido(s), ${custasCelosStatus.ultimo_comprovantes_pendentes ?? 0} pendente(s)`}
                  </p>
                )}
              </div>

              <p className="text-[10px] text-gray-400 -mt-1">
                Os botões de "executar agora" (mesmo fluxo do agendamento) ficam mais abaixo, na área principal.
              </p>
            </section>
          )}

          {/* Lista de registros — oculta quando o robô usa o ZIP do EPROC, Tribunais, Classificador, Análise de Processos ou Baixa de Processos */}
          {!isOtherUserRunning && !robo.temZipEproc && !robo.temTribunais && !robo.temClassificador && !robo.temAnaliseProcessos && !robo.temPdfToText && !robo.temBaixaProcessos && !robo.temLoyCaptura && !robo.temCustasCelos && (
            <section className="flex-1 min-h-0 flex flex-col">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                Registros ({registros.length})
              </p>
              <div className="flex-1 min-h-0 border border-gray-100 rounded-xl overflow-y-auto bg-gray-50">
                <table className="w-full text-left text-[10px] font-mono">
                  <tbody className="divide-y divide-gray-100">
                    {registros.map((r, i) => (
                      <tr key={i} className="hover:bg-[#EEF1F8]">
                        {robo.colKeys.map((k, ki) => (
                          <td key={k} className={`p-1.5 truncate ${ki === 0 ? "text-gray-600" : "text-[#1B2B5B]"}`}>{r[k]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Credencial + Botão — ocultos quando bloqueado por outro usuário */}
          {!isOtherUserRunning && (
            <>
              {/* Seletor de credencial padrão — oculto para robôs de tribunais, classificador, análise de processos e baixa de processos (não usa login SISJUR no fluxo novo) */}
              {!robo.temTribunais && !robo.temClassificador && !robo.temAnaliseProcessos && !robo.temPdfToText && !robo.temBaixaProcessos && !robo.temLoyCaptura && !robo.temCustasCelos && (
                <section>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Credencial</p>
                  </div>
                  {credentials.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">Nenhuma credencial cadastrada para este robô.</p>
                  ) : (
                    <div className="relative">
                      <select value={credSel} onChange={e => setCredSel(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 bg-white outline-none focus:ring-2 focus:ring-[#1B2B5B]/20 focus:border-[#1B2B5B] appearance-none">
                        <option value="">— Selecionar credencial —</option>
                        {credentials.map(c => (
                          <option key={c.id} value={c.id}>{c.nome} ({c.login})</option>
                        ))}
                      </select>
                      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  )}
                  {cred_sel_obj && (
                    <p className="text-[10px] text-gray-400 mt-1.5 font-mono">Login: {cred_sel_obj.login}</p>
                  )}
                </section>
              )}

              {/* Alerta geral (credencial, erro de rede) — visível para todos os robôs */}
              {alerta && (robo.temTribunais || robo.temAnaliseProcessos || robo.temPdfToText) && (
                <div className={`p-2.5 rounded-lg flex items-start gap-1.5 text-xs
                  ${alerta.tipo === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                  {alerta.tipo === "ok"
                    ? <CheckCircle size={13} className="mt-0.5 flex-shrink-0" />
                    : <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />}
                  {alerta.msg}
                </div>
              )}

              {/* Agendamento pendente */}
              {agendamento && !status.rodando && (
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <div>
                    <p className="text-xs font-bold text-amber-700 flex items-center gap-1.5">
                      <Clock size={12} /> Agendado para {agendamento.hora}
                    </p>
                    <p className="text-[10px] text-amber-600 mt-0.5">O robô iniciará automaticamente.</p>
                  </div>
                  <button onClick={cancelarAgendamento}
                    className="text-amber-500 hover:text-red-600 transition-colors p-1">
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Botão Parar / Executar */}
              {status.rodando ? (
                <Btn variant="danger" size="lg" className="w-full justify-center" onClick={async () => {
                  try {
                    await api(`/parar/${robo.id}`, {
                      method: "POST",
                      body: JSON.stringify({ executionId: executionIdRef.current }),
                    });
                  } catch {}
                  setNovncPort(null);
                  setRunningExec(null);
                  executionIdRef.current = null;
                }}>
                  Parar Execução
                </Btn>
              ) : (
                <div className={`flex gap-2 ${robo.temZipsMultiplos ? "" : ""}`}>
                  {robo.temZipsMultiplos && !agendamento && (
                    <Btn variant="secondary" size="lg" className="flex-1 justify-center"
                      disabled={registros.length === 0 || !credSel}
                      onClick={() => { setAlertaAgendar(null); setShowModalAgendar(true); }}>
                      <Calendar size={14} /> Agendar
                    </Btn>
                  )}
                  {/* Petição: fluxo obrigatório — detectar IA primeiro, depois escolher com/sem IA */}
                  {robo.temZipPeticao ? (
                    !resultadoIA ? (
                      <div className="w-full flex flex-col items-center gap-1.5">
                        <Btn variant="primary" size="lg" className="w-full justify-center opacity-40 cursor-not-allowed" disabled>
                          Executar Agora
                        </Btn>
                        <p className="text-[10px] text-amber-600 text-center">
                          Execute a detecção de DECISÃO via IA antes de executar.
                        </p>
                      </div>
                    ) : (
                      <div className="w-full flex flex-col gap-2">
                        <Btn variant="primary" size="lg" className="w-full justify-center"
                          disabled={!peticaoZipPath || !registros.length || !resultadoIA.output_path}
                          onClick={() => executarRPA(true)}>
                          Executar com IA
                        </Btn>
                        <Btn variant="secondary" size="lg" className="w-full justify-center"
                          disabled={!peticaoZipPath || !registros.length}
                          onClick={() => executarRPA(false)}>
                          Executar sem IA
                        </Btn>
                      </div>
                    )
                  ) : robo.temAnaliseProcessos ? (
                    <Btn variant={analiseAmbiente === "producao" ? "danger" : "primary"} size="lg" className="w-full justify-center"
                      disabled={!analisePlanilhaPath || !analiseStoragePath}
                      onClick={() => executarRPA()}>
                      {analiseAmbiente === "producao" ? "Executar (Produção)" : "Executar (Homologação)"}
                    </Btn>
                  ) : robo.temPdfToText ? (
                    <Btn variant="primary" size="lg" className="w-full justify-center"
                      disabled={!pdfArquivos.length || pdfAguardaConfirmacao}
                      onClick={() => executarRPA()}>
                      {pdfAguardaConfirmacao ? "Aguardando confirmação acima" : "Converter"}
                    </Btn>
                  ) : (!robo.temTribunais && !robo.temClassificador && !robo.temBaixaProcessos && !robo.temLoyCaptura && !robo.temCustasCelos) ? (
                    <Btn variant="primary" size="lg"
                      className={`justify-center ${robo.temZipsMultiplos && !agendamento ? "flex-1" : "w-full"}`}
                      disabled={
                        (!robo.temZipEproc && registros.length === 0) ||
                        !credSel ||
                        (robo.temZipEproc      && !eprocZipPath) ||
                        (robo.temZipsMultiplos && !zipsDir)
                      } onClick={() => executarRPA()}>
                      Executar Agora
                    </Btn>
                  ) : null}
                  {robo.id === "baixa-processos" && (
                    <div className="w-full flex flex-col">
                      <Btn variant={kitBaixaContagem?.sisjur_pex_ambiente === "producao" ? "danger" : "primary"}
                        size="lg" className="w-full justify-center"
                        disabled={status.rodando}
                        onClick={executarDryRunFluxoCompleto}>
                        {kitBaixaContagem?.sisjur_pex_ambiente === "producao"
                          ? "Rodar fluxo completo (PRODUÇÃO — cria no SISJUR e atualiza o Autojur)"
                          : "Rodar fluxo completo (homologação — dry-run, sem SISJUR/Autojur)"}
                      </Btn>
                      {alerta && (
                        <div className={`mt-2 p-2.5 rounded-lg flex items-start gap-1.5 text-xs
                          ${alerta.tipo === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                          {alerta.tipo === "ok" ? <CheckCircle size={13} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />}
                          {alerta.msg}
                        </div>
                      )}
                    </div>
                  )}
                  {robo.id === "custas-celos" && (
                    <div className="w-full flex flex-col">
                      {!OCULTAR_MAPEAMENTO_CELOS && (
                        <Btn variant="secondary" size="lg" className="w-full justify-center"
                          disabled={status.rodando}
                          onClick={executarFluxoCompletoCelos}>
                          Rodar mapeamento (homologação — nenhuma guia real é criada)
                        </Btn>
                      )}
                      <div className={`bg-amber-50 border border-amber-200 rounded-lg p-2.5 ${OCULTAR_MAPEAMENTO_CELOS ? "" : "mt-2"}`}>
                        <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wide mb-1.5">
                          Só estas 3 categorias (TJSC) emitem guia real e enviam à CELOS sozinhas
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {["Custas Apelação", "Custas Agravo de Instrumento", "Custas Recurso Especial"].map(cat => (
                            <span key={cat}
                              className="text-[10px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5">
                              {cat}
                            </span>
                          ))}
                        </div>
                        <p className="text-[10px] text-amber-600 mt-1.5">
                          Validado ao vivo em 28/09/2026 (guias reais emitidas, fluxo completo até o envio à CELOS).
                          Demais categorias e tarefas do TJPR ficam pendentes para conferência manual.
                        </p>
                      </div>
                      <Btn variant="danger" size="lg" className="w-full justify-center mt-2"
                        disabled={status.rodando}
                        onClick={executarCustasCelosProducao}>
                        Rodar produção agora (mesmo fluxo de segunda-feira — emite guia real)
                      </Btn>
                      <Btn variant="primary" size="lg" className="w-full justify-center mt-2"
                        disabled={status.rodando}
                        onClick={executarCustasCelosComprovantes}>
                        Buscar comprovantes agora (mesmo fluxo de sexta-feira)
                      </Btn>
                      {alerta && (
                        <div className={`mt-2 p-2.5 rounded-lg flex items-start gap-1.5 text-xs
                          ${alerta.tipo === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                          {alerta.tipo === "ok" ? <CheckCircle size={13} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />}
                          {alerta.msg}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Resultados */}
          {resultados.length > 0 && (
            <section>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Resultados</p>
              <div className="space-y-1.5">
                {resultados.map(f => (
                  <a key={f.nome} href={`${API_URL}/download/${f.nome}?token=${localStorage.getItem("rpa_token")}`}
                    className="flex items-center gap-2 bg-gray-50 hover:bg-[#EEF1F8] border border-gray-100 rounded-xl p-2.5 transition-all"
                    download>
                    <Download size={12} className="text-[#1B2B5B] flex-shrink-0" />
                    <span className="text-[10px] text-gray-700 truncate flex-1">{f.nome}</span>
                  </a>
                ))}
              </div>
            </section>
          )}
        </aside>

        {/* Main */}
        <main className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0 bg-white border-b border-gray-100 flex items-center justify-center">
            {robo.temClassificador ? (
              <div className="w-full h-full overflow-auto p-6 bg-gray-50">
                <ClassificadorDashboard data={dashboardData} envioResumo={envioResumo} rodando={status.rodando} />
              </div>
            ) : robo.temLoyCaptura ? (
              <div className="w-full h-full overflow-auto p-6 bg-gray-50 flex items-center justify-center">
                <div className="max-w-sm w-full space-y-3 text-center">
                  <div className="text-5xl mb-2">📨</div>
                  <p className="text-sm font-semibold text-gray-700">
                    {loyStatus?.rodando ? "Capturando intimações e enviando pra Loy..." : "Sem execução em andamento"}
                  </p>
                  {loyStatus?.ultimo_run && (
                    <div className="bg-white border border-gray-100 rounded-xl p-4 text-left text-xs text-gray-600 space-y-1">
                      <p><strong>Última execução:</strong> {new Date(loyStatus.ultimo_run).toLocaleString("pt-BR")}
                        {" — "}{loyStatus.ultimo_status === "ok" ? "✅ OK" : loyStatus.ultimo_status === "erro" ? "❌ Erro" : loyStatus.ultimo_status}
                      </p>
                      <p><strong>Tarefas de hoje encontradas:</strong> {loyStatus.ultimo_total_lancadas ?? "—"}</p>
                      <p><strong>CNJ novos enviados:</strong> {loyStatus.ultimo_novos_cnj ?? "—"}</p>
                      <p><strong>Já enviados antes:</strong> {loyStatus.ultimo_ja_enviados ?? "—"}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (autoStatus?.rodando && autoStatus?.novnc_port) ? (
              /* Execução automática — mostra noVNC do tribunal atual */
              <iframe
                key={autoStatus.novnc_port}
                src={`${_PROTO}//${_HOST}:${autoStatus.novnc_port}/vnc.html?autoconnect=true&reconnect=true&reconnect_delay=2000&resize=scale`}
                title="VNC Auto" className="w-full h-full border-0"
                allow="clipboard-read; clipboard-write"
              />
            ) : novncPort ? (
              <iframe
                key={novncPort}
                src={`${_PROTO}//${_HOST}:${novncPort}/vnc.html?autoconnect=true&reconnect=true&reconnect_delay=2000&resize=scale`}
                title="VNC" className="w-full h-full border-0"
                allow="clipboard-read; clipboard-write"
              />
            ) : (
              <div className="text-center text-gray-300">
                <div className="text-5xl mb-3">{(robo.temAnaliseProcessos || robo.temPdfToText) ? "📁" : "🖥"}</div>
                <p className="text-sm font-medium text-gray-400">
                  {autoStatus?.rodando
                    ? `Aguardando noVNC — ${autoStatus.current_tribunal || "iniciando..."}`
                    : status.rodando ? "Processando..." : "Aguardando execução"}
                </p>
                <p className="text-xs mt-1 text-gray-300">
                  {autoStatus?.rodando
                    ? "O ambiente aparecerá em instantes"
                    : (robo.temAnaliseProcessos || robo.temPdfToText)
                      ? "Acompanhe pelo Console Output abaixo"
                      : (robo.temBaixaProcessos || robo.temCustasCelos)
                        ? "Clique em 'Rodar fluxo completo' para iniciar"
                        : "Selecione uma credencial e clique em Executar"}
                </p>
              </div>
            )}
          </div>
          {/* Console: mostra logs da execução automática quando ativa, senão logs manuais */}
          <div className="h-52 flex flex-col flex-shrink-0 bg-white">
            <div className="px-5 py-2 border-b border-gray-100 text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
              Console Output
              {autoStatus?.rodando && autoStatus?.current_tribunal && (
                <span className="text-emerald-600 font-semibold normal-case text-[10px]">
                  — {autoStatus.current_tribunal}
                </span>
              )}
            </div>
            <div ref={terminalRef} className="flex-1 overflow-y-auto p-4 bg-gray-950/5">
              {autoStatus?.rodando && autoStatus?.logs?.length > 0 ? (
                autoStatus.logs.slice(-200).map((linha, i) => <LogLine key={i} linha={linha} />)
              ) : logs.length === 0 ? (
                <p className="text-gray-300 italic text-[11px]">Aguardando...</p>
              ) : (
                logs.map((linha, i) => <LogLine key={i} linha={linha} />)
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Modal — Agendar Execução */}
      {showModalAgendar && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={e => { if (e.target === e.currentTarget) setShowModalAgendar(false); }}>
          <div className="bg-white rounded-2xl shadow-xl w-80 p-6">
            <h3 className="text-base font-bold text-gray-900 mb-1">Agendar Execução</h3>
            <p className="text-xs text-gray-500 mb-4">
              Escolha o horário entre <span className="font-semibold text-gray-700">18:00</span> e{" "}
              <span className="font-semibold text-gray-700">08:00</span> (horário de baixo fluxo).
            </p>
            <input
              type="time"
              value={horaAgendada}
              onChange={e => setHoraAgendada(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 bg-white outline-none focus:ring-2 focus:ring-[#1B2B5B]/20 focus:border-[#1B2B5B] mb-3"
            />
            {alertaAgendar && (
              <div className="mb-3 p-2.5 rounded-lg flex items-start gap-1.5 text-xs bg-red-50 text-red-600">
                <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
                {alertaAgendar.msg}
              </div>
            )}
            <div className="flex gap-2">
              <Btn variant="secondary" size="md" className="flex-1 justify-center"
                onClick={() => setShowModalAgendar(false)}>
                Cancelar
              </Btn>
              <Btn variant="primary" size="md" className="flex-1 justify-center"
                disabled={agendandoLoading} onClick={criarAgendamento}>
                {agendandoLoading ? <Loader size={14} className="animate-spin" /> : "Confirmar"}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {showModalChecklist && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={e => { if (e.target === e.currentTarget) setShowModalChecklist(false); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 flex flex-col gap-3 max-h-[85vh]">
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-1">Checklist da análise (PDF to Text)</h3>
              <p className="text-xs text-gray-500">
                Markdown com seções <code className="bg-gray-100 px-1 rounded">##</code> — cada seção vira uma
                pergunta que a IA responde lendo só o auto convertido. Usado quando "Rodar análise por checklist" está marcado.
              </p>
            </div>
            {checklistLoading ? (
              <div className="flex-1 flex items-center justify-center py-10">
                <Loader size={20} className="animate-spin text-[#1B2B5B]" />
              </div>
            ) : (
              <textarea
                value={checklistTexto}
                onChange={e => setChecklistTexto(e.target.value)}
                className="flex-1 min-h-[320px] border border-gray-200 rounded-lg p-3 text-xs font-mono text-gray-900 bg-gray-50 outline-none focus:ring-2 focus:ring-[#1B2B5B]/20 focus:border-[#1B2B5B] resize-y"
                spellCheck={false}
              />
            )}
            {checklistAlerta && (
              <div className="p-2.5 rounded-lg flex items-start gap-1.5 text-xs bg-red-50 text-red-600">
                <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
                {checklistAlerta.msg}
              </div>
            )}
            <div className="flex gap-2">
              <Btn variant="secondary" size="md" className="flex-1 justify-center"
                onClick={() => setShowModalChecklist(false)}>
                Cancelar
              </Btn>
              <Btn variant="primary" size="md" className="flex-1 justify-center"
                disabled={checklistLoading || checklistSaving} onClick={salvarChecklist}>
                {checklistSaving ? <Loader size={14} className="animate-spin" /> : "Salvar checklist"}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* Modal — confirmação de execução em PRODUÇÃO (Baixa de Processos) */}
      {showModalProducaoConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={e => { if (e.target === e.currentTarget) setShowModalProducaoConfirm(false); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="bg-red-50 border-b border-red-100 px-6 py-4 flex items-start gap-3">
              <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-bold text-red-700">Ambiente de produção</h3>
                <p className="text-xs text-red-600 mt-0.5">Essa ação grava dados reais — não pode ser desfeita.</p>
              </div>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-gray-700 leading-relaxed">
                Isso vai <strong>abrir de verdade</strong> o serviço de Encerramento no SISJUR PEX e{" "}
                <strong>atualizar de verdade</strong> a situação das tarefas no Autojur para os processos elegíveis.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                Confirma que quer rodar o fluxo completo em produção agora?
              </p>
            </div>
            <div className="px-6 pb-6 flex gap-2">
              <Btn variant="secondary" size="md" className="flex-1 justify-center"
                onClick={() => setShowModalProducaoConfirm(false)}>
                Cancelar
              </Btn>
              <Btn variant="danger" size="md" className="flex-1 justify-center"
                onClick={() => { setShowModalProducaoConfirm(false); _dispararDryRunFluxoCompleto(); }}>
                Confirmar e rodar em produção
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 4. ADMIN — USUÁRIOS
// ═══════════════════════════════════════════════════════════════════════════════
function AdminUsersPanel({ user, onBack, onLogout, onCredentials, onExecucoes }) {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null); // null | {mode:'create'} | {mode:'edit', user}

  const load = async () => {
    setLoading(true);
    const res = await api("/admin/users");
    if (res.ok) setUsers(await res.json());
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Excluir este usuário?")) return;
    await api(`/admin/users/${id}`, { method: "DELETE" });
    load();
  };

  return (
    <div className="min-h-screen bg-[#F5F6FA] flex flex-col">
      <NavHeader user={user} onLogout={onLogout} onUsers={() => {}} onCredentials={onCredentials}
        onExecucoes={onExecucoes} currentScreen="users" onRobots={onBack} />

      <main className="max-w-5xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 sm:mb-8">
          <div>
            <p className="text-xs font-semibold text-[#B8963E] uppercase tracking-widest mb-1">Administração</p>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2"><Users size={22} /> Usuários</h1>
          </div>
          <Btn onClick={() => setModal({ mode: "create" })}>
            <Plus size={14} /> Novo Usuário
          </Btn>
        </div>

        <Card className="overflow-x-auto">
          {loading ? (
            <div className="p-10 text-center text-gray-400"><Loader size={20} className="animate-spin mx-auto" /></div>
          ) : (
            <table className="w-full text-sm min-w-[480px]">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-3 sm:px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Nome</th>
                  <th className="text-left px-3 sm:px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">E-mail</th>
                  <th className="text-left px-3 sm:px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Perfil</th>
                  <th className="text-left px-3 sm:px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Robôs</th>
                  <th className="text-left px-3 sm:px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Status</th>
                  <th className="px-3 sm:px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 sm:px-5 py-3 font-medium text-gray-900 text-xs sm:text-sm">{u.name}</td>
                    <td className="px-3 sm:px-5 py-3 text-gray-500 text-xs hidden sm:table-cell">{u.email}</td>
                    <td className="px-3 sm:px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold
                        ${u.role === "admin" ? "bg-[#1B2B5B] text-white" : "bg-gray-100 text-gray-600"}`}>
                        {u.role === "admin" ? "Admin" : "Usuário"}
                      </span>
                      {u.manage_credentials && (
                        <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#B8963E]/10 text-[#B8963E]">
                          Creds
                        </span>
                      )}
                    </td>
                    <td className="px-3 sm:px-5 py-3 text-xs text-gray-400 hidden md:table-cell">
                      {(u.robots || []).join(", ") || "—"}
                    </td>
                    <td className="px-3 sm:px-5 py-3 hidden sm:table-cell">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold
                        ${u.active ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                        {u.active ? "Ativo" : "Inativo"}
                      </span>
                    </td>
                    <td className="px-3 sm:px-5 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <Btn size="sm" variant="ghost" onClick={() => setModal({ mode: "edit", u })}>
                          <Pencil size={12} />
                        </Btn>
                        <Btn size="sm" variant="ghost" className="hover:text-red-600 hover:bg-red-50" onClick={() => del(u.id)}>
                          <Trash2 size={12} />
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </main>

      {modal && (
        <UserModal
          mode={modal.mode}
          existing={modal.u}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function UserModal({ mode, existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:               existing?.name  || "",
    email:              existing?.email || "",
    password:           "",
    role:               existing?.role  || "user",
    active:             existing?.active ?? true,
    robots:             existing?.robots || [],
    manage_credentials: existing?.manage_credentials || false,
  });
  const [erro,   setErro]   = useState("");
  const [saving, setSaving] = useState(false);

  const toggle = (field) => setForm(f => ({ ...f, [field]: !f[field] }));
  const toggleRobot = (id) => setForm(f => ({
    ...f, robots: f.robots.includes(id) ? f.robots.filter(r => r !== id) : [...f.robots, id],
  }));

  const save = async () => {
    setErro(""); setSaving(true);
    try {
      const body = { ...form };
      if (mode === "edit" && !body.password) delete body.password;
      const res = await api(
        mode === "create" ? "/admin/users" : `/admin/users/${existing.id}`,
        { method: mode === "create" ? "POST" : "PUT", body: JSON.stringify(body) },
      );
      const data = await res.json();
      if (!res.ok) { setErro(data.erro || "Erro ao salvar"); return; }
      onSaved();
    } catch { setErro("Erro de conexão"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-start sm:items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-md p-5 sm:p-6 my-4 sm:my-0">
        <h3 className="text-lg font-bold text-gray-900 mb-5">
          {mode === "create" ? "Novo Usuário" : "Editar Usuário"}
        </h3>
        {erro && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 text-xs rounded-lg px-3 py-2 mb-4"><AlertCircle size={13}/>{erro}</div>}

        <div className="flex flex-col gap-3">
          <Input label="Nome" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
          <Input label="E-mail" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
          <Input label={mode === "edit" ? "Nova Senha (deixe vazio para manter)" : "Senha"}
            type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} />

          <Select label="Perfil" value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
            <option value="user">Usuário</option>
            <option value="admin">Admin</option>
          </Select>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Acesso a Robôs</p>
            <div className="flex flex-col gap-1">
              {ROBOS_DISPONIVEIS.map(r => (
                <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded px-2 py-1.5">
                  <input type="checkbox" className="accent-[#1B2B5B]"
                    checked={form.robots.includes(r.id)} onChange={() => toggleRobot(r.id)} />
                  {r.nome} — {r.subtitulo}
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded px-2 py-1.5">
            <input type="checkbox" className="accent-[#B8963E]"
              checked={form.manage_credentials} onChange={() => toggle("manage_credentials")} />
            Pode gerenciar credenciais
          </label>

          <label className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded px-2 py-1.5">
            <input type="checkbox" className="accent-[#1B2B5B]"
              checked={form.active} onChange={() => toggle("active")} />
            Usuário ativo
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="secondary" onClick={onClose}>Cancelar</Btn>
          <Btn variant="primary" onClick={save} disabled={saving}>
            {saving ? <Loader size={13} className="animate-spin" /> : null}
            {mode === "create" ? "Criar" : "Salvar"}
          </Btn>
        </div>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 5. ADMIN — CREDENCIAIS
// ═══════════════════════════════════════════════════════════════════════════════
function AdminCredentialsPanel({ user, onBack, onLogout, onUsers, onExecucoes }) {
  const [creds,   setCreds]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);
  const [filter,  setFilter]  = useState("");

  const load = async () => {
    setLoading(true);
    const res = await api("/credentials");
    if (res.ok) setCreds(await res.json());
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Excluir esta credencial?")) return;
    await api(`/credentials/${id}`, { method: "DELETE" });
    load();
  };

  const filtered = filter ? creds.filter(c => c.robot_id === filter) : creds;
  const roboNome = (id) => ROBOS_DISPONIVEIS.find(r => r.id === id)?.nome || id;

  return (
    <div className="min-h-screen bg-[#F5F6FA] flex flex-col">
      <NavHeader user={user} onLogout={onLogout} onUsers={onUsers}
        onCredentials={() => {}} onExecucoes={onExecucoes} currentScreen="credentials" onRobots={onBack} />

      <main className="max-w-5xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 sm:mb-8">
          <div>
            <p className="text-xs font-semibold text-[#B8963E] uppercase tracking-widest mb-1">Gerenciamento</p>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2"><Key size={22} /> Credenciais</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={filter} onChange={e => setFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-700 outline-none focus:border-[#1B2B5B]">
              <option value="">Todos os robôs</option>
              {ROBOS_DISPONIVEIS.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
            </select>
            <Btn onClick={() => setModal({ mode: "create" })}>
              <Plus size={14} /> Nova Credencial
            </Btn>
          </div>
        </div>

        <Card className="overflow-x-auto">
          {loading ? (
            <div className="p-10 text-center text-gray-400"><Loader size={20} className="animate-spin mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-gray-400 text-sm">Nenhuma credencial cadastrada.</div>
          ) : (
            <table className="w-full text-sm min-w-[380px]">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-3 sm:px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Nome</th>
                  <th className="text-left px-3 sm:px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Login</th>
                  <th className="text-left px-3 sm:px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Senha</th>
                  <th className="text-left px-3 sm:px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Robô</th>
                  <th className="px-3 sm:px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-5 py-3 font-medium text-gray-900 text-xs sm:text-sm">{c.nome}</td>
                    <td className="px-3 sm:px-5 py-3 text-gray-500 font-mono text-xs hidden sm:table-cell">{c.login}</td>
                    <td className="px-3 sm:px-5 py-3 text-gray-300 font-mono text-xs hidden md:table-cell">{"•".repeat(8)}</td>
                    <td className="px-3 sm:px-5 py-3">
                      <span className="px-2 py-0.5 bg-[#EEF1F8] text-[#1B2B5B] rounded-full text-[10px] font-semibold">
                        {roboNome(c.robot_id)}
                      </span>
                    </td>
                    <td className="px-3 sm:px-5 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <Btn size="sm" variant="ghost" onClick={() => setModal({ mode: "edit", c })}>
                          <Pencil size={12} />
                        </Btn>
                        <Btn size="sm" variant="ghost" className="hover:text-red-600 hover:bg-red-50" onClick={() => del(c.id)}>
                          <Trash2 size={12} />
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </main>

      {modal && (
        <CredentialModal
          mode={modal.mode}
          existing={modal.c}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function CredentialModal({ mode, existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    nome:     existing?.nome     || "",
    login:    existing?.login    || "",
    senha:    existing?.senha    || "",
    robot_id: existing?.robot_id || ROBOS_DISPONIVEIS[0].id,
  });
  const [erro,   setErro]   = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setErro(""); setSaving(true);
    try {
      const res = await api(
        mode === "create" ? "/credentials" : `/credentials/${existing.id}`,
        { method: mode === "create" ? "POST" : "PUT", body: JSON.stringify(form) },
      );
      const data = await res.json();
      if (!res.ok) { setErro(data.erro || "Erro ao salvar"); return; }
      onSaved();
    } catch { setErro("Erro de conexão"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-start sm:items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-sm p-5 sm:p-6 my-4 sm:my-0">
        <h3 className="text-lg font-bold text-gray-900 mb-5">
          {mode === "create" ? "Nova Credencial" : "Editar Credencial"}
        </h3>
        {erro && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 text-xs rounded-lg px-3 py-2 mb-4"><AlertCircle size={13}/>{erro}</div>}
        <div className="flex flex-col gap-3">
          <Input label="Nome da Credencial" placeholder="Ex: Login Principal EPROC"
            value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} />
          <Select label="Robô" value={form.robot_id} onChange={e => setForm({...form, robot_id: e.target.value})}>
            {ROBOS_DISPONIVEIS.map(r => <option key={r.id} value={r.id}>{r.nome} — {r.subtitulo}</option>)}
          </Select>
          <Input label="Login (usuário do sistema)" placeholder="usuario.eproc"
            value={form.login} onChange={e => setForm({...form, login: e.target.value})} />
          <Input label="Senha" type="password" placeholder="••••••••"
            value={form.senha} onChange={e => setForm({...form, senha: e.target.value})} />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="secondary" onClick={onClose}>Cancelar</Btn>
          <Btn variant="primary" onClick={save} disabled={saving}>
            {saving ? <Loader size={13} className="animate-spin" /> : null}
            {mode === "create" ? "Criar" : "Salvar"}
          </Btn>
        </div>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 6. EXECUÇÕES
// ═══════════════════════════════════════════════════════════════════════════════
const ROBOT_NOMES = {
  "sisjurpex-custas":  "SisJurPex — Custas",
  "sisjur-insercao":   "SisJur — Inserção",
  "eproc-guias":       "EPROC — Guias",
  "sisjurpex-peticao": "Petição e Protocolo",
  "autojur-loy":       "Autojur → Loy Legal",
  "custas-celos":      "Custas CELOS",
};

// Custas CELOS compartilha um único robot_id pra 3 ações bem distintas
// (mapeamento/homologação, produção real de segunda-feira, comprovantes de
// sexta-feira) — robot_subflow (ver backend/server.js) distingue qual foi.
const ROBOT_SUBFLOW_LABELS = {
  mapeamento:    { label: "Mapeamento (homologação)", cls: "bg-gray-100 text-gray-600" },
  producao:      { label: "Produção (segunda-feira)", cls: "bg-red-100 text-red-700" },
  comprovantes:  { label: "Comprovantes (sexta-feira)", cls: "bg-blue-100 text-blue-700" },
};

function StatusBadge({ status }) {
  const cfg = {
    running:      { cls: "bg-blue-100 text-blue-700",   label: "Executando" },
    ok:           { cls: "bg-green-100 text-green-700",  label: "Concluído"  },
    com_erros:    { cls: "bg-amber-100 text-amber-700",  label: "Com erros"  },
    interrompido: { cls: "bg-orange-100 text-orange-700",label: "Interrompido"},
    timeout:      { cls: "bg-red-100 text-red-700",      label: "Timeout"    },
  }[status] || { cls: "bg-gray-100 text-gray-600", label: status };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.cls}`}>
      {status === "running" && <Loader size={10} className="animate-spin" />}
      {cfg.label}
    </span>
  );
}

function ExecProgressBar({ total, ok, falha, status, processados = 0 }) {
  const naoProc    = total > 0 ? Math.max(0, total - ok - falha) : 0;
  const pOk        = total > 0 ? Math.round((ok       / total) * 100) : 0;
  const pFalha     = total > 0 ? Math.round((falha    / total) * 100) : 0;
  const pNaoProc   = total > 0 ? Math.max(0, 100 - pOk - pFalha)     : 0;
  const interrompido = status === "interrompido";

  // Durante execução: usa processados (parcial) para barra de progresso
  const progAtual  = (ok + falha) > 0 ? (ok + falha) : processados;
  const pProgAtual = (status === "running" && total > 0 && progAtual > 0)
    ? Math.round((progAtual / total) * 100) : 0;

  return (
    <div className="w-full">
      <div className="flex h-2 rounded-full overflow-hidden bg-gray-100 w-full">
        {status === "running" ? (
          pProgAtual > 0
            ? <div style={{ width: `${pProgAtual}%` }} className="h-full bg-blue-400 transition-all" />
            : <div className="h-full bg-blue-400 animate-pulse w-full" />
        ) : (
          <>
            {pOk      > 0 && <div style={{ width: `${pOk}%`      }} className="h-full bg-green-500 transition-all" />}
            {pFalha   > 0 && <div style={{ width: `${pFalha}%`   }} className="h-full bg-red-400 transition-all" />}
            {pNaoProc > 0 && (
              <div style={{ width: `${pNaoProc}%` }}
                className={`h-full transition-all ${interrompido ? "bg-amber-300" : "bg-gray-200"}`} />
            )}
          </>
        )}
      </div>
      {/* Labels */}
      {total > 0 && status === "running" && progAtual > 0 && (
        <div className="flex gap-3 mt-1 text-xs">
          <span className="text-blue-500 font-medium">{progAtual}/{total} processados</span>
          <span className="text-gray-400">{pProgAtual}%</span>
        </div>
      )}
      {total > 0 && status !== "running" && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs">
          {ok    > 0 && <span className="text-green-600 font-medium">{ok} ok</span>}
          {falha > 0 && <span className="text-red-500 font-medium">{falha} erro{falha !== 1 ? "s" : ""}</span>}
          {naoProc > 0 && interrompido && (
            <span className="text-amber-500 font-medium">{naoProc} não processado{naoProc !== 1 ? "s" : ""}</span>
          )}
          <span className="text-gray-400">{total} total · {pOk}%</span>
        </div>
      )}
      {/* Sem total mas com status claro */}
      {total === 0 && status === "interrompido" && (
        <span className="text-xs text-amber-500 font-medium">interrompido</span>
      )}
      {total === 0 && status === "com_erros" && (
        <span className="text-xs text-red-500 font-medium">com erros</span>
      )}
    </div>
  );
}

// ── Resumo Custas CELOS — produção (segunda) / comprovantes (sexta) ──
// Diferente de BaixaProcessosResumo/LoyResumo (que baixam um arquivo à
// parte), aqui os números já vêm prontos na própria linha "[RESUMO-...]"
// que o worker loga — mais simples só ler do exec.logs já carregado.
function CustasCelosSubfluxoResumo({ exec }) {
  const logs = exec.logs || "";
  if (exec.robot_subflow === "producao") {
    const m = logs.match(/\[RESUMO-PRODUCAO-CELOS\]\s*total_elegiveis=(\d+)\s*concluidas=(\d+)\s*falhas=(\d+)\s*pendentes_manual=(\d+)\s*ambiente=(\S+)/i);
    if (!m) return null;
    const [, elegiveis, concluidas, falhas, pendentes, ambiente] = m;
    return (
      <div className="px-6 py-4 border-b border-gray-100">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
          Resumo — Produção Real (segunda-feira) · ambiente: {ambiente}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><p className="text-[10px] text-gray-400 uppercase">Elegíveis</p><p className="text-xl font-bold text-gray-800">{elegiveis}</p></div>
          <div><p className="text-[10px] text-gray-400 uppercase">Concluídas</p><p className="text-xl font-bold text-emerald-600">{concluidas}</p></div>
          <div><p className="text-[10px] text-gray-400 uppercase">Falhas</p><p className="text-xl font-bold text-red-600">{falhas}</p></div>
          <div><p className="text-[10px] text-gray-400 uppercase">Pendentes manual</p><p className="text-xl font-bold text-amber-600">{pendentes}</p></div>
        </div>
      </div>
    );
  }
  if (exec.robot_subflow === "comprovantes") {
    const m = logs.match(/\[RESUMO-COMPROVANTES-CELOS\]\s*total_pendentes=(\d+)\s*resolvidos=(\d+)\s*ainda_pendentes=(\d+)/i);
    if (!m) return null;
    const [, totalPendentes, resolvidos, aindaPendentes] = m;
    return (
      <div className="px-6 py-4 border-b border-gray-100">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
          Resumo — Busca de Comprovantes (sexta-feira)
        </p>
        <div className="grid grid-cols-3 gap-3">
          <div><p className="text-[10px] text-gray-400 uppercase">No início</p><p className="text-xl font-bold text-gray-800">{totalPendentes}</p></div>
          <div><p className="text-[10px] text-gray-400 uppercase">Resolvidos</p><p className="text-xl font-bold text-emerald-600">{resolvidos}</p></div>
          <div><p className="text-[10px] text-gray-400 uppercase">Ainda pendentes</p><p className="text-xl font-bold text-amber-600">{aindaPendentes}</p></div>
        </div>
      </div>
    );
  }
  return null;
}

function ExecDetalheModal({ execId, onClose, user, onParado }) {
  const [exec,        setExec]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [parando,     setParando]     = useState(false);
  const logRef = useRef(null);

  const isAdmin  = user?.role === "admin";
  const isOwner  = exec && user && exec.user_id === user.id;
  const podeParar = exec?.status === "running" && (isAdmin || isOwner);

  useEffect(() => {
    api(`/execucoes/${execId}`)
      .then(r => r.json())
      .then(d => setExec(d))
      .finally(() => setLoading(false));
  }, [execId]);

  const handleParar = async () => {
    setParando(true);
    try {
      await api(`/execucoes/${execId}/parar`, { method: "POST" });
      setExec(e => e ? { ...e, status: "interrompido" } : e);
      onParado?.();
    } catch {}
    setParando(false);
  };

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [exec]);

  const fmtDt  = dt => dt ? new Date(dt).toLocaleString("pt-BR") : "—";
  const fmtDur = (ini, fim) => {
    if (!ini || !fim) return "—";
    const s = Math.round((new Date(fim) - new Date(ini)) / 1000);
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    return s < 60 ? `${sec}s` : `${m}m ${sec}s`;
  };

  const logLines = (exec?.logs || "").split("\n").filter(Boolean);
  const lineClass = l => {
    if (l.includes("[ERRO]") || l.includes("[ERROR]") || l.includes("FALHA:"))
      return "text-red-400";
    if (l.includes("[WARNING]") || l.includes("[WARN]"))
      return "text-amber-400";
    if (l.includes("[SISTEMA]") || l.includes("CONCLUÍDO") || l.includes("Finalizado"))
      return "text-blue-400";
    return "text-gray-300";
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        {/* Cabeçalho */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              {exec ? (ROBOT_NOMES[exec.robot_id] || exec.robot_name) : "Carregando..."}
              {exec?.robot_subflow && ROBOT_SUBFLOW_LABELS[exec.robot_subflow] && (
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${ROBOT_SUBFLOW_LABELS[exec.robot_subflow].cls}`}>
                  {ROBOT_SUBFLOW_LABELS[exec.robot_subflow].label}
                </span>
              )}
            </h2>
            {exec && <p className="text-sm text-gray-500 mt-0.5">{exec.user_name} · #{exec.id}</p>}
          </div>
          <div className="flex items-center gap-2">
            {podeParar && (
              <Btn variant="danger" size="sm" onClick={handleParar} disabled={parando}>
                {parando ? <Loader size={12} className="animate-spin" /> : null}
                Parar Execução
              </Btn>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100">✕</button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-16">
            <Loader size={24} className="animate-spin text-gray-400" />
          </div>
        ) : exec ? (
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
            {/* Métricas */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-6 border-b border-gray-100">
              <div>
                <p className="text-xs text-gray-400 mb-1 flex items-center gap-1"><Calendar size={11} /> Início</p>
                <p className="text-sm font-semibold text-gray-800">{fmtDt(exec.iniciado_em)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1 flex items-center gap-1"><Calendar size={11} /> Fim</p>
                <p className="text-sm font-semibold text-gray-800">{fmtDt(exec.finalizado_em)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1 flex items-center gap-1"><Clock size={11} /> Duração</p>
                <p className="text-sm font-semibold text-gray-800">{fmtDur(exec.iniciado_em, exec.finalizado_em)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Status</p>
                <StatusBadge status={exec.status} />
              </div>
            </div>

            {/* Barra de progresso grande */}
            {exec.registros_total > 0 && (
              <div className="px-6 py-4 border-b border-gray-100">
                <ExecProgressBar
                  total={exec.registros_total} ok={exec.registros_ok}
                  falha={exec.registros_falha} status={exec.status} />
              </div>
            )}

            {/* Dashboard do classificador (snapshot da execução) */}
            {exec.robot_id === "classificador-intimacoes" && exec.dashboard_snapshot && (() => {
              try {
                const snap = typeof exec.dashboard_snapshot === "string"
                  ? JSON.parse(exec.dashboard_snapshot)
                  : exec.dashboard_snapshot;
                return (
                  <div className="px-6 py-4 border-b border-gray-100">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                      Dashboard desta Execução
                    </p>
                    <ClassificadorDashboard data={snap} />
                  </div>
                );
              } catch { return null; }
            })()}

            {/* Resumo Baixa de Processos (destino por processo + SISJUR / Autojur) */}
            {exec.robot_id === "baixa-processos" &&
              Array.isArray(exec.result_files) &&
              exec.result_files.some(f => /^(cnj_baixa_processos_|dry_run_fluxo_completo_)/.test(f.nome || f)) && (
                <BaixaProcessosResumo exec={exec} />
            )}

            {/* Resumo SisJur — Inserção (criado no SisJur / finalizado no Autojur, por tarefa) */}
            {exec.robot_id === "sisjur-insercao" &&
              Array.isArray(exec.result_files) &&
              exec.result_files.some(f => (f.nome || f).startsWith("resultados_sisjur_")) && (
                <SisjurInsercaoResumo exec={exec} />
            )}

            {/* Resumo Autojur -> Loy Legal (quais CNJ foram enviados) */}
            {exec.robot_id === "autojur-loy" &&
              Array.isArray(exec.result_files) &&
              exec.result_files.some(f => (f.nome || f).startsWith("loy_envio_")) && (
                <LoyResumo exec={exec} />
            )}

            {/* Resumo Custas CELOS — produção (segunda) / comprovantes (sexta) */}
            {exec.robot_id === "custas-celos" &&
              (exec.robot_subflow === "producao" || exec.robot_subflow === "comprovantes") && (
                <CustasCelosSubfluxoResumo exec={exec} />
            )}

            {/* Arquivos de resultado */}
            {Array.isArray(exec.result_files) && exec.result_files.length > 0 && (
              <div className="px-6 py-4 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
                  Arquivos Gerados
                  <span className="ml-2 text-gray-300 normal-case font-normal">(disponíveis por 15 dias)</span>
                </p>
                <div className="flex flex-col gap-1.5">
                  {exec.result_files.map((f, i) => {
                    const nome = f.nome || f;
                    const tamanho = f.tamanho
                      ? f.tamanho > 1024 * 1024
                        ? `${(f.tamanho / 1024 / 1024).toFixed(1)} MB`
                        : `${Math.round(f.tamanho / 1024)} KB`
                      : "";
                    const handleDownload = async () => {
                      try {
                        const r = await api(`/execucoes/${exec.id}/arquivo/${encodeURIComponent(nome)}`);
                        if (!r.ok) return;
                        const blob = await r.blob();
                        const url  = URL.createObjectURL(blob);
                        const a    = document.createElement("a");
                        a.href     = url;
                        a.download = nome;
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch {}
                    };
                    return (
                      <button key={i} onClick={handleDownload}
                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline text-left">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="7 10 12 15 17 10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        {nome}
                        {tamanho && <span className="text-xs text-gray-400">{tamanho}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Log completo */}
            <div className="flex-1 flex flex-col min-h-0 p-6 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Log da Execução</p>
              <div ref={logRef}
                className="flex-1 bg-gray-900 rounded-xl p-4 overflow-y-auto font-mono text-xs leading-relaxed min-h-[200px] max-h-[40vh]">
                {logLines.length === 0
                  ? <span className="text-gray-500">Sem logs disponíveis.</span>
                  : logLines.map((l, i) => (
                    <div key={i} className={lineClass(l)}>{l}</div>
                  ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center py-16 text-gray-400">Execução não encontrada.</div>
        )}
      </div>
    </div>
  );
}

const EXEC_PAGE_SIZE = 15;

function ExecucoesPanel({ user, onBack, onLogout, onUsers, onCredentials, onExecucoes }) {
  const canSeeAll = user?.role === "admin" || user?.manage_credentials;
  const isAdmin   = user?.role === "admin";
  const [execucoes,       setExecucoes]       = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [detalheId,       setDetalheId]       = useState(null);
  const [pagina,          setPagina]          = useState(1);
  const [reiniciarExec,    setReiniciarExec]    = useState(null);
  const [reiniciarXlsx,    setReiniciarXlsx]    = useState("");
  const [reiniciarZips,    setReiniciarZips]    = useState("");
  const [reiniciarZip,     setReiniciarZip]     = useState("");
  const [reiniciarEprocZip, setReiniciarEprocZip] = useState("");
  const [reiniciarLoading, setReiniciarLoading] = useState(false);
  const [reiniciarErro,    setReiniciarErro]    = useState("");

  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try {
      const r = await api("/execucoes");
      if (r.ok) setExecucoes(await r.json());
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirReiniciar = (e) => {
    setReiniciarExec(e);
    setReiniciarXlsx(`/app/temp_files/lista_${e.robot_id}.xlsx`);
    setReiniciarZips("");
    setReiniciarZip("");
    setReiniciarEprocZip("");
    setReiniciarErro("");
  };

  const confirmarReiniciar = async () => {
    if (!reiniciarExec) return;
    setReiniciarLoading(true);
    setReiniciarErro("");
    try {
      const rid = reiniciarExec.robot_id;
      let body = {};
      if (rid === "sisjurpex-custas") {
        body = { eproc_zip_path: reiniciarEprocZip.trim() };
      } else if (rid === "sisjurpex-peticao") {
        body = { xlsx_path: reiniciarXlsx.trim(), zip_path: reiniciarZip.trim() };
      } else if (rid === "eproc-guias") {
        body = { xlsx_path: reiniciarXlsx.trim() };
      } else if (rid === "consulta-tribunais") {
        body = {
          tribunal: reiniciarZip.trim() || "",
          ...(reiniciarZips.trim() ? { csv_path: reiniciarZips.trim() } : {}),
        };
      } else {
        body = {
          xlsx_path: reiniciarXlsx.trim(),
          ...(reiniciarZips.trim() ? { zips_dir: reiniciarZips.trim() } : {}),
        };
      }
      const r = await api(`/admin/reiniciar-execucao/${reiniciarExec.id}`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) { setReiniciarErro(data.erro || "Erro ao reiniciar"); return; }
      setReiniciarExec(null);
      carregar(true);
    } catch (err) {
      setReiniciarErro("Erro de conexão");
    } finally {
      setReiniciarLoading(false);
    }
  };

  // Auto-refresh a cada 5s enquanto houver execuções em andamento
  useEffect(() => {
    const temRodando = execucoes.some(e => e.status === "running");
    if (!temRodando) return;
    const t = setInterval(() => carregar(true), 5000);
    return () => clearInterval(t);
  }, [execucoes, carregar]);

  // ETA: estimativa de tempo restante baseada na taxa de progresso
  const calcETA = (e) => {
    if (e.status !== "running" || !e.registros_total) return null;
    const processados = (e.registros_ok + e.registros_falha) || e.registros_processados || 0;
    if (processados === 0) return null;
    const elapsedMs  = Date.now() - new Date(e.iniciado_em).getTime();
    const msPerRec   = elapsedMs / processados;
    const restantes  = e.registros_total - processados;
    if (restantes <= 0) return null;
    const etaS = Math.round((msPerRec * restantes) / 1000);
    return etaS < 60 ? `~${etaS}s` : `~${Math.floor(etaS / 60)}m ${etaS % 60}s`;
  };

  const fmtDt  = dt => dt ? new Date(dt).toLocaleString("pt-BR") : "—";
  const fmtDur = (ini, fim) => {
    const end = fim ? new Date(fim) : new Date();
    if (!ini) return "—";
    const s = Math.round((end - new Date(ini)) / 1000);
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    return s < 60 ? `${sec}s` : `${m}m ${sec}s`;
  };

  // ── Estatísticas ──────────────────────────────────────────────────────────────
  const hoje = new Date().toLocaleDateString("pt-BR");

  // Rótulos de resultado por robô (o que cada "ok" representa)
  const STATS_LABEL = {
    "eproc-guias":              { acao: "guias emitidas",               icon: "📄" },
    "sisjur-insercao":          { acao: "documentos inseridos",          icon: "📁" },
    "sisjurpex-custas":         { acao: "solicitações de pagamento",     icon: "💳" },
    "sisjurpex-peticao":        { acao: "petições protocoladas",         icon: "📋" },
    "classificador-intimacoes": { acao: "intimações classificadas",      icon: "🤖" },
    "baixa-processos":          { acao: "processos baixados",            icon: "🔒" },
    "custas-celos":             { acao: "guias que chegariam até o fim",  icon: "🧾" },
  };

  // Por robô: soma de registros_ok + quantos ok hoje
  const statsPorRobo = Object.keys(STATS_LABEL).map(id => {
    const tudo  = execucoes.filter(e => e.robot_id === id);
    const hj    = tudo.filter(e => new Date(e.iniciado_em).toLocaleDateString("pt-BR") === hoje);
    const totalOk = tudo.reduce((s, e) => s + (e.registros_ok || 0), 0);
    const hojeOk  = hj.reduce((s, e) => s + (e.registros_ok || 0), 0);
    return { id, totalOk, hojeOk, ...STATS_LABEL[id] };
  }).filter(r => r.totalOk > 0 || execucoes.some(e => e.robot_id === r.id));

  // ── Paginação ─────────────────────────────────────────────────────────────────
  const totalPaginas = Math.max(1, Math.ceil(execucoes.length / EXEC_PAGE_SIZE));
  const paginaAtual  = Math.min(pagina, totalPaginas);
  const execPagina   = execucoes.slice(
    (paginaAtual - 1) * EXEC_PAGE_SIZE,
    paginaAtual * EXEC_PAGE_SIZE,
  );

  // Geração de páginas a mostrar (janela de 5 ao redor da atual)
  const paginasVisiveis = () => {
    const pages = [];
    const start = Math.max(1, paginaAtual - 2);
    const end   = Math.min(totalPaginas, start + 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="min-h-screen bg-[#F5F6FA] flex flex-col">
      <NavHeader user={user} onLogout={onLogout} onUsers={onUsers}
        onCredentials={onCredentials} onExecucoes={onExecucoes || (() => {})}
        currentScreen="execucoes" onRobots={onBack} />

      <main className="max-w-6xl mx-auto w-full px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-semibold text-[#B8963E] uppercase tracking-widest mb-1">Monitoramento</p>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Activity size={22} /> Execuções
            </h1>
          </div>
          <Btn variant="secondary" onClick={carregar} disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
          </Btn>
        </div>

        {/* ── Cards de estatísticas ── */}
        {!loading && statsPorRobo.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {statsPorRobo.map(r => (
              <div key={r.id} className="bg-white rounded-2xl border border-gray-200 px-6 py-5 shadow-sm flex flex-col gap-1">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">{r.acao}</p>
                <p className="text-4xl font-bold text-gray-900 mt-1">{r.totalOk.toLocaleString("pt-BR")}</p>
                <p className="text-xs text-gray-400 mt-0.5">{r.hojeOk.toLocaleString("pt-BR")} hoje</p>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader size={28} className="animate-spin text-gray-400" />
          </div>
        ) : execucoes.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
            <Activity size={40} className="text-gray-200" />
            <p className="text-sm font-medium">Nenhuma execução registrada ainda.</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-3 sm:px-5 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Robô</th>
                    {canSeeAll && <th className="text-left px-3 sm:px-5 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide hidden sm:table-cell">Usuário</th>}
                    <th className="text-left px-3 sm:px-5 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide hidden md:table-cell">Início</th>
                    <th className="text-left px-3 sm:px-5 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide hidden md:table-cell">Duração</th>
                    <th className="text-left px-3 sm:px-5 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Status</th>
                    <th className="px-3 sm:px-5 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Progresso</th>
                    <th className="px-3 sm:px-5 py-3 w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {execPagina.map(e => {
                    const eta           = calcETA(e);
                    const podeParar     = e.status === "running" && (isAdmin || e.user_id === user?.id);
                    const podeReiniciar = isAdmin && e.status !== "running";
                    const handleStop    = async () => {
                      await api(`/execucoes/${e.id}/parar`, { method: "POST" }).catch(() => {});
                      carregar(true);
                    };
                    return (
                      <tr key={e.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 sm:px-5 py-3 sm:py-4 font-medium text-gray-800 text-xs sm:text-sm">
                          {ROBOT_NOMES[e.robot_id] || e.robot_name}
                          {e.robot_subflow && ROBOT_SUBFLOW_LABELS[e.robot_subflow] && (
                            <span className={`ml-1.5 inline-block px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide ${ROBOT_SUBFLOW_LABELS[e.robot_subflow].cls}`}>
                              {ROBOT_SUBFLOW_LABELS[e.robot_subflow].label}
                            </span>
                          )}
                        </td>
                        {canSeeAll && (
                          <td className="px-3 sm:px-5 py-3 sm:py-4 text-gray-500 text-xs hidden sm:table-cell">{e.user_name || "—"}</td>
                        )}
                        <td className="px-3 sm:px-5 py-3 sm:py-4 text-gray-500 text-xs whitespace-nowrap hidden md:table-cell">{fmtDt(e.iniciado_em)}</td>
                        <td className="px-3 sm:px-5 py-3 sm:py-4 text-gray-500 text-xs whitespace-nowrap hidden md:table-cell">
                          {fmtDur(e.iniciado_em, e.finalizado_em)}
                          {eta && <div className="text-[10px] text-blue-400 mt-0.5">restam {eta}</div>}
                        </td>
                        <td className="px-3 sm:px-5 py-3 sm:py-4"><StatusBadge status={e.status} /></td>
                        <td className="px-3 sm:px-5 py-3 sm:py-4">
                          <ExecProgressBar
                            total={e.registros_total} ok={e.registros_ok}
                            falha={e.registros_falha} status={e.status}
                            processados={e.registros_processados || 0} />
                        </td>
                        <td className="px-3 sm:px-5 py-3 sm:py-4">
                          <div className="flex items-center gap-2 justify-end">
                            {podeParar && (
                              <button onClick={handleStop}
                                className="text-xs text-red-500 font-semibold hover:underline whitespace-nowrap">
                                Parar
                              </button>
                            )}
                            {podeReiniciar && (
                              <button onClick={() => abrirReiniciar(e)}
                                className="text-xs text-amber-600 font-semibold hover:underline whitespace-nowrap">
                                Reiniciar
                              </button>
                            )}
                            <button onClick={() => setDetalheId(e.id)}
                              className="text-xs text-[#1B2B5B] font-semibold hover:underline whitespace-nowrap">
                              Ver mais
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* ── Paginador ── */}
              {totalPaginas > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
                  <p className="text-xs text-gray-400">
                    {(paginaAtual - 1) * EXEC_PAGE_SIZE + 1}–{Math.min(paginaAtual * EXEC_PAGE_SIZE, execucoes.length)} de {execucoes.length}
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPagina(p => Math.max(1, p - 1))}
                      disabled={paginaAtual === 1}
                      className="px-2 py-1 rounded-lg text-xs text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed">
                      ‹
                    </button>
                    {paginasVisiveis().map(p => (
                      <button key={p} onClick={() => setPagina(p)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                          p === paginaAtual
                            ? "bg-[#1B2B5B] text-white"
                            : "text-gray-500 hover:bg-gray-200"
                        }`}>
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
                      disabled={paginaAtual === totalPaginas}
                      className="px-2 py-1 rounded-lg text-xs text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed">
                      ›
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {detalheId && (
        <ExecDetalheModal
          execId={detalheId}
          user={user}
          onClose={() => setDetalheId(null)}
          onParado={() => { carregar(true); }}
        />
      )}

      {reiniciarExec && (() => {
        const rid = reiniciarExec.robot_id;
        const isCustas   = rid === "sisjurpex-custas";
        const isPeticao  = rid === "sisjurpex-peticao";
        const isEproc    = rid === "eproc-guias";
        const isConsulta = rid === "consulta-tribunais";
        const isInsercao = !isCustas && !isPeticao && !isEproc && !isConsulta;
        const confirmDisabled = reiniciarLoading
          || (isCustas  && !reiniciarEprocZip.trim())
          || (isPeticao && (!reiniciarXlsx.trim() || !reiniciarZip.trim()))
          || (isEproc   && !reiniciarXlsx.trim())
          || (isInsercao && !reiniciarXlsx.trim());
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
              <h3 className="text-base font-bold text-gray-800 mb-1">Reiniciar Execução</h3>
              <p className="text-xs text-gray-500 mb-4">
                A execução será criada em nome de{" "}
                <span className="font-semibold">{reiniciarExec.user_name || "usuário original"}</span>,
                usando a credencial cadastrada para o robô.
              </p>

              {isCustas && (
                <>
                  <label className="block text-xs font-medium text-gray-700 mb-1">ZIP do EPROC</label>
                  <input
                    type="text"
                    value={reiniciarEprocZip}
                    onChange={e => setReiniciarEprocZip(e.target.value)}
                    placeholder="/app/temp_files/arquivo.zip"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs mb-3 focus:outline-none focus:ring-2 focus:ring-[#1B2B5B]/30"
                  />
                </>
              )}

              {isPeticao && (
                <>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Caminho do XLSX</label>
                  <input
                    type="text"
                    value={reiniciarXlsx}
                    onChange={e => setReiniciarXlsx(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs mb-3 focus:outline-none focus:ring-2 focus:ring-[#1B2B5B]/30"
                  />
                  <label className="block text-xs font-medium text-gray-700 mb-1">ZIP de Petições</label>
                  <input
                    type="text"
                    value={reiniciarZip}
                    onChange={e => setReiniciarZip(e.target.value)}
                    placeholder="/app/temp_files/peticoes.zip"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs mb-3 focus:outline-none focus:ring-2 focus:ring-[#1B2B5B]/30"
                  />
                </>
              )}

              {(isEproc || isInsercao) && (
                <>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Caminho do XLSX</label>
                  <input
                    type="text"
                    value={reiniciarXlsx}
                    onChange={e => setReiniciarXlsx(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs mb-3 focus:outline-none focus:ring-2 focus:ring-[#1B2B5B]/30"
                  />
                </>
              )}

              {isConsulta && (
                <>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tribunal</label>
                  <select
                    value={reiniciarZip}
                    onChange={e => setReiniciarZip(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs mb-3 focus:outline-none focus:ring-2 focus:ring-[#1B2B5B]/30 bg-white">
                    <option value="">Todos (TJSC e TJPR)</option>
                    <option value="TJSC - Eproc 1">TJSC — Eproc 1º Grau</option>
                    <option value="TJSC - Eproc 2">TJSC — Eproc 2º Grau</option>
                    <option value="TJPR - 1o Grau">TJPR — Projudi</option>
                  </select>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    CSV de Processos <span className="text-gray-400 font-normal">(opcional, caminho em /app/temp_files/)</span>
                  </label>
                  <input
                    type="text"
                    value={reiniciarZips}
                    onChange={e => setReiniciarZips(e.target.value)}
                    placeholder="/app/temp_files/processos_consulta_..."
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs mb-3 focus:outline-none focus:ring-2 focus:ring-[#1B2B5B]/30"
                  />
                </>
              )}

              {isInsercao && (
                <>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Pasta de ZIPs <span className="text-gray-400 font-normal">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    value={reiniciarZips}
                    onChange={e => setReiniciarZips(e.target.value)}
                    placeholder="/app/temp_files/zips_grp_..."
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs mb-3 focus:outline-none focus:ring-2 focus:ring-[#1B2B5B]/30"
                  />
                </>
              )}

              {reiniciarErro && (
                <p className="text-xs text-red-500 mb-3">{reiniciarErro}</p>
              )}

              <div className="flex gap-2 justify-end mt-1">
                <button
                  onClick={() => setReiniciarExec(null)}
                  disabled={reiniciarLoading}
                  className="px-4 py-2 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
                  Cancelar
                </button>
                <button
                  onClick={confirmarReiniciar}
                  disabled={confirmDisabled}
                  className="px-4 py-2 text-xs rounded-lg bg-[#1B2B5B] text-white font-semibold hover:bg-[#162347] disabled:opacity-50">
                  {reiniciarLoading ? "Iniciando..." : "Confirmar"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// APP PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
export default function App() {
  const [token, setToken] = useState(localStorage.getItem("rpa_token"));
  const [user,  setUser]  = useState(() => {
    const t = localStorage.getItem("rpa_token");
    return t ? parseJwt(t) : null;
  });
  const [screen,       setScreen]       = useState("robots");
  const [selectedRobot, setSelectedRobot] = useState(null);

  const onLogin = (newToken, userData) => {
    localStorage.setItem("rpa_token", newToken);
    setToken(newToken);
    setUser(userData);
  };

  const onLogout = () => {
    localStorage.removeItem("rpa_token");
    setToken(null);
    setUser(null);
    setScreen("robots");
    setSelectedRobot(null);
  };

  if (!token) return <LoginScreen onLogin={onLogin} />;

  const goExecucoes = () => { setSelectedRobot(null); setScreen("execucoes"); };

  if (selectedRobot) return (
    <RobotPanel
      robo={selectedRobot}
      user={user}
      onVoltar={() => setSelectedRobot(null)}
      onLogout={onLogout}
      onUsers={() => { setSelectedRobot(null); setScreen("users"); }}
      onCredentials={() => { setSelectedRobot(null); setScreen("credentials"); }}
      onExecucoes={goExecucoes}
    />
  );

  if (screen === "users") return (
    <AdminUsersPanel
      user={user}
      onBack={() => setScreen("robots")}
      onLogout={onLogout}
      onCredentials={() => setScreen("credentials")}
      onExecucoes={goExecucoes}
    />
  );

  if (screen === "credentials") return (
    <AdminCredentialsPanel
      user={user}
      onBack={() => setScreen("robots")}
      onLogout={onLogout}
      onUsers={() => setScreen("users")}
      onExecucoes={goExecucoes}
    />
  );

  if (screen === "execucoes") return (
    <ExecucoesPanel
      user={user}
      onBack={() => setScreen("robots")}
      onLogout={onLogout}
      onUsers={() => setScreen("users")}
      onCredentials={() => setScreen("credentials")}
      onExecucoes={goExecucoes}
    />
  );

  return (
    <RobotSelector
      user={user}
      onSelect={setSelectedRobot}
      onLogout={onLogout}
      onUsers={() => setScreen("users")}
      onCredentials={() => setScreen("credentials")}
      onExecucoes={goExecucoes}
    />
  );
}
